'use client';

import { uploadResumable } from '@/lib/content/resumable-client';
import {
  Background,
  BackgroundVariant,
  ConnectionMode,
  ReactFlow,
  SelectionMode,
  useReactFlow,
  useStore as useRfStore,
  useStoreApi,
  useViewport,
  ViewportPortal,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type Viewport,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Maximize, Maximize2, Plus, Upload, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgentNode } from './nodes/agent-node';
import { ContentNode } from './nodes/content-node';
import { PostNode } from './nodes/post-node';
// terminal / daemon / ssh / python / files node types were DROPPED pre-launch
// (dead — zero spawn sites, not in the FAB, not widget-backed). Unregistering
// them here un-imports the components so they + their heavy transitive deps
// (ShellFrame, xterm, the host Pyodide runtime) tree-shake out of the workspace
// bundle. The now-orphaned files + type-union entries are removed in a
// dedicated follow-up. A legacy CRDT snapshot embedding one renders React
// Flow's default node (harmless) instead of a live shell.
import { AnnotationNode } from './nodes/annotation-node';
import { BoardNode } from './nodes/board-node';
import { ConnectorEdge } from './connector-edge';
import { DelegationEdge } from './delegation-edge';
import { isResizingItem } from './use-node-resize';
import { clampNodeSizeForHandset } from './handset-node-clamp';
import { readSafeAreaInsets } from './safe-area-probe';
import { BAR_MEASURED_EVENT, BAR_PX_VAR } from './mobile-tab-bar';
import { uploadFileAsContentItem } from './paste-handler';
import {
  useCurrentScreenId,
  useDesktopFullscreen,
  useDesktopFullscreenShownId,
  useFullscreenItemId,
  useHandsetFullscreen,
  usePendingFullscreen,
  useOverviewMode,
  usePendingAnnotation,
  usePresentedWindowId,
  usePrevScreenId,
  useWorkspace,
  useWorkspaceActions,
  useWorkspaceItems,
} from './store';
import {
  displayScreenEntries,
  fullscreenCameraFor,
} from '@/lib/workspaces/stage-rules';
import { StageTidyOffer } from './stage-tidy';
import {
  presentOnHandset,
  presentedWindowId,
  REFERENCE_SIZING_BOX,
  usableBox,
  type UsableBox,
} from '@/lib/workspaces/handset-present';
import {
  entryOfItem,
  FRAME_MARGIN_PX,
  frameEntryTarget,
  frameViewportForRect,
  isScreenType,
  orderScreenEntries,
  SCREEN_CELL,
  orderScreens,
  pickSettleScreen,
  nextFreeScreenCell,
  newScreenName,
  screenOutlineRect,
  rectContainsRect,
  rectsIntersect,
  workspaceIsEmpty,
  SETTLE_MIN_COVERAGE,
  unionTiledRect,
  viewportsClose,
  type ScreenEntry,
  type ScreenRect,
  type ViewportTransform,
} from '@/lib/workspaces/screens';
import {
  clearDropTargets,
  publishRegionDropTarget,
} from '@/components/workspace/board-drop-target';
import {
  LiveContentRenameSync,
  resolveAppDocumentTitle,
  resolveItemLabel,
  resolveScreenEntryLabel,
  useLiveContentMeta,
} from './item-label';
import { loadCurrentScreen } from '@/lib/workspaces/screen-state';
import {
  loadScreenCameras,
  recallScreenCamera,
  rememberScreenCamera,
  saveScreenCameras,
  type ScreenCameraMemory,
} from '@/lib/workspaces/screen-camera';
import {
  OVERDRAG_CEILING_HOLD_MS,
  keepHeaderInView,
  overdragFromPointer,
} from '@/lib/workspaces/overdrag';
import { ZoomControls } from './zoom-controls';
import { AppSwitcher } from './app-switcher';
import { WorkspacePresence } from './presence';
import { CanvasComments } from './comments';
import { requestNewThreadAt } from './comments/comments-bridge';
import { BackToContentPill } from './rail/rail-canvas-controls';
import { SelectionToolbar } from './selection-toolbar';
import { WorkspaceSelectionProvider } from './selection-context';
import {
  CanvasContextMenu,
  buildMenuItemsFromCommands,
  type CanvasMenuItem,
} from './canvas-context-menu';
import { useWorkspaceShortcuts } from './use-workspace-shortcuts';
import { useWorkspaceClipboard, getWorkspaceClipboardDebugState } from './clipboard-handler';
import { useCommandContext } from './use-command-context';
import { usePickThrough, stackUnder } from './use-pick-through';
import { useCoarsePointer } from '@/hooks/use-coarse-pointer';
import { useHandsetLandscape } from '@/hooks/use-handset-landscape';
import { buildWorkspaceCommands, formatShortcut } from '@/lib/workspace/commands';
import { shareLink } from '@/lib/share';
import { useRegionDropTarget } from '@/components/workspace/board-drop-target';
import { cn } from '@/lib/utils';
import { useT, useTranslation } from '@/components/i18n-provider';
import { openWorkspacePanel } from '@/components/workspace/panels/panel-events';
import { watchPanelOwnsEsc } from '@/components/workspace/fullscreen/panel-owns-esc';
import { useIsMd } from '@/components/workspace/panels/panel-shell';
import { useRailV2 } from '@/lib/workspaces/rail-flag';
import { isAppPinchOn } from '@/components/workspace/app-pinch';
import { DiscoverView } from './discover/discover-view';
import { StageCorners } from './stage-corners';
import { EmptyWorkspaceHint } from './empty-workspace-hint';
import { ClosedWindowUndo } from './closed-window-undo';
import { computeAlignmentSnap, type GuideLine, type Rect } from '@/lib/workspace/snap';
import { AlignmentGuides } from './alignment-guides';
import {
  getPlacement,
  isOnCanvas,
  type WorkspaceItemDTO,
  type WorkspaceItemType,
} from '@/lib/workspaces/types';

const NODE_TYPES = {
  agent: AgentNode,
  content: ContentNode,
  post: PostNode,
  annotation: AnnotationNode,
  board: BoardNode,
} as const;

const EDGE_TYPES = {
  connector: ConnectorEdge,
  delegation: DelegationEdge,
} as const;

// Exported so nodes can snap their own resize handlers to the same
// lattice as RF's drag-snap. Keep these in sync with the <Background>
// `gap` values below and the `snapGrid` prop on <ReactFlow>.
export const GRID_MINOR = 24;
export const GRID_MAJOR = 120;

// When a widget (node) context menu opens, a pane ("workspace") menu
// requested within this window is suppressed so a touch long-press over a
// widget can't raise two overlapping menus (the native `contextmenu` and
// the widget's own long-press timer fire a few ms apart). Long enough to
// cover the same gesture, short enough that a deliberate empty-canvas
// long-press moments later (≥500ms hold) still opens the pane menu.
const CROSS_MENU_SUPPRESS_MS = 300;
// How long a request to open a window full screen (the `/?app=<id>` entry)
// waits for that window to be on the canvas and, on a phone, presented.
// Long enough for a first load on a slow connection, short enough that it
// cannot fire under something the person opened later.
const PENDING_FULLSCREEN_MS = 15_000;

// Workspace (canvas) zoom is intentionally disabled: it overlaps with the
// in-app zoom that widgets like EDA own, so pinch / Cmd-wheel / double-click on
// the canvas competed with the app under the cursor. The canvas stays a fixed
// surface you PAN around, at 100%; the Stage view is the map. Flip to true to
// restore interactive canvas zoom.
//
// Disabled means not reachable by any door, not just the gestures this flag
// gates. Until 2026-09-26 the `?zoom=` / `?fit=` deep links, the `ws:zoom` /
// `ws:fit` window events, the "Fit to selection" command and the "Fit to
// content" pill all still zoomed, and an automated browser pass on production
// found and used them. They are gone, and
// `tests/unit/workspaces/no-workspace-zoom-api.test.ts` keeps them out. What
// remains below 100% on purpose: the Stage view, and the touch two-finger
// navigation with its mobile recovery menu (`ws:screen` zoom-100 /
// zoom-fit-screen / zoom-fit-all), which was a deliberate product call.
const WORKSPACE_ZOOM_ENABLED: boolean = false;

type DragPayload = {
  type: WorkspaceItemType;
  refId?: string | null;
  cache?: Record<string, unknown>;
};

/**
 * Pick the pair of cardinal handle ids (`t/r/b/l`) that best matches
 * the relative position of a child widget to its parent. Used for the
 * quiet provenance edge that `spawnChild` lays down — RF's default of
 * `bottom-source → top-target` looks wrong whenever the placement was
 * `right` / `left`, which is the common case for `plt.show()` figures
 * and code-generated content widgets.
 *
 * Picks the axis with the larger separation; on a near-diagonal the
 * larger delta wins and we get a clean horizontal OR vertical lead-in.
 * Returning string handle ids matches the `t/r/b/l` IDs registered on
 * every node in `node-frame.tsx` / `annotation-node.tsx`.
 */
function pickProvenanceHandles(
  dx: number,
  dy: number,
): { sourceHandle: string; targetHandle: string } {
  if (Math.abs(dx) >= Math.abs(dy)) {
    // Horizontal dominant — child sits to the side.
    return dx >= 0
      ? { sourceHandle: 'r', targetHandle: 'l' }
      : { sourceHandle: 'l', targetHandle: 'r' };
  }
  // Vertical dominant — child sits above or below.
  return dy >= 0
    ? { sourceHandle: 'b', targetHandle: 't' }
    : { sourceHandle: 't', targetHandle: 'b' };
}

/**
 * Screen position dots (coarse pointers): one dot per screen when the
 * workspace has more than one, active = the parked screen. Tap a dot to
 * go; complements the flick paging. Collapses to a "3 / 14" pill when
 * there are too many dots to tap reliably.
 */
function ScreenPageDots({
  items,
  currentScreenId,
  fullscreenIds,
}: {
  items: WorkspaceItemDTO[];
  currentScreenId: string | null;
  /** Windows this device shows full screen: each has a dot of its own (rule 17). */
  fullscreenIds: readonly string[];
}) {
  const entries = displayScreenEntries(items, fullscreenIds);
  if (entries.length < 2) return null;
  const active = entryOfItem(entries, currentScreenId);
  const activeIdx = active ? entries.indexOf(active) : -1;
  return (
    // Bottom offset clears the portrait bar via its published var; the bar
    // only exists there, so md restores the old offset.
    <div
      className="pointer-events-none absolute inset-x-0 z-30 flex justify-center"
      style={{
        bottom:
          'calc(0.875rem + env(safe-area-inset-bottom) + var(--ws-bar-b, 0px))',
      }}
    >
      <div className="liquid-glass pointer-events-auto flex items-center gap-2 rounded-full px-3 py-1.5">
        {entries.length <= 10 ? (
          entries.map((e, i) => (
            <button
              key={e.id}
              type="button"
              aria-label={`Go to screen ${i + 1}`}
              aria-current={e === active ? 'true' : undefined}
              onClick={() =>
                window.dispatchEvent(
                  new CustomEvent('ws:screen', {
                    detail: { kind: 'item', itemId: e.id },
                  }),
                )
              }
              className={cn(
                'h-2 w-2 rounded-full transition-colors',
                e === active
                  ? 'bg-foreground'
                  : 'bg-foreground/25 hover:bg-foreground/50',
              )}
            />
          ))
        ) : (
          <span className="text-[11px] font-medium text-foreground/70">
            {activeIdx >= 0 ? activeIdx + 1 : '·'} / {entries.length}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Where the overview's "New screen" tile sits: one gap after the
 * rightmost screen (spread positions honored), sized like a screen
 * card. Shared by the tile itself and the canvas drag hit-test so the
 * drop target and the visual can never disagree.
 */
function overviewNewScreenSlot(
  items: WorkspaceItemDTO[],
  spread: Map<string, { x: number; y: number }> | null,
): ScreenRect | null {
  const screens = orderScreens(items).filter((it) => isOnCanvas(it.state));
  if (screens.length === 0) return null;
  // The next FREE cell on the canonical lattice (`nextFreeScreenCell`, which
  // the server's add-content path uses too, so the two can never drift). This
  // used to append at `maxX + 64` sized to the AVERAGE of the existing
  // screens, so blank screens landed in a ragged row whose size drifted as
  // the average moved — the second half of the overlapping-screens problem
  // (the first being the camera-derived grid in `findScreenGridSlot`).
  // Overview substitutes the SPREAD positions for the real ones.
  return nextFreeScreenCell(
    screens.map((it) => {
      const p = it.type !== 'board' ? spread?.get(it.id) : undefined;
      return {
        x: p ? p.x : it.state.x,
        y: p ? p.y : it.state.y,
        width: it.state.width,
        height: it.state.height,
      };
    }),
  );
}

/**
 * Overview "+" tile — the next free cell in the lattice. Click = create
 * a new screen right away (auto-named Screen N; no intermediate menu,
 * per the Part VI field review). Also the DROP TARGET for "move this
 * window to a new screen": while a window drag hovers it
 * (`dropActive`), it lights up; the canvas commits the drop by minting
 * a blank screen and filing the window onto it. Lives in the
 * transformed viewport (world coordinates); the glyph counter-scales
 * so it reads at any overview zoom.
 */
function OverviewNewScreenTile({
  slot,
  dropActive,
  onNewScreen,
}: {
  slot: ScreenRect;
  dropActive: boolean;
  onNewScreen: () => void;
}) {
  const t = useT();
  const { zoom } = useViewport();
  const { x, y, width, height } = slot;
  const inv = 1 / Math.max(zoom, 0.02);
  const scale = Math.min(inv, 12);
  return (
    <ViewportPortal>
      <button
        type="button"
        aria-label={t('stage.newScreen')}
        onClick={onNewScreen}
        style={{
          position: 'absolute',
          left: x,
          top: y,
          width,
          height,
          borderWidth: 2 * inv,
          borderRadius: 8 * inv,
        }}
        className={cn(
          'pointer-events-auto flex cursor-pointer flex-col items-center justify-center border-dashed transition-colors',
          dropActive
            ? 'border-primary/70 bg-primary/[0.06]'
            : 'border-border/80 hover:border-border hover:bg-foreground/[0.02]',
        )}
      >
        <div
          className={cn(
            'pointer-events-none flex flex-col items-center gap-1.5',
            dropActive ? 'text-primary' : 'text-muted-foreground',
          )}
          style={{ transform: `scale(${scale})` }}
        >
          <Plus className="h-6 w-6" strokeWidth={1.6} />
          <span className="text-xs font-medium">{t('stage.newScreen')}</span>
        </div>
      </button>
    </ViewportPortal>
  );
}

/**
 * The screenful boundary an entry occupies in the overview: a blank
 * screen IS its sheet; an app entry gets a wrapper-sized cell centered
 * on its (spread-aware) member union. Shared by the outlines component
 * and the overview entry-fit so the fit can never clip an outline.
 */
// The "Screen N" naming (`newScreenName`: the number the screen takes on
// Stage, since fix round 2; `nextScreenName`, one past the highest in use,
// when that name is taken) and the outline below live in
// lib/workspaces/screens.ts since 2026-09-29 (lane D): the store stores a
// "Screen N" sheet at exactly this outline when the last window leaves a
// screen that still holds notes (rule 10), so the geometry and the name have
// one home.
function overviewEntryOutlineRect(
  entry: ScreenEntry,
  items: WorkspaceItemDTO[],
  spread: Map<string, { x: number; y: number }> | null,
  vw: number,
  vh: number,
): ScreenRect | null {
  // A blank screen is drawn as a CELL centred on its sheet, not as the sheet
  // itself. The sheet's stored size is whatever SCREEN_CELL was the day it was
  // created, so returning it made an older screen a visibly different size and
  // shape from a screen an app just opened. Every screen is one cell.
  return screenOutlineRect(entry, items, vw, vh, spread);
}

/**
 * Overview screen outlines (Part V): every screen entry gets a visible
 * screenful boundary and its number while the overview is open, so
 * even a single window reads as "screen 1" with a place to drop things
 * next to it. App entries draw a hairline screenful card centered on
 * their (spread-aware) union; blank screens already draw their own
 * sheet, so they get only the number chip. Numbers ARE the ⌘⇧digit
 * keys — both come from `orderScreenEntries`.
 */
/** Horizontal gap between overview grid cells, world px. Sized against the
 *  SCREEN CELL (not a constant): at the fit zoom a 64px world gap collapsed to
 *  ~25 screen px between 750px-wide cells, which reads as "no gap at all". */
const OVERVIEW_GRID_COL_GAP = Math.round(SCREEN_CELL.width * 0.14);
/**
 * Vertical gap between grid ROWS, world px. Larger than the column gap
 * on purpose: the label row (keycap + name, constant SCREEN size via
 * counter-scaling) lives in this band, and at overview zooms well
 * below 1 a 64px world gap collapses to less than the label's height —
 * the Part VII field round saw labels overlapping the next row's cells.
 */
const OVERVIEW_GRID_ROW_GAP = Math.round(SCREEN_CELL.height * 0.22);

type OverviewGridCell = {
  id: string;
  kind: ScreenEntry['kind'];
  /** Where this screen DISPLAYS in the overview lattice. */
  cell: ScreenRect;
  /** display = true + (dx, dy); the drop remap inverts this. */
  dx: number;
  dy: number;
  /** A temporary full-screen screen on this device (rule 13): badged, never renamed or removed. */
  fullscreen?: boolean;
};

type OverviewGrid = {
  /** Display-only position overrides for every shifted member (+ sheet). */
  display: Map<string, { x: number; y: number }>;
  cells: OverviewGridCell[];
  /** The "+ New screen" tile — the next free cell in the lattice. */
  tile: ScreenRect;
  /** The row gap this lattice was laid out with (adaptive; see
   * computeOverviewGrid) — the entry fit reserves half of it for labels. */
  rowGap: number;
};

/**
 * The overview lattice (Part VI): screens display as a tidy GRID of
 * screenful cells — one row up to 3, 2x2 at 4, then near-square (3x2,
 * 3x3, …) — regardless of where they live in true space. Display-only:
 * every member item gets an override shifted by its screen's cell
 * delta, and drops invert the delta of the cell they land in, so
 * "commit what you see" still writes TRUE screen-space positions.
 */
function computeOverviewGrid(
  items: WorkspaceItemDTO[],
  /** Cell size — one SCREEN, so every entry fills its cell exactly. Using the
   *  viewport here (as this did) meant a screen larger than the current window
   *  overflowed its cell and ran into the label band beneath it: the ragged
   *  Stage with labels sitting on top of the rects. */
  vw: number,
  vh: number,
  /** The actual canvas, for the fit-zoom prediction and the phone check. */
  viewW: number,
  viewH: number,
  /**
   * This device's screen list (`displayScreenEntries`): a window shown full
   * screen has a card of its own right after the screen it came from, as a
   * full-screen Space does in Mission Control (rules 13 and 17).
   */
  screenEntries?: ScreenEntry[],
): OverviewGrid | null {
  const entries = screenEntries ?? orderScreenEntries(items);
  if (entries.length === 0) return null;
  const n = entries.length;
  // The lattice flows SCREENS + the "+" tile as one sequence. Two
  // columns up to 2x2 — 1 screen puts the tile on the RIGHT, 2 screens
  // put it BELOW, 3 screens complete the square (Part VII picks) —
  // then near-square beyond (4 screens → 3+2, 8 → 3x3, …).
  const slots = n + 1;
  // Narrow viewports (phones) cap at TWO columns and width-fit the entry
  // (the map scrolls vertically): fitting nine screens into 375px produced
  // cells a few dozen pixels wide, which is a picker nobody can read.
  const narrow = viewW < 700;
  const cols =
    slots <= 4 ? Math.min(slots, 2) : narrow ? 2 : Math.ceil(Math.sqrt(slots));
  const rows = Math.ceil(slots / cols);
  // ADAPTIVE row gap. The label row under each cell is counter-scaled
  // (constant ~52 SCREEN px), while this gap is WORLD px — at the fit zoom
  // the labels need 52/zoom world px, so a constant gap collides the moment
  // the lattice gets dense or the viewport small (found live at 375px).
  // Two prediction passes converge (a larger gap lowers the fit zoom a bit).
  const LABEL_SCREEN_PX = 52;
  let rowGap = OVERVIEW_GRID_ROW_GAP;
  for (let pass = 0; pass < 2; pass++) {
    const gridW = cols * vw + (cols - 1) * OVERVIEW_GRID_COL_GAP;
    const gridH = rows * vh + (rows - 1) * rowGap;
    const z = narrow
      ? Math.min((viewW - 32) / gridW, 1)
      : Math.min((viewW - 96) / gridW, (viewH - 96) / gridH, 1);
    rowGap = Math.max(
      OVERVIEW_GRID_ROW_GAP,
      Math.ceil((LABEL_SCREEN_PX / Math.max(z, 0.02)) * 1.1),
    );
  }
  const trueRects = entries.map((e) =>
    overviewEntryOutlineRect(e, items, null, vw, vh),
  );
  let anchorX = Infinity;
  let anchorY = Infinity;
  for (const r of trueRects) {
    if (!r) continue;
    anchorX = Math.min(anchorX, r.x);
    anchorY = Math.min(anchorY, r.y);
  }
  if (anchorX === Infinity) return null;
  const cellAt = (i: number): ScreenRect => ({
    x: anchorX + (i % cols) * (vw + OVERVIEW_GRID_COL_GAP),
    y: anchorY + Math.floor(i / cols) * (vh + rowGap),
    width: vw,
    height: vh,
  });
  const display = new Map<string, { x: number; y: number }>();
  const cells: OverviewGridCell[] = [];
  entries.forEach((e, i) => {
    const t = trueRects[i];
    if (!t) return;
    const cell = cellAt(i);
    const dx = cell.x - t.x;
    const dy = cell.y - t.y;
    cells.push({ id: e.id, kind: e.kind, cell, dx, dy, fullscreen: !!e.fullscreen });
    if (dx !== 0 || dy !== 0) {
      for (const id of e.memberIds) {
        const it = items.find((x) => x.id === id);
        if (it) display.set(id, { x: it.state.x + dx, y: it.state.y + dy });
      }
    }
  });
  return { display, cells, tile: cellAt(n), rowGap };
}

/**
 * Overview screen chrome (Part VI): each grid cell draws its screenful
 * boundary, and ONE label row sits UNDER the cell — number keycap plus
 * the screen's name. Blank screens rename inline right there; app
 * screens show their derived window name. This is the ONLY place
 * screens are labeled — the desktop shows no screen chrome at all.
 * Numbers ARE the ⌘⇧digit keys (same `orderScreenEntries` order).
 */
function OverviewScreenOutlines({
  grid,
  items,
  onRenameScreen,
  onDeleteScreen,
  screenEntries,
}: {
  grid: OverviewGrid;
  items: WorkspaceItemDTO[];
  /** This device's screen list (`displayScreenEntries`); the stored one when absent. */
  screenEntries?: ScreenEntry[];
  /**
   * Rename a screen. `kind` matters: a blank screen just patches its sheet's
   * title, an app screen has no sheet yet and gets one — renaming is what
   * turns an implicit cluster of windows into a normal, named screen.
   */
  onRenameScreen: (id: string, title: string, kind: ScreenEntry['kind']) => void;
  /**
   * Deletes a screen: the apps on it MOVE to the neighbouring screen and
   * only the place goes away. Never offered for the last remaining screen —
   * there would be nowhere for its windows to go.
   */
  onDeleteScreen: (id: string, kind: ScreenEntry['kind']) => void;
}) {
  const t = useT();
  const { zoom } = useViewport();
  // App screens are named live-first through item-label.ts, the rail's rule.
  // This row read the stored snapshot, so on desktop the overview still said
  // "Revent" under a rail that said "Revent" after the app was renamed (the
  // 2026-09-13 browser pass). Read-only: the name is never written back.
  const live = useLiveContentMeta();
  // The blank screen that would receive the window being dragged. Read here
  // rather than in the board node, because this component now draws every
  // screen's boundary — including the receiving glow.
  const dropTargetId = useRegionDropTarget();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (editingId) inputRef.current?.select();
  }, [editingId]);
  // This device's screen list, the one `grid` was laid out from, so a
  // full-screen window's temporary screen names its app (rule 13).
  const entries = screenEntries ?? orderScreenEntries(items);
  const inv = 1 / Math.max(zoom, 0.02);
  const chipScale = Math.min(inv, 12);
  const commit = (id: string) => {
    const next = draft.trim();
    const kind = grid.cells.find((c) => c.id === id)?.kind ?? 'blank';
    // Leaving a blank screen's name as it was writes nothing. The field starts
    // from the name the row SHOWS, and for a screen an app opened that is the
    // app's live name, not the sheet title stored in the shared doc
    // (`resolveScreenEntryLabel` in item-label.ts). Committing it anyway would
    // pin today's name into the doc from a click that changed nothing, and the
    // screen would stop following the app's next rename.
    const entry = entries.find((e) => e.id === id);
    const shown = entry ? resolveScreenEntryLabel(entry, items, live) : 'Blank screen';
    if (next && !(kind === 'blank' && next === shown)) onRenameScreen(id, next, kind);
    setEditingId(null);
  };
  return (
    <ViewportPortal>
      {grid.cells.map((row, i) => {
        const entry = entries.find((e) => e.id === row.id);
        const label = entry
          ? resolveScreenEntryLabel(entry, items, live)
          : 'Blank screen';
        // Every screen can be renamed now — see onRenameScreen. Except a
        // temporary full-screen screen (lane D, rules 13 and 17): it is not
        // stored, so there is nothing to name, and it goes on exit or close,
        // so there is nothing for the X to remove. It carries the badge.
        const renameable = !row.fullscreen;
        return (
          <div
            key={row.id}
            className="pointer-events-none absolute"
            style={{
              left: row.cell.x,
              top: row.cell.y,
              width: row.cell.width,
              height: row.cell.height,
              zIndex: -10000,
            }}
          >
            {/* Boundary — ONE renderer for both kinds. A blank screen used to
                draw its own sheet from the board node, in world units, while
                this drew a counter-scaled outline: same rect, but visibly
                different corner radius, border weight and tint, so screen 1
                and screen 2 never looked like the same kind of thing. */}
            <div
              aria-hidden
              className={cn(
                'absolute inset-0 transition-colors',
                dropTargetId && dropTargetId === row.id
                  ? 'border-primary/70 bg-primary/[0.06]'
                  : 'border-border/70 bg-primary/[0.03]',
              )}
              style={{ borderWidth: 1.5 * inv, borderRadius: 8 * inv }}
            />
            {/* The label row, centered UNDER the screen. */}
            <div
              className="absolute inset-x-0 flex justify-center"
              style={{ top: '100%', paddingTop: 14 * inv }}
            >
              <div
                className={cn(
                  'group flex items-center gap-2',
                  renameable && 'pointer-events-auto',
                )}
                style={{ transform: `scale(${chipScale})`, transformOrigin: 'top center' }}
              >
                <span
                  aria-hidden
                  className="inline-flex h-6 min-w-6 items-center justify-center rounded-md border border-border/60 bg-background/95 px-1.5 font-mono text-[12px] font-medium leading-none text-foreground/70 shadow-sm"
                >
                  {i + 1}
                </span>
                {row.fullscreen && (
                  <span
                    data-testid="stage-card-fullscreen-badge"
                    title={t('workspace.node.fullScreen')}
                    className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-border/60 bg-background/95 text-foreground/70 shadow-sm"
                  >
                    <Maximize2 aria-hidden className="h-3.5 w-3.5" strokeWidth={2} />
                    <span className="sr-only">{t('workspace.node.fullScreen')}</span>
                  </span>
                )}
                {editingId === row.id ? (
                  <input
                    ref={inputRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={() => commit(row.id)}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter') commit(row.id);
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="nodrag w-40 rounded-md border border-border/60 bg-background/95 px-2 py-0.5 text-[13px] font-medium text-foreground outline-none"
                    aria-label={t('stage.screenName')}
                  />
                ) : renameable ? (
                  <button
                    type="button"
                    className="max-w-[40ch] truncate rounded-md px-1.5 py-0.5 text-[13px] font-medium text-foreground/80 transition-colors hover:bg-accent/70 hover:text-foreground"
                    title={t('stage.renameScreen')}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDraft(label);
                      setEditingId(row.id);
                    }}
                  >
                    {label}
                  </button>
                ) : (
                  <span className="max-w-[40ch] truncate text-[13px] font-medium text-foreground/70">
                    {label}
                  </span>
                )}
                {/* Every screen can go — its apps move to the neighbour
                    (see onDeleteScreen). The LAST screen can't: its windows
                    would have nowhere to land. */}
                {grid.cells.length > 1 && editingId !== row.id && !row.fullscreen && (
                  <button
                    type="button"
                    aria-label={`Delete ${label}`}
                    title="Delete screen (apps move to the next screen)"
                    className="inline-flex h-5 w-5 items-center justify-center rounded-md text-muted-foreground/70 opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteScreen(row.id, row.kind);
                    }}
                  >
                    <X className="h-3.5 w-3.5" strokeWidth={2} />
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </ViewportPortal>
  );
}

/**
 * Height of the mobile bottom bar in CSS px. The bar OVERLAYS the canvas
 * (z-96), so every "fits the viewport" computation must subtract it or
 * windows extend underneath.
 *
 * MEASURED FIRST (RV-034, 2026-09-29): the bar publishes its real box as
 * `--ws-bar-px` (mobile-tab-bar.tsx), inset included, and that is the
 * answer whenever it is set. The old `--ws-bar-b` parse is the fallback for
 * the frame before the bar has measured: it says `3.5rem` while the bar is
 * `3.5rem + env(safe-area-inset-bottom)` tall, which is exactly the 24 px by
 * which the clamp's ceiling overran the tester's Pixel 8 Pro.
 */
function mobileBarPx(): number {
  const root = getComputedStyle(document.documentElement);
  const measured = root.getPropertyValue(BAR_PX_VAR).trim();
  if (measured.endsWith('px')) {
    const px = parseFloat(measured);
    if (Number.isFinite(px) && px >= 0) return px;
  }
  const raw = root.getPropertyValue('--ws-bar-b').trim();
  return raw.endsWith('rem')
    ? parseFloat(raw) * (parseFloat(root.fontSize) || 16)
    : parseFloat(raw) || 0;
}

/**
 * Render the React Flow canvas. Must be mounted inside a `ReactFlowProvider`
 * shared with the workspace chrome (panels, dock, tab bar) so they can access
 * the same flow instance via `useReactFlow()`.
 */
export function WorkspaceCanvas() {
  const t = useT();
  const items = useWorkspaceItems();
  // This tab's live content metadata, for every menu and hint below that
  // names a window or a screen (Pick layer, Move to screen, Go to screen,
  // Share, the edge-swipe hint). They read the snapshot stored in the shared
  // item, so a renamed app kept its old name in them (the 2026-09-13 browser
  // pass); they resolve through item-label.ts now. Read-only, and it changes
  // only when `seedContent` replaces the slice, far less often than `items`.
  const liveContentMeta = useLiveContentMeta();
  const workspace = useWorkspace();
  const {
    patchItemState,
    addItem,
    setCamera,
    removeItem,
    bringToFront,
    setPendingAnnotation,
    setViewportAnchor,
    setViewportRect,
    setViewportBox,
    setCurrentScreen,
    setOverviewMode,
    setHandsetFullscreen,
    clearPendingFullscreen,
    recallFullscreenOnLoad,
    enterDesktopFullscreen,
    exitDesktopFullscreen,
    restoreScreens,
    settleAfterMove,
    setScreenView,
  } = useWorkspaceActions();
  const [snap, setSnap] = useState(false);
  // Rail V2 hosts zoom + annotation tools in the left rail, so the
  // floating ZoomControls column is gated off (see render below).
  const railV2 = useRailV2();
  const flow = useReactFlow();
  // Direct handle to React Flow's internal Zustand store. We use it
  // for one job only — clearing selection on pane click. RF's own
  // `resetSelectedElements` iterates the `nodes` array from the store
  // and bails on entries without `selected === true`. In our
  // controlled setup (where we pass `nodes` from our own store and
  // never attach `selected` to the Node objects), every entry's
  // `selected` reads as `undefined`, so the reset is a no-op and the
  // floating annotation toolbar stays mounted even after the user
  // taps the empty canvas. The controlled `selectedNodeIds` /
  // `selectedEdgeIds` sets declared below own that state now.
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Overview state is read HERE (above the nodes memo) because the
  // Exposé spread overrides node positions at render time.
  const overviewMode = useOverviewMode();

  // DESKTOP FULL SCREEN (lane D, 2026-09-29; the Stage algorithm, rules 12
  // to 17; wireframes v3, section B). The windows THIS device shows full
  // screen (the store's local `desktopFullscreen`, never the shared
  // document), each on a temporary screen of its own right after the screen
  // it came from (`displayScreenEntries`). While the view is on one
  // (`desktopFullscreenShown`), the node builder draws that window over the
  // whole canvas at a display-only size, its stored top-left under the
  // camera's origin (`fullscreenCameraFor`), with the size echo skipped, and
  // hides every other node; NodeFrame's header is lane B's desktop app bar;
  // the rail stands down. The page, not the node, is the browser's full
  // screen (maximize-app-node.ts), so Stage and the other screens stay one
  // chord away and a refusal writes nothing. Read through refs by the
  // long-lived camera callbacks, like the handset presentation below.
  const desktopFullscreen = useDesktopFullscreen();
  const desktopFullscreenIds = useMemo(
    () => desktopFullscreen.map((r) => r.id),
    [desktopFullscreen],
  );
  const desktopFullscreenIdsRef = useRef(desktopFullscreenIds);
  desktopFullscreenIdsRef.current = desktopFullscreenIds;
  const desktopFullscreenRef = useRef(desktopFullscreen);
  desktopFullscreenRef.current = desktopFullscreen;
  const desktopFullscreenShownRaw = useDesktopFullscreenShownId();

  // True for the lifetime of a window/selection drag (same window as
  // the `.ws-item-drag` wrapper class). Freezes the overview grid so a
  // dragged card can't tow the lattice around, and exempts the dragged
  // items from the display overrides so they track the pointer 1:1.
  const [itemDragActive, setItemDragActive] = useState(false);
  // Every node participating in the current drag, at its TRUE (screen-
  // space) start coords — the Esc-cancel restore target. Declared here,
  // above the nodes memo, because the memo reads it during render.
  const dragStartPosRef = useRef<Map<string, { x: number; y: number }> | null>(null);
  const dragCancelRef = useRef<Map<string, { x: number; y: number }> | null>(null);

  /**
   * The overview lattice (Part VI): display-only grid of screenful
   * cells + the "+ New screen" tile cell. Null outside the overview.
   */
  const overviewGrid = useMemo<OverviewGrid | null>(() => {
    if (!overviewMode) return null;
    const box = wrapperRef.current?.getBoundingClientRect();
    if (!box || box.width < 40 || box.height < 40) return null;
    return computeOverviewGrid(
      items,
      SCREEN_CELL.width,
      SCREEN_CELL.height,
      Math.round(box.width),
      Math.round(box.height),
      displayScreenEntries(items, desktopFullscreenIds),
    );
  }, [overviewMode, items, desktopFullscreenIds]);

  // While a drag is in flight the grid FREEZES: cells and deltas derive
  // from item bounds, so recomputing live would let the dragged card
  // drag the whole lattice (and the tile) along with it.
  const frozenGridRef = useRef<OverviewGrid | null>(null);
  if (!itemDragActive && frozenGridRef.current) frozenGridRef.current = null;
  if (itemDragActive && !frozenGridRef.current && overviewGrid) {
    frozenGridRef.current = overviewGrid;
  }
  const effectiveGrid = itemDragActive
    ? (frozenGridRef.current ?? overviewGrid)
    : overviewGrid;

  // The morph class outlives the mode by one beat so the cards glide
  // BACK to true geometry on exit instead of teleporting.
  // A short-lived transform transition for a window we MOVE on the user's
  // behalf (the off-screen rescue below) — the same glide the overview
  // morph uses, so a corrected window slides home instead of teleporting.
  const [glideAnim, setGlideAnim] = useState(false);
  const glideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const glideOnce = useCallback(() => {
    setGlideAnim(true);
    if (glideTimerRef.current) clearTimeout(glideTimerRef.current);
    glideTimerRef.current = setTimeout(() => setGlideAnim(false), 380);
  }, []);
  useEffect(
    () => () => {
      if (glideTimerRef.current) clearTimeout(glideTimerRef.current);
    },
    [],
  );
  const [overviewAnim, setOverviewAnim] = useState(false);
  useEffect(() => {
    if (overviewMode) {
      setOverviewAnim(true);
      return;
    }
    const t = setTimeout(() => setOverviewAnim(false), 420);
    return () => clearTimeout(t);
  }, [overviewMode]);

  /**
   * Create a BLANK SCREEN (Part IV): its backing board sheet is exactly
   * one screenful, pinned to the next free lattice cell (or the given
   * spot), at the BACK of the stack. Auto-named "Screen N" (Part VI) —
   * renameable from the overview's label row. N is the number the screen
   * takes on Stage (`nameScreen`, fix round 2), worked out by the store once
   * the sheet's place is final: it was one past the highest "Screen N" in
   * use, so the second screen of a workspace read "2 · Screen 1".
   */
  const createBoardAt = useCallback(
    (at?: { x: number; y: number }) => {
      const minZ = items.reduce(
        (acc, it) => Math.min(acc, it.state.zIndex ?? 0),
        0,
      );
      // Sheet size = one lattice CELL. It used to be the wrapper's pixel
      // size, so the same "new screen" was a different object on a laptop, a
      // phone and a 4K display — and those mismatched rects are what made
      // screens overlap. See SCREEN_CELL.
      const dims = { width: SCREEN_CELL.width, height: SCREEN_CELL.height };
      void addItem({
        type: 'board',
        refId: null,
        nameScreen: true,
        state: {
          ...(at
            ? {
                x: Math.round(at.x / GRID_MINOR) * GRID_MINOR,
                y: Math.round(at.y / GRID_MINOR) * GRID_MINOR,
              }
            : {}),
          ...dims,
          zIndex: minZ - 1,
        },
      });
    },
    [addItem, items],
  );

  // ---- canvas context menu -----------------------------------------------
  // Right-click on the empty pane (or long-press on touch) opens a tiny
  // utility menu with the most common viewport actions: jump-to-anything
  // search, paste-from-clipboard, fit-to-view, and reset-zoom. Anchor is
  // pointer coordinates in viewport space; flow position is also stashed
  // so paste can drop content under the cursor instead of canvas centre.
  const [paneMenu, setPaneMenu] = useState<{
    x: number;
    y: number;
    flowX: number;
    flowY: number;
  } | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressStart = useRef<{ x: number; y: number } | null>(null);

  const openPaneMenu = useCallback(
    (e: { clientX: number; clientY: number }) => {
      // A long-press over a widget must raise ONLY the widget menu. On
      // touch (esp. Android) a long-press also fires a native
      // `contextmenu`, and mid-hold `.ws-gesturing` makes nodes
      // pointer-transparent — so the event hit-tests to the pane and
      // would open this "workspace" menu on top of the widget menu. If a
      // node (widget) menu opened within the same gesture window, defer
      // to it. `lastOpenRef.at` is stamped by `openNodeMenu` below.
      const lastNodeOpenAt = lastOpenRef.current?.at ?? 0;
      if (Date.now() - lastNodeOpenAt < CROSS_MENU_SUPPRESS_MS) return;
      const pt = flow.screenToFlowPosition({ x: e.clientX, y: e.clientY });
      setNodeMenu(null);
      setPaneMenu({ x: e.clientX, y: e.clientY, flowX: pt.x, flowY: pt.y });
    },
    [flow],
  );

  // Best-effort clipboard text → upload it as a markdown content
  // item, then add a content widget pointing at the new id under the
  // pointer. Silent when the clipboard is empty or permission denied;
  // we never spam the user with an alert from a context menu.
  const pasteAtPoint = useCallback(
    async (flowX: number, flowY: number) => {
      try {
        const text = await navigator.clipboard?.readText();
        if (!text || !text.trim()) return;
        const blob = new Blob([text], { type: 'text/markdown' });
        // Resumable like every portal upload (CASFS-UI PF-4); a note is one part.
        const data = await uploadResumable(blob, { filename: 'Pasted note.md', mimeType: 'text/markdown', displayName: 'Pasted note' });
        const id = data.id;
        await addItem({
          type: 'content',
          refId: id,
          // Cursor-anchored: the user invoked this from the right-click
          // menu at a specific point. `placement: 'cursor'` keeps the
          // widget near that point and only slides it off if the exact
          // spot is occupied.
          placement: 'cursor',
          state: {
            x: flowX - 160,
            y: flowY - 110,
            width: 320,
            height: 220,
            cache: {
              displayName: data.displayName,
              contentType: 'document',
              mimeType: 'text/markdown',
            },
          },
        });
      } catch {
        /* clipboard blocked or upload failed — silent */
      }
    },
    [addItem],
  );

  // ---- node context menu ------------------------------------------------
  // Right-click / long-press on a widget opens a per-node menu. Mouse
  // right-click flows through ReactFlow's `onNodeContextMenu`; touch
  // long-press is detected inside NodeFrame which dispatches a window
  // event we listen for here. Items are derived from the node's type,
  // its position relative to the viewport, and whether it's already
  // docked.
  const [nodeMenu, setNodeMenu] = useState<{
    x: number;
    y: number;
    itemId: string;
  } | null>(null);

  // Dedupe rapid duplicate opens (within 250 ms for the same itemId).
  // On Android Chrome the browser's native `contextmenu` event AND
  // our `NodeFrame` 500 ms touch timer can both reach this function
  // for the same long-press, with very slightly different x/y
  // coordinates — that causes the menu to re-render at the second
  // position, looking like a flicker or two stacked menus. 250 ms is
  // well outside the contextmenu-vs-timer jitter window in practice,
  // and short enough that an intentional re-open after the user
  // dismisses isn't blocked.
  const lastOpenRef = useRef<{ itemId: string; at: number } | null>(null);
  // Latest-selection refs so `openNodeMenu` can branch on multi-select
  // without taking selection state into its `useCallback` dep array
  // (the dep would re-create the callback on every selection change
  // and bust downstream memoisation that wires it into RF). The
  // refs themselves are declared here; syncing effects sit right
  // below the early `selectedNodeIds` useState block.
  const selectedNodeIdsRef = useRef<Set<string>>(new Set());
  const selectedEdgeIdsRef = useRef<Set<string>>(new Set());

  // ---- selection state (early) -------------------------------------------
  //
  // Declared here — BEFORE the command registry / clipboard hooks —
  // so `useWorkspaceClipboard` and `useCommandContext` can read the
  // live selection via an explicit override. Those hooks mount in
  // this component ABOVE `WorkspaceSelectionProvider` (the provider
  // wraps the JSX return, not the hook body), so `useContext` alone
  // would always see the default empty sets. That was the root cause
  // of "..." showing only Paste and Cmd+C/V doing nothing.
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [selectedEdgeIds, setSelectedEdgeIds] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    selectedNodeIdsRef.current = selectedNodeIds;
  }, [selectedNodeIds]);
  useEffect(() => {
    selectedEdgeIdsRef.current = selectedEdgeIds;
  }, [selectedEdgeIds]);

  // Touch-first gesture model gate. On a coarse (finger) primary pointer
  // we switch the canvas to "pan-first": one finger always pans, and a
  // widget is only draggable while selected (see the nodes memo below)
  // so a drag over an unselected widget falls through to canvas panning.
  // Fine-pointer (desktop / touch-laptop trackpad) keeps today's direct-
  // manipulation behaviour untouched.
  const isCoarse = useCoarsePointer();
  // Width breakpoint for the narrow-overview behavior (pan + width-fit)
  // and the mobile bar offsets — width-gated like the shell's other
  // navigation chrome, deliberately not pointer-gated.
  const isMdViewport = useIsMd();
  // Handset framing (see `frameViewportForRect`): the 100% law holds only
  // where a screenful and the viewport are the same size, which is never
  // true on a phone. Same condition the Stage SHEET uses to replace the
  // spatial Stage, so the two presentations agree about what a handset is.
  const handsetLandscape = useHandsetLandscape();
  // Read through a ref so `frameVp` stays referentially stable: every
  // camera callback below closes over it, and making it a dependency would
  // churn a dozen memoized handlers on an orientation change.
  const fitFramingRef = useRef(false);
  fitFramingRef.current = !isMdViewport || handsetLandscape;
  // The same flag as a render value, for the node builder below: the ref
  // exists so camera callbacks stay stable, but the nodes memo must re-run
  // when framing flips (crossing md, rotating), because it draws windows
  // CLAMPED to the visible viewport while handset framing is on (display
  // only; see handset-node-clamp.ts and the e2e run of 2026-09-26, where a
  // desktop-sized window opened on a phone with its edges off screen).
  const fitFraming = fitFramingRef.current;
  // The visible canvas box in screen px, bottom bar subtracted; kept as
  // state (deduped by value) so the clamp re-runs on a viewport resize.
  const [canvasBox, setCanvasBox] = useState<{ width: number; height: number } | null>(null);
  // THE PHONE APP VIEW (handoff 2026-09-29, sections 4 and 5; wireframes v3
  // section A). On a handset the platform presents ONE window, filling the
  // tab between the browser's bar and the bottom navigation, in place and
  // writing nothing. `usableBoxState` is the measured box it fills (the
  // wrapper minus the MEASURED navigation and the safe-area insets, see
  // lib/workspaces/handset-present.ts); `presentedId` is the window (the
  // parked screen's own window, or a blank screen's top member). The node
  // builder draws that window at the usable size and hides every other
  // node; the camera callbacks below put its stored top-left on the usable
  // box's top-left at zoom 1 through `handsetPresentationCamera`, so what is
  // drawn and where the camera looks come from one function (RV-023 was the
  // two disagreeing). Null on a desktop: nothing below changes there.
  const [usableBoxState, setUsableBoxState] = useState<UsableBox | null>(null);
  const usableBoxRef = useRef<UsableBox | null>(null);
  const presentedWindow = usePresentedWindowId();
  const presentedId = fitFraming ? presentedWindow : null;
  const presentedRef = useRef<string | null>(null);
  presentedRef.current = presentedId;
  // The window whose temporary full-screen screen the view is on (lane D;
  // see `desktopFullscreen` above). A desktop only: a handset shows one app
  // at a time already and clears the list (below).
  const desktopFullscreenShown = fitFraming ? null : desktopFullscreenShownRaw;
  const desktopFullscreenShownRef = useRef<string | null>(null);
  desktopFullscreenShownRef.current = desktopFullscreenShown;
  // Ids drawn smaller than their persisted size right now. RF's
  // ResizeObserver measures the clamped DOM and emits `dimensions` for it;
  // the echo skips these ids so the phone's clamp never becomes the size.
  // The presented window joins the set: it is drawn at the usable box, and
  // that must never be written back either. So do the windows this device
  // shows full screen (lane D): drawn at the canvas box, never that size.
  const handsetClampedRef = useRef<Set<string>>(new Set());
  const frameVp = useCallback(
    (
      rect: ScreenRect,
      viewportWidth: number,
      viewportHeight: number,
      /**
       * The entry kind decides the handset framing (user call): an APP
       * screen lands at 100% anchored top-left -- a whole desktop-scale
       * screenful fit into a phone was unreadable AND spilled far enough
       * vertically to show the neighbouring screens. Blank screens (and
       * unknown callers) keep the fit: their content is the sheet itself.
       */
      kind?: 'app' | 'blank',
    ) =>
      frameViewportForRect(rect, viewportWidth, viewportHeight, {
        fit: fitFramingRef.current && kind !== 'app',
      }),
    [],
  );
  /**
   * Handset framing target for a screen ENTRY. Every screen is board-backed
   * now (app-opened screens mint their own sheet), so "is the item a board"
   * stopped meaning "is this screen blank" — the sheet's screen taps all
   * fit-framed again, landing zoomed out with the vertical neighbours in
   * view. The real questions are: does the screen HOLD app windows (then
   * land at 100% — 'app' framing), and WHERE (on the WINDOWS' union, not
   * the cell — a phone-sized window centred in a 1600px cell would
   * otherwise anchor on empty corner space). Desktop framing is untouched:
   * without fit framing the entry rect stands.
   */
  const frameTargetForEntry = useCallback(
    (entry: {
      rect: ScreenRect;
      memberIds?: string[];
    }): { rect: ScreenRect; kind: 'app' | 'blank' } => {
      // The WHAT-to-frame decision lives in `frameEntryTarget` (screens.ts,
      // pure + pinned by tests): handsets land on the member windows' union;
      // the desktop does too WHEN the entry rect overflows the canvas box —
      // framing a 1600x1000 cell in a smaller box anchors the CELL's corner
      // and hangs the window (and its bottom toolbar) off the fold, the
      // measured spawn-clipping bug. A cell that fits keeps today's framing.
      const memberRects = (entry.memberIds ?? [])
        .map((mid) => screensItemsRef.current.find((x) => x.id === mid))
        .filter(
          (it): it is NonNullable<typeof it> => !!it && it.type !== 'board',
        )
        .map((it) => ({
          x: it.state.x,
          y: it.state.y,
          width: it.state.width,
          height: it.state.height,
        }));
      const b = wrapperRef.current?.getBoundingClientRect();
      const box =
        b && b.width > 0 && b.height > 0
          ? { width: b.width, height: b.height }
          : null;
      return frameEntryTarget(entry, memberRects, box, fitFramingRef.current);
    },
    [],
  );
  /**
   * THIS DEVICE'S LAST CAMERA FOR EACH SCREEN (fix round 2, 2026-09-30;
   * lib/workspaces/screen-camera.ts has the whole why). A return to a screen
   * used to frame it again, centring its windows' union, which put a Stage
   * cascade's view 12 px off the one it was built in, and the next open then
   * covered a window whole (lane D's open question 1). The canvas now
   * remembers the camera it rested on for each screen (`rememberCameraNow`,
   * by `animateViewport` and a gesture's end) and a return restores it
   * exactly while it still shows the screen. Per device (this page, and
   * `localStorage` for the next load), desktop only: a handset presents one
   * app with its own camera, and a full-screen screen has its own.
   */
  const cameraMemoryRef = useRef<ScreenCameraMemory | null>(null);
  if (cameraMemoryRef.current === null) cameraMemoryRef.current = loadScreenCameras(workspace.id);
  const recalledCamera = useCallback(
    (entry: ScreenEntry): ViewportTransform | null => {
      if (fitFramingRef.current || !cameraMemoryRef.current) return null;
      const b = wrapperRef.current?.getBoundingClientRect();
      if (!b || b.width <= 0 || b.height <= 0) return null;
      return recallScreenCamera(cameraMemoryRef.current, entry, screensItemsRef.current, {
        width: b.width,
        height: b.height,
      });
    },
    [],
  );
  /**
   * The handset PRESENTATION camera for the screen `id` (the id the camera
   * is about to park on: a window, or a blank screen's sheet), or null when
   * there is nothing to present: a desktop, a blank screen without windows,
   * an unmeasured box. The camera puts the presented window's stored
   * top-left on the usable box's top-left at zoom 1; the node builder draws
   * the same window at the usable size, so the two agree by construction
   * (lib/workspaces/handset-present.ts). Nothing is written.
   */
  const handsetPresentationCamera = useCallback(
    (id: string | null): ViewportTransform | null => {
      if (!fitFramingRef.current) return null;
      const usable = usableBoxRef.current;
      if (!usable) return null;
      const pid = presentedWindowId(screensItemsRef.current, id);
      if (!pid) return null;
      const it = screensItemsRef.current.find((x) => x.id === pid);
      if (!it) return null;
      return presentOnHandset(it.state, usable).camera;
    },
    [],
  );
  /**
   * The DESKTOP FULL-SCREEN camera for the screen `id` (lane D, rules 12 to
   * 14): when `id` is a window this device shows full screen, its stored
   * top-left on the canvas's top-left at 100% (`fullscreenCameraFor`); the
   * node builder draws the same window at the canvas box from there, so the
   * two agree by construction. Null otherwise (and always on a handset).
   * Nothing is written.
   */
  const fullscreenCamera = useCallback(
    (id: string | null): ViewportTransform | null => {
      if (fitFramingRef.current || !id) return null;
      if (!desktopFullscreenIdsRef.current.includes(id)) return null;
      const it = screensItemsRef.current.find((x) => x.id === id);
      if (!it || !isOnCanvas(it.state)) return null;
      return fullscreenCameraFor(it.state);
    },
    [],
  );
  /**
   * THE one camera for framing a screen entry: the handset presentation when
   * a window will be presented (`parkId` names the member the camera parks
   * on; the entry's own id otherwise), a desktop full-screen screen's own
   * camera (lane D), else the desktop path exactly as it was
   * (`frameTargetForEntry` then `frameVp`). `frameScreen`,
   * `pageToNeighbor`, the edge swipe and the settle rescue all go through
   * here, so the handset camera cannot drift apart across the call sites the
   * way the box height did (handoff section 5, stage 1, step 2).
   */
  const frameEntryCamera = useCallback(
    (
      entry: { id: string; rect: ScreenRect; memberIds?: string[]; kind?: ScreenEntry['kind'] },
      box: { width: number; height: number },
      parkId?: string,
    ): ViewportTransform => {
      const presented = handsetPresentationCamera(parkId ?? entry.id);
      if (presented) return presented;
      const full = fullscreenCamera(parkId ?? entry.id);
      if (full) return full;
      // A return to a screen: this device's last camera for it, while it
      // still shows it (fix round 2). Only for a real screen entry.
      const recalled =
        entry.kind && entry.memberIds
          ? recalledCamera({ id: entry.id, rect: entry.rect, memberIds: entry.memberIds, kind: entry.kind })
          : null;
      if (recalled) return recalled;
      const t = frameTargetForEntry(entry);
      return frameVp(t.rect, box.width, box.height, t.kind);
    },
    [frameTargetForEntry, frameVp, fullscreenCamera, handsetPresentationCamera, recalledCamera],
  );
  /** This device's screen list, now (the refs the callbacks read). */
  const screenEntriesNow = useCallback(
    (source?: WorkspaceItemDTO[]) =>
      displayScreenEntries(source ?? screensItemsRef.current, desktopFullscreenIdsRef.current),
    [],
  );

  // Touch "navigate / zoom-lock" mode (the canvas FAB): while on, widgets
  // ignore touches so pinch/pan reaches the canvas everywhere (see
  // `.ws-navigate` in globals.css). Auto-off when the pointer isn't coarse.
  const [navigateMode, setNavigateMode] = useState(false);
  /**
   * Max zoom-OUT (user call): the camera never shows more world than about
   * one screen-cell width, so zooming out in hand mode can't wander into a
   * sea of neighbouring screens. Matches the fit-framing math (margins
   * included) so a phone's fit-width framing sits exactly at the floor.
   * The Stage lattice needs far deeper zooms — the prop switches there.
   */
  const [dynMinZoom, setDynMinZoom] = useState(0.08);
  const dynMinZoomRef = useRef(0.08);
  /**
   * Fit-all needs to zoom BELOW the interaction floor (many screens cannot
   * fit above it). Pressing it relaxes the floor; the first time the user
   * zooms back above the normal floor, the relax clears and the limit is
   * law again.
   */
  const [zoomFloorRelaxed, setZoomFloorRelaxed] = useState(false);
  // Read by the long-lived capture-phase touch listeners (bound once) —
  // hand mode must not re-bind them just to change one predicate.
  const navigateModeRef = useRef(false);
  useEffect(() => {
    navigateModeRef.current = navigateMode;
  }, [navigateMode]);
  // Mirror for the long-lived touch listeners: two-finger nav is DEFAULT on
  // coarse pointers (no mode), so the pinch handler needs the live value.
  const isCoarseRef = useRef(false);
  useEffect(() => {
    isCoarseRef.current = isCoarse;
  }, [isCoarse]);
  // DEV-ONLY automation hook: the Hand FAB is coarse-gated, so desktop
  // automation (extension-driven verification, e2e) has no UI way into
  // navigate mode. Stripped from production bundles by the NODE_ENV fold.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    (window as unknown as Record<string, unknown>).__wsSetNavigateMode = setNavigateMode;
    return () => {
      delete (window as unknown as Record<string, unknown>).__wsSetNavigateMode;
    };
  }, []);
  // Edge-peek affordance: the neighbor's NAME rides the pulled edge while
  // the gesture is live, so the beginning of a swipe is unmistakable.
  const [peekHint, setPeekHint] = useState<{ label: string; dir: -1 | 1 } | null>(null);
  // Late-bound handle to `maybeSettle` (declared further down) for the
  // gesture effects above its definition - refs dodge the TDZ that a dep
  // array would trip on.
  const maybeSettleRef = useRef<(duration?: number) => void>(() => {});
  /**
   * Hand-mode "Fit" (mobile zoom control): frame the screen the camera is
   * nearest to. The recovery move for a pinch that went too far — one tap
   * returns to a readable screenful without leaving hand mode. Research
   * (Figma/FigJam, Miro, tldraw mobile) converges on gesture-first with ONE
   * lightweight recover control, not a +/- cluster.
   */
  /** Hand-mode "100%": back to readable scale, viewport centre anchored. */
  const zoomTo100 = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    const c = flow.screenToFlowPosition({
      x: box.left + box.width / 2,
      y: box.top + box.height / 2,
    });
    flow.setViewport(
      { x: box.width / 2 - c.x, y: box.height / 2 - c.y, zoom: 1 },
      { duration: 240 },
    );
  }, [flow]);
  /** Zoom menu "Fit all": every screen in view, floor temporarily relaxed. */
  const fitAllScreens = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    if (box.width === 0 || box.height === 0) return;
    const entries = orderScreenEntries(screensItemsRef.current);
    if (entries.length === 0) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const e of entries) {
      minX = Math.min(minX, e.rect.x);
      minY = Math.min(minY, e.rect.y);
      maxX = Math.max(maxX, e.rect.x + e.rect.width);
      maxY = Math.max(maxY, e.rect.y + e.rect.height);
    }
    const bw = Math.max(1, maxX - minX);
    const bh = Math.max(1, maxY - minY);
    const PAD = 48;
    const zoom = Math.max(
      0.05,
      Math.min(1, (box.width - PAD * 2) / bw, (box.height - PAD * 2) / bh),
    );
    setZoomFloorRelaxed(true);
    // The relaxed minZoom must RENDER before the clamped setViewport runs.
    window.requestAnimationFrame(() => {
      flow.setViewport(
        {
          x: box.width / 2 - (minX + bw / 2) * zoom,
          y: box.height / 2 - (minY + bh / 2) * zoom,
          zoom,
        },
        { duration: 260 },
      );
    });
  }, [flow]);

  const fitNearestScreen = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    if (box.width === 0 || box.height === 0) return;
    const tl = flow.screenToFlowPosition({ x: box.left, y: box.top });
    const br = flow.screenToFlowPosition({ x: box.right, y: box.bottom });
    const view = { x: tl.x, y: tl.y, width: br.x - tl.x, height: br.y - tl.y };
    const entries = orderScreenEntries(screensItemsRef.current).map((e) => ({
      id: e.id,
      rect: e.rect,
    }));
    if (entries.length === 0) return;
    // Coverage-then-distance — degrades to nearest-centre when zoomed far
    // out and everything is tiny.
    const pick = pickSettleScreen(entries, view);
    const rect = entries.find((e) => e.id === pick?.id)?.rect;
    if (!rect) return;
    flow.setViewport(
      frameViewportForRect(rect, box.width, box.height, { fit: true }),
      { duration: 280 },
    );
  }, [flow]);
  // In hand mode widgets are inert (pointer-events off is what lets the
  // pan/pinch travel OVER iframes). A single TAP no longer exits the mode
  // (user call — an accidental tap kept dropping people out of it); instead
  // the tap is FORWARDED to whatever the inertness was masking: lift the
  // inert class for a hit-test, then replay the tap on the revealed element
  // so window chrome and native widget UI respond while hand mode stays on.
  // The hard boundary stands: a cross-origin app iframe's INTERIOR can never
  // receive synthesized events, so there the replay lands on the iframe's
  // element and the window frame (select / raise) responds, not the app's
  // own buttons. Exiting the mode is the Hand FAB, only.
  useEffect(() => {
    if (!navigateMode) return;
    const el = wrapperRef.current;
    if (!el) return;
    let start: { x: number; y: number; t: number } | null = null;
    const down = (e: PointerEvent) => {
      // Skip only OUR OWN replayed events (marked below) — they bubble back
      // through these capture listeners and must not re-arm the detector.
      // Everything else counts, whatever the pointer type and however it
      // was produced: "tap" here means press-without-drag, and gating on
      // isTrusted broke automation and some webviews for no safety gain.
      if ((e as unknown as { __wsReplay?: boolean }).__wsReplay) return;
      start = { x: e.clientX, y: e.clientY, t: e.timeStamp || performance.now() };
    };
    const up = (e: PointerEvent) => {
      if (!start || (e as unknown as { __wsReplay?: boolean }).__wsReplay) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      // EVENT timestamps, not handler-run clocks: the duration guard must
      // measure the FINGER, not the main thread. Handler-time deltas
      // (performance.now() in down vs up) swallow every jank between the
      // two handlers — on a dev build under device emulation that
      // regularly exceeded any sane tap window, so every real tap was
      // rejected as a long-press while back-to-back synthetic pairs
      // passed. Found live from a tap log with all events delivered and
      // zero forwards.
      const dt = (e.timeStamp || performance.now()) - start.t;
      start = null;
      // 500ms + 12px: generous, because a throttled device stretches and
      // wobbles real taps; drags are excluded by movement, and a genuine
      // long-press (context menu territory) sits well past 500ms.
      if (Math.hypot(dx, dy) >= 12 || dt >= 500) return;
      const t = e.target as HTMLElement | null;
      // Chrome (the FABs, the guest pill, panels) is not inert — it already
      // received the real tap; replaying would double-fire it.
      if (t?.closest('button')) return;
      // What was the inertness hiding? The probe class FORCES nodes
      // hittable for one synchronous hit-test — overriding ws-navigate AND
      // ws-gesturing, because a real tap's pointerdown starts a pane pan
      // and the gesture lock would otherwise keep the nodes masked (which
      // silently killed forwarding for every real tap while synthetic
      // test taps, which start no gesture, sailed through).
      el.classList.add('ws-tap-probe');
      let revealed: Element | null = null;
      try {
        revealed = document.elementFromPoint(e.clientX, e.clientY);
      } finally {
        el.classList.remove('ws-tap-probe');
      }
      // Nothing was masked (the tap really was on the pane / empty canvas):
      // the real events already did everything there was to do.
      if (!revealed || revealed === e.target || !el.contains(revealed)) return;
      // An app IFRAME: its interior is a separate document that events
      // synthesized here can never enter (browser boundary). Forward the
      // tap's iframe-local coordinates instead — the sandbox shell (or the
      // injected forwarder on the srcdoc fallback) re-dispatches the tap
      // INSIDE the app's own document, where it is a real interaction.
      // The replay below still runs on the iframe ELEMENT, so the window
      // frame responds too (select / raise), like any tap on a window.
      if (revealed instanceof HTMLIFrameElement && revealed.contentWindow) {
        const r = revealed.getBoundingClientRect();
        // rect is VISUAL (canvas zoom applies), clientWidth is LAYOUT —
        // divide out the scale so the coordinates land in the app's own
        // CSS pixel space.
        const sx = r.width / Math.max(1, revealed.clientWidth || r.width);
        const sy = r.height / Math.max(1, revealed.clientHeight || r.height);
        revealed.contentWindow.postMessage(
          {
            source: 'robutler-host-tap',
            x: Math.round((e.clientX - r.left) / sx),
            y: Math.round((e.clientY - r.top) / sy),
          },
          '*',
        );
      }
      const base = {
        bubbles: true,
        cancelable: true,
        clientX: e.clientX,
        clientY: e.clientY,
        view: window,
      } as const;
      const mark = (ev: Event) => {
        (ev as unknown as { __wsReplay?: boolean }).__wsReplay = true;
        return ev;
      };
      revealed.dispatchEvent(
        mark(
          new PointerEvent('pointerdown', {
            ...base,
            pointerId: e.pointerId,
            pointerType: 'touch',
            isPrimary: true,
          }),
        ),
      );
      revealed.dispatchEvent(
        mark(
          new PointerEvent('pointerup', {
            ...base,
            pointerId: e.pointerId,
            pointerType: 'touch',
            isPrimary: true,
          }),
        ),
      );
      revealed.dispatchEvent(mark(new MouseEvent('click', base)));
    };
    // Touch-stream fallback: complete the tap from `touchend` as well.
    // Whichever of pointerup/touchend lands first wins (`start` clears), so
    // a browser that swallows one of the two streams mid-gesture — d3-zoom
    // and the pinch handler both manipulate the touch stream — still
    // completes the tap from the other.
    const touchUp = (e: TouchEvent) => {
      const t = e.changedTouches[0];
      if (!t) return;
      up({
        clientX: t.clientX,
        clientY: t.clientY,
        pointerId: 1,
        target: e.target,
        timeStamp: e.timeStamp,
      } as unknown as PointerEvent);
    };
    el.addEventListener('pointerdown', down, { capture: true, passive: true });
    el.addEventListener('pointerup', up, { capture: true, passive: true });
    el.addEventListener('touchend', touchUp, { capture: true, passive: true });
    return () => {
      el.removeEventListener('pointerdown', down, { capture: true });
      el.removeEventListener('pointerup', up, { capture: true });
      el.removeEventListener('touchend', touchUp, { capture: true });
    };
  }, [navigateMode]);

  const selectionSnapshot = useMemo(
    () => ({
      count: selectedNodeIds.size + selectedEdgeIds.size,
      nodeIds: selectedNodeIds,
      edgeIds: selectedEdgeIds,
    }),
    [selectedNodeIds, selectedEdgeIds],
  );

  const openNodeMenu = useCallback(
    (itemId: string, x: number, y: number) => {
      const now = Date.now();
      const prev = lastOpenRef.current;
      if (prev && prev.itemId === itemId && now - prev.at < 250) return;
      lastOpenRef.current = { itemId, at: now };

      // Multi-select route: when more than one item is selected AND
      // the right-clicked item is part of that selection, the per-
      // node context menu is suppressed. The unified
      // `SelectionToolbar` is the single multi-item action surface,
      // and stacking a per-node menu on top of it would let the user
      // accidentally Delete just the right-clicked widget while
      // visually believing the selection was the target.
      // If the click hits something OUTSIDE the current selection,
      // RF's default behaviour will shift selection to that node
      // first (because the pointerdown happens before the
      // contextmenu) — that's why we still fall through to the
      // per-node menu in that case.
      const selCount =
        selectedNodeIdsRef.current.size + selectedEdgeIdsRef.current.size;
      if (
        selCount > 1 &&
        (selectedNodeIdsRef.current.has(itemId) ||
          selectedEdgeIdsRef.current.has(itemId))
      ) {
        return;
      }

      const target = items.find((it) => it.id === itemId);
      // Right-click on ANY item — including annotations and
      // connectors — opens the unified canvas context menu so the
      // command set is consistent across surfaces. The Figjam-style
      // floating value editors for annotations / connectors stay
      // for property tweaks (color, stroke, arrowheads, …); the
      // right-click menu carries the cross-cutting commands
      // (duplicate, delete, bring to front, …).
      //
      // We first auto-select the right-clicked item so the
      // registry's predicates evaluate against the correct
      // selection. We poke `selectedNodeIds` / `selectedEdgeIds`
      // directly rather than going through `flow.setNodes` /
      // `flow.setEdges` — the imperative API only emits
      // `'replace'` diffs in our controlled setup, never
      // `'select'` changes, so the new change-driven selection
      // path wouldn't see the toggle.
      if (target?.type === 'connector') {
        setSelectedEdgeIds(new Set([itemId]));
        setSelectedNodeIds((prev) => (prev.size === 0 ? prev : new Set()));
      } else {
        setSelectedNodeIds(new Set([itemId]));
        setSelectedEdgeIds((prev) => (prev.size === 0 ? prev : new Set()));
      }
      // A widget owns its context menu: clear any pane ("workspace") menu
      // a racing native long-press / contextmenu may have opened, and the
      // timestamp stamped in `lastOpenRef` above lets `openPaneMenu`
      // suppress a pane menu that fires moments later in the same gesture.
      // Together these guarantee a long-press over a widget shows ONLY the
      // widget menu — never two overlapping menus.
      setPaneMenu(null);
      setNodeMenu({ itemId, x, y });
    },
    [items],
  );

  useEffect(() => {
    const onEvent = (e: Event) => {
      const ev = e as CustomEvent<{ itemId: string; x: number; y: number }>;
      if (!ev.detail) return;
      openNodeMenu(ev.detail.itemId, ev.detail.x, ev.detail.y);
    };
    window.addEventListener('workspace:node-context-menu', onEvent);
    return () =>
      window.removeEventListener('workspace:node-context-menu', onEvent);
  }, [openNodeMenu]);

  // Action helpers reused across multiple menu items.
  const focusOnItem = useCallback(
    (id: string) => {
      const it = items.find((x) => x.id === id);
      if (!it) return;
      flow.setCenter(
        it.state.x + it.state.width / 2,
        it.state.y + it.state.height / 2,
        { zoom: flow.getZoom(), duration: 280 },
      );
    },
    [items, flow],
  );

  // `ws:focus-item` is the single chokepoint anything off-canvas (the
  // delegation viz hook, future deep-link consumers) uses to scroll the
  // RF viewport so a particular item is centered + visible. Without a
  // listener the dispatched events used to silently no-op, which made
  // "click talked-to-@agent → nothing visible happens" indistinguishable
  // from a broken handler — the spawn worked but landed offscreen and
  // the user had nothing to focus on.
  //
  // The `expand` flag is consumed by the AgentNode mount: a fresh puck
  // spawn is collapsed, so we ALSO flip the cache.collapsed flag here
  // when the consumer asked us to expand. Best-effort — a missing item
  // (race against tear-down) just drops the focus request.
  //
  // `focusAskedRef`: something asked for a window since this canvas mounted.
  // The arrival camera below ("land where you last were") is deferred by a
  // frame, and a link's own request is not: `/?app=revent` on a workspace
  // whose last screen was another app raised Revent in a microtask and was
  // then framed back onto the other app a frame later, so the link showed
  // the wrong app (the Revent run on dev, 2026-10-02: twice out of two
  // tries for a person whose last screen was Reach; a person whose last
  // screen was Revent never saw it). The arrival camera reads this and
  // frames THAT window instead of the last one. It holds the id, not a flag,
  // because the request may run before the camera has been placed at all:
  // for a window on the screen the view is already parked on, the handler
  // below only moves the parked id (rule 7), and the arrival frame is then
  // what actually puts the window in view.
  const focusAskedRef = useRef<string | null>(null);
  useEffect(() => {
    /**
     * RULE 7 OF THE STAGE ALGORITHM (lane D, 2026-09-29): a window that
     * opened on the screen you are on moves NOTHING, the view included. The
     * camera was framed on that screen before the open; re-framing it after
     * would glide it by half of whatever the window added to the screen's
     * union (6 px for the second window of a cascade, 12 px for the third),
     * and the next open's spots would drift with it. So when the item is on
     * the parked screen and already wholly in view, only the parked id moves.
     * A desktop only: a handset presents the new window instead (lane C).
     */
    const alreadyInView = (id: string): boolean => {
      if (fitFramingRef.current || overviewRef.current || fsRef.current) return false;
      const entries = screenEntriesNow();
      const here = entryOfItem(entries, currentScreenRef.current);
      const mine = entryOfItem(entries, id);
      if (!here || !mine || here.id !== mine.id || mine.fullscreen) return false;
      const el = wrapperRef.current;
      const it = screensItemsRef.current.find((x) => x.id === id);
      if (!el || !it) return false;
      const box = el.getBoundingClientRect();
      if (box.width === 0 || box.height === 0) return false;
      const tl = flow.screenToFlowPosition({ x: box.left, y: box.top });
      const br = flow.screenToFlowPosition({ x: box.right, y: box.bottom });
      return rectContainsRect(
        { x: tl.x, y: tl.y, width: br.x - tl.x, height: br.y - tl.y },
        it.state,
        1,
      );
    };
    /**
     * RULE 6: a person's open takes the keyboard. The app's frame mounts a
     * beat after the window (its row resolves first), so look for it for up
     * to three seconds, and take the keyboard only while nobody else has it:
     * the page (a Discover overlay that just closed hands it back) or another
     * window on the canvas. A field the person has since started typing in
     * keeps it. An agent's open never asks (rule 8).
     */
    const giveKeyboardTo = (id: string) => {
      const started = performance.now();
      const attempt = () => {
        const el = wrapperRef.current;
        if (!el) return;
        const host = Array.from(el.querySelectorAll<HTMLElement>('[data-item-id]')).find(
          (n) => n.getAttribute('data-item-id') === id,
        );
        const frame = host?.querySelector('iframe');
        if (frame) {
          const active = document.activeElement;
          if (!active || active === document.body || el.contains(active)) frame.focus();
          return;
        }
        if (performance.now() - started < 3000) requestAnimationFrame(attempt);
      };
      requestAnimationFrame(attempt);
    };
    const onFocus = (e: Event) => {
      // No `zoom` in the detail: this chokepoint is how every surface
      // brings something into view, and workspace zoom is disabled, so it
      // frames windows at 100% (below) and otherwise pans at the CURRENT
      // zoom. It used to accept `zoom` (the `?focus=<id>&zoom=` deep link)
      // and floored everything else at 0.8, which made it a zoom API.
      const ev = e as CustomEvent<{
        itemId?: string;
        expand?: boolean;
        keyboard?: boolean;
      }>;
      const id = ev.detail?.itemId;
      if (!id) return;
      const it = items.find((x) => x.id === id);
      if (!it) return;
      focusAskedRef.current = id;
      // A blank screen minted FROM the overview stays IN the overview
      // (Part VI): the lattice re-fits to include the new cell — the
      // user is arranging screens, not asking to leave. Select it and
      // stop there.
      if (it.type === 'board' && overviewRef.current) {
        setSelectedNodeIds(new Set([id]));
        setSelectedEdgeIds(new Set());
        return;
      }
      if (isScreenType(it.type) && isOnCanvas(it.state)) {
        if (alreadyInView(id)) {
          setCurrentScreen(id);
        } else {
          // Screens v2: app-like windows are framed (fit + margin, camera
          // parked on them) via the ws:screen chokepoint instead of a bare
          // center-at-current-zoom — spawning or jumping to an app always
          // lands on a fully readable window with its header in view.
          window.dispatchEvent(
            new CustomEvent('ws:screen', { detail: { kind: 'item', itemId: id } }),
          );
        }
      } else {
        flow.setCenter(
          it.state.x + it.state.width / 2,
          it.state.y + it.state.height / 2,
          { zoom: flow.getZoom(), duration: 280 },
        );
      }
      if (ev.detail?.keyboard === true) giveKeyboardTo(id);
      // Also select the focused item so subsequent keyboard ops
      // (Delete, Cmd+D duplicate) target it without an extra click.
      setSelectedNodeIds(new Set([id]));
      setSelectedEdgeIds(new Set());
    };
    window.addEventListener('ws:focus-item', onFocus);
    return () => window.removeEventListener('ws:focus-item', onFocus);
  }, [items, flow, screenEntriesNow, setCurrentScreen]);

  // ─── Deep-link camera op dispatched via URL_ACTIONS (`?pan=`) ─────
  // Pans at the current zoom. There were two more listeners here,
  // `ws:zoom` (in / out / reset / absolute) and `ws:fit` (fitView over
  // all / selection / one item), and together they were a public zoom
  // API: any script on the page, and the `?zoom=` / `?fit=` deep links,
  // could drive the camera to any level although workspace zoom is
  // disabled. Removed 2026-09-26 with those deep links; see
  // `tests/unit/workspaces/no-workspace-zoom-api.test.ts`.
  useEffect(() => {
    const onPan = (e: Event) => {
      const ev = e as CustomEvent<{ x: number; y: number }>;
      if (!ev.detail) return;
      flow.setCenter(ev.detail.x, ev.detail.y, {
        zoom: flow.getZoom(),
        duration: 280,
      });
    };
    window.addEventListener('ws:pan', onPan);
    return () => {
      window.removeEventListener('ws:pan', onPan);
    };
  }, [flow]);

  // ---- command registry + clipboard + shortcut layer --------------------
  //
  // The registry is the single source of truth for every right-click
  // menu row, floating toolbar button, and keyboard shortcut. Building
  // it once per mount keeps the same command identities across renders
  // so menu memos can short-circuit.
  const commands = useMemo(() => buildWorkspaceCommands(), []);
  const clipboard = useWorkspaceClipboard(selectionSnapshot);
  useWorkspaceShortcuts({ commands, clipboard, selectionOverride: selectionSnapshot });

  // Command context factory — bundles selection / store / flow /
  // clipboard / cursor into the shape the registry predicates +
  // runners expect. We call it lazily when we actually need to build
  // menu items (not on every render) so unrelated re-renders don't
  // re-evaluate predicates.
  const buildCtx = useCommandContext(clipboard, selectionSnapshot);

  // Dev helper: add `?wsDebug=clipboard` to the URL, then in the
  // browser console run `window.__wsClipboardDebug()` after Cmd+C.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!new URLSearchParams(window.location.search).has('wsDebug')) return;
    (
      window as Window & {
        __wsClipboardDebug?: () => Record<string, unknown>;
      }
    ).__wsClipboardDebug = () => ({
      selection: {
        nodeIds: [...selectionSnapshot.nodeIds],
        edgeIds: [...selectionSnapshot.edgeIds],
        count: selectionSnapshot.count,
      },
      clipboard: getWorkspaceClipboardDebugState(),
    });
  }, [selectionSnapshot]);

  // Build menu items for the per-item right-click menu. The registry
  // produces the cross-cutting rows (Duplicate, Copy, Cut, Bring to
  // front, …); per-type "extras" (Open profile, New chat, Share…) get
  // spliced in via the `bySection` option so the section dividers
  // resolve correctly.
  // Build a "Pick" submenu listing every positioned item whose AABB
  // contains the given flow-space point, ordered top-to-bottom by
  // zIndex. Discovery alternative to Alt-click pick-through — Figma
  // exposes the same gesture as the right-click "Select layer"
  // submenu. Returns null when fewer than 2 items overlap (a single
  // hit is just "select this item", and zero is already obvious
  // from the empty pane).
  //
  // Label design:
  //   - Numbered prefix (1, 2, 3…) signals the visual order — the
  //     top widget is "1", peeling down toward "N" at the bottom.
  //   - The widget's display name is the second token so the user
  //     scans for the one they want; fallbacks (title, text, type)
  //     prevent "(no label)" rows when a widget has no cache name.
  //   - "(top)" / "(bottom)" hints anchor the extremes so the user
  //     doesn't have to count to know which is in front.
  //   - Trailing widget type token (small grey) tells the user what
  //     kind of thing they're about to select without forcing them
  //     to memorise display names.
  const buildSelectUnderSubmenu = useCallback(
    (flowX: number, flowY: number): CanvasMenuItem | null => {
      const stack = stackUnder(items, { x: flowX, y: flowY });
      if (stack.length < 2) return null;
      const TYPE_LABEL: Record<string, string> = {
        agent: 'Agent',
        annotation: 'Note',
        content: 'Content',
        post: 'Post',
        chat: 'Chat',
        widget: 'Widget',
      };
      const rows: CanvasMenuItem[] = stack.map((id, idx) => {
        const it = items.find((x) => x.id === id);
        const cache = (it?.state.cache ?? {}) as Record<string, unknown>;
        // The item's name, live-first (item-label.ts), then a note's own text:
        // a sticky has no name, and its words are what identify it in a stack.
        const text = typeof cache.text === 'string' ? cache.text.trim() : '';
        const displayName =
          (it ? resolveItemLabel(it, liveContentMeta, '') : '') || text;
        const typeLabel = it ? TYPE_LABEL[it.type] ?? it.type : 'Item';
        const fallback = `Untitled ${typeLabel.toLowerCase()}`;
        const name = displayName ? displayName.trim().slice(0, 40) : fallback;
        const positionHint =
          idx === 0
            ? ' (top)'
            : idx === stack.length - 1
              ? ' (bottom)'
              : '';
        return {
          kind: 'action',
          id: `select-under-${id}`,
          label: `${idx + 1}. ${name} · ${typeLabel}${positionHint}`,
          onSelect: () => {
            const target = items.find((x) => x.id === id);
            if (!target) return;
            if (target.type === 'connector') {
              setSelectedEdgeIds(new Set([id]));
              setSelectedNodeIds(new Set());
            } else {
              setSelectedNodeIds(new Set([id]));
              setSelectedEdgeIds(new Set());
            }
          },
        };
      });
      return {
        kind: 'submenu',
        id: 'select-under',
        label: `Pick layer (${stack.length})`,
        items: rows,
      };
    },
    [items, liveContentMeta],
  );

  /**
   * Place a window inside a target screen rect: near its center, nudged
   * diagonally off occupied spots, clamped to the rect with a margin,
   * snapped to the grid. The camera stays put — this is the "Move to
   * screen" verb (Part IV), not a navigation.
   */
  const fileWindowIntoRect = useCallback(
    (
      nodeId: string,
      target: ScreenRect,
      /**
       * Rects to treat as occupied on top of what `items` says. Moving a
       * whole screen's worth of windows in one go patches them one at a
       * time, and `items` is a snapshot — without this, mover #2 would see
       * mover #1 at its OLD spot and land on top of it.
       */
      alsoOccupied?: ReadonlyArray<ScreenRect>,
    ): ScreenRect | null => {
      const it = items.find((x) => x.id === nodeId);
      if (!it) return null;
      const w = it.state.width;
      const h = it.state.height;
      let px = target.x + target.width / 2 - w / 2;
      let py = target.y + target.height / 2 - h / 2;
      const others = items
        .filter(
          (o) =>
            o.id !== nodeId &&
            o.type !== 'board' &&
            o.type !== 'connector' &&
            isOnCanvas(o.state),
        )
        .map((o) => o.state as ScreenRect)
        .concat(alsoOccupied ?? []);
      const overlaps = (X: number, Y: number) =>
        others.some(
          (o) =>
            X < o.x + o.width &&
            X + w > o.x &&
            Y < o.y + o.height &&
            Y + h > o.y,
        );
      let tries = 0;
      while (overlaps(px, py) && tries < 24) {
        px += GRID_MINOR;
        py += GRID_MINOR;
        tries += 1;
      }
      // Clamp inside the target — but only where there IS slack. A window
      // that fills its screen (apps open maximized now) has none, and the
      // clamp would then read `min(max(px, left+24), left-24)` and file it 24px
      // OUTSIDE the screen's left edge. Centre it instead.
      const fit = (
        p: number,
        edge: number,
        extent: number,
        size: number,
      ): number =>
        extent - size - GRID_MINOR * 2 <= 0
          ? edge + (extent - size) / 2
          : Math.min(Math.max(p, edge + GRID_MINOR), edge + extent - size - GRID_MINOR);
      px = fit(px, target.x, target.width, w);
      py = fit(py, target.y, target.height, h);
      const x = Math.round(px / GRID_MINOR) * GRID_MINOR;
      const y = Math.round(py / GRID_MINOR) * GRID_MINOR;
      patchItemState(nodeId, { x, y });
      return { x, y, width: w, height: h };
    },
    [items, patchItemState],
  );

  const nodeMenuItems = useMemo<CanvasMenuItem[]>(() => {
    if (!nodeMenu) return [];
    const item = items.find((it) => it.id === nodeMenu.itemId);
    if (!item) return [];
    const cache = item.state.cache ?? {};
    const ctx = buildCtx();
    const widgetExtras: CanvasMenuItem[] = [];
    if (item.type === 'agent') {
      const username = (cache as Record<string, unknown>).username as
        | string
        | undefined;
      if (username) {
        widgetExtras.push({
          kind: 'action',
          id: 'open-profile',
          label: 'Open profile',
          icon: 'open',
          onSelect: () => window.open(`/@${username}`, '_blank', 'noopener'),
        });
      }
      widgetExtras.push({
        kind: 'action',
        id: 'new-chat',
        label: 'New chat',
        icon: 'new-chat',
        onSelect: () =>
          patchItemState(item.id, { chatId: undefined, view: 'welcome' }),
      });
    } else if (item.type === 'content' && item.refId) {
      widgetExtras.push({
        kind: 'action',
        id: 'open-editor',
        label: 'Open in full editor',
        icon: 'open',
        onSelect: () =>
          window.open(`/content/${item.refId}`, '_blank', 'noopener'),
      });
      // An APP window's `refId` is the app's CATALOG row, not the work open in
      // it — a saved document lives in `state.projectFolderId`, and an unsaved
      // draft lives in a private canvas-only room with nothing to link to. So
      // this used to hand out `/content/<the app>` for every app window. Share
      // the document (at its `/w` app landing) or nothing at all; the node's
      // own Share action applies the same rule (see `shareableContentId`).
      const isAppWindow =
        ((cache as Record<string, unknown>).contentType as string | undefined) === 'widget';
      const projectFolderId = item.state.projectFolderId as string | undefined;
      const shareHref = isAppWindow
        ? projectFolderId
          ? `/w/${projectFolderId}`
          : null
        : `/content/${item.refId}`;
      if (shareHref) {
        widgetExtras.push({
          kind: 'action',
          id: 'share',
          label: 'Share…',
          icon: 'share',
          onSelect: () =>
            void shareLink({
              // Live-first (item-label.ts): a renamed document is shared
              // under the name it has now, not the one stored in the item.
              // An app window's link is its DOCUMENT, so the title is the
              // document's name too; titling it with the app's name sent a
              // "/w/<agenda>" link out as "Revent".
              title: isAppWindow
                ? resolveAppDocumentTitle(item, liveContentMeta, 'Content')
                : resolveItemLabel(item, liveContentMeta, 'Content'),
              url: `${window.location.origin}${shareHref}`,
            }),
        });
      }
    } else if (item.type === 'post' && item.refId) {
      widgetExtras.push({
        kind: 'action',
        id: 'open-post',
        label: 'Open post',
        icon: 'open',
        onSelect: () => window.open(`/p/${item.refId}`, '_blank', 'noopener'),
      });
      widgetExtras.push({
        kind: 'action',
        id: 'share',
        label: 'Share…',
        icon: 'share',
        onSelect: () =>
          void shareLink({
            title:
              ((cache as Record<string, unknown>).title as string) ?? 'Post',
            url: `${window.location.origin}/p/${item.refId}`,
          }),
      });
    }
    // "Move to screen" (Part IV): file a window onto any other screen
    // without touching the camera. Blank screens and split partners are
    // one row each — the same list the digits and dots address. A
    // temporary full-screen screen is not a place to move to (it holds one
    // app on this device only), so it has no row. Moving a screen's last
    // window away leaves that screen (the Stage algorithm, rules 9 to 11),
    // which the store settles after the move.
    if (isScreenType(item.type) && item.type !== 'board') {
      const entries = displayScreenEntries(items, desktopFullscreenIds);
      const own = entryOfItem(entries, item.id);
      const rows: CanvasMenuItem[] = entries
        .filter((e) => e !== own && !e.fullscreen)
        .map((e) => ({
          kind: 'action',
          id: `move-screen-${e.id}`,
          label: `${entries.indexOf(e) + 1}. ${resolveScreenEntryLabel(e, items, liveContentMeta)}`,
          onSelect: () => {
            const before = items;
            if (fileWindowIntoRect(item.id, e.rect)) settleAfterMove(before, [item.id]);
          },
        }));
      if (rows.length > 0) {
        widgetExtras.push({
          kind: 'submenu',
          id: 'move-to-screen',
          label: 'Move to screen',
          items: rows,
        });
      }
    }
    // "Select layer" submenu (Figma-style pick-through alternative).
    // Compute the flow-space hit point from the right-click viewport
    // coords so the stack matches what the user clicked, not the
    // selected item's centre.
    const flowPt = flow.screenToFlowPosition({ x: nodeMenu.x, y: nodeMenu.y });
    const selectUnder = buildSelectUnderSubmenu(flowPt.x, flowPt.y);
    const leading: CanvasMenuItem[] | undefined = selectUnder
      ? [selectUnder, { kind: 'separator' }]
      : undefined;

    const built = buildMenuItemsFromCommands(commands, ctx, 'selection', {
      leading,
      // Splice the per-widget extras after the `view` section (between
      // View and Dock). NOTE: bySection keys must name a section that
      // actually EXISTS in the registry output — a made-up key is
      // silently dropped (which is how these extras went missing when
      // they were keyed `widget`).
      bySection: widgetExtras.length > 0 ? { view: widgetExtras } : undefined,
    }).concat();
    if (item.type !== 'board') return built;
    // A blank screen is a PLACE, not an app: no dock, no clipboard
    // verbs, no window-title toggle. Rename lives on the whisper
    // (double-click); Delete and z-order stay.
    const APP_ONLY = new Set([
      'cut',
      'copy',
      'duplicate',
      'toggle-header',
      'pin-to-dock',
      'unpin-from-dock',
    ]);
    return built.filter(
      (row) => !('id' in row && row.id && APP_ONLY.has(row.id)),
    );
  }, [buildCtx, buildSelectUnderSubmenu, commands, desktopFullscreenIds, fileWindowIntoRect, flow, items, liveContentMeta, nodeMenu, patchItemState, settleAfterMove]);

  const paneMenuItems = useMemo<CanvasMenuItem[]>(() => {
    if (!paneMenu) return [];
    const ctx = buildCtx();
    const platform = ctx.platform;
    // The pane menu rolls its own list rather than using the
    // registry's `pane` section (currently empty) — the items
    // historically lived inline and we keep them here so existing
    // tests + muscle memory (Search Everything ⌘K, Paste as note,
    // Add widget…, Fit, Reset, Snap toggle) stay intact.
    const cmdKey = formatShortcut({ key: 'k', mod: true }, platform);
    const cmdPaste = formatShortcut({ key: 'v', mod: true }, platform);
    const selectUnder = buildSelectUnderSubmenu(paneMenu.flowX, paneMenu.flowY);
    return [
      ...(selectUnder ? ([selectUnder, { kind: 'separator' }] as CanvasMenuItem[]) : []),
      {
        kind: 'action',
        id: 'search',
        label: 'Search everything…',
        icon: 'search',
        shortcut: cmdKey,
        // Open the in-panel Search view (the search surface under railV2),
        // consistent with ⌘K and the rail Search button.
        onSelect: () => openWorkspacePanel('left', { tab: 'search' }),
      },
      {
        kind: 'action',
        id: 'paste',
        label: 'Paste',
        icon: 'paste',
        shortcut: cmdPaste,
        onSelect: () => {
          void clipboard
            .pasteFromClipboard({ x: paneMenu.flowX, y: paneMenu.flowY })
            .then((handled) => {
              if (!handled) {
                // Fall back to legacy paste-as-note pipeline (reads
                // the system clipboard text and uploads it as a
                // markdown content widget) when nothing matched
                // the internal MIME.
                void pasteAtPoint(paneMenu.flowX, paneMenu.flowY);
              }
            });
        },
      },
      { kind: 'separator' },
      // "Add app…" is gone: it opened the retired Apps panel. Browsing and
      // adding apps is Discover's job now (rail compass / ⌘⇧K), and the
      // "Search everything…" row above already covers add-from-here.
      {
        kind: 'action',
        id: 'add-note',
        // Annotation creation moved here from the (now-removed) rail Create menu.
        label: 'Add note',
        icon: 'note',
        onSelect: () =>
          void addItem({
            type: 'annotation',
            refId: null,
            state: {
              x: paneMenu.flowX,
              y: paneMenu.flowY,
              width: 192,
              height: 144,
              cache: { kind: 'sticky', text: '', color: 'yellow' },
            },
          }),
      },
      {
        kind: 'action',
        id: 'new-screen',
        // Part IV: a blank screen, one screenful, at the next free
        // cell. The word "board" left the product vocabulary.
        label: t('stage.newScreen'),
        icon: 'board',
        onSelect: () => createBoardAt(),
      },
      {
        kind: 'action',
        id: 'comment',
        label: 'Comment here',
        icon: 'comment',
        onSelect: () => requestNewThreadAt(paneMenu.flowX, paneMenu.flowY),
      },
      { kind: 'separator' },
      // Navigation for the screens era (Part IX): the freeform relics
      // (Fit to view, Reset zoom) are gone — the desktop is always at
      // 100% and the map IS the stage view.
      ...(() => {
        // This device's list: a full-screen window's own screen is a place
        // to go to (rule 17).
        const entries = displayScreenEntries(items, desktopFullscreenIds);
        const rows: CanvasMenuItem[] =
          entries.length > 1
            ? [
                {
                  kind: 'submenu',
                  id: 'go-to-screen',
                  label: 'Go to screen',
                  icon: 'board',
                  items: entries.map((en, i) => ({
                    kind: 'action',
                    id: `go-screen-${en.id}`,
                    label: `${i + 1}. ${resolveScreenEntryLabel(en, items, liveContentMeta)}`,
                    onSelect: () =>
                      window.dispatchEvent(
                        new CustomEvent('ws:screen', {
                          detail: { kind: 'item', itemId: en.id },
                        }),
                      ),
                  })),
                },
              ]
            : [];
        rows.push({
          kind: 'action',
          id: 'stage-view',
          label: 'Stage view',
          icon: 'fit',
          shortcut: formatShortcut(
            { key: 'e', mod: true, shift: true },
            platform,
          ),
          onSelect: () =>
            window.dispatchEvent(
              new CustomEvent('ws:screen', {
                detail: { kind: 'overview-toggle' },
              }),
            ),
        });
        return rows;
      })(),
      { kind: 'separator' },
      {
        kind: 'action',
        id: 'snap',
        label: snap ? 'Disable snap to grid' : 'Enable snap to grid',
        icon: 'snap',
        onSelect: () => setSnap((v) => !v),
      },
    ];
  }, [buildCtx, buildSelectUnderSubmenu, clipboard, createBoardAt, desktopFullscreenIds, items, liveContentMeta, paneMenu, pasteAtPoint, snap]);

  // "More" menu opened by the floating selection toolbar AND by the
  // per-kind toolbars (AnnotationToolbar / ConnectorEdgeToolbar) via
  // the `workspace:open-more-menu` event below. Sourced from the
  // registry so every command surface stays consistent.
  const [moreMenu, setMoreMenu] = useState<{
    x: number;
    y: number;
    items: CanvasMenuItem[];
  } | null>(null);
  const openMoreMenu = useCallback(
    (anchor: { x: number; y: number }, items: CanvasMenuItem[]) => {
      setMoreMenu({ x: anchor.x, y: anchor.y, items });
    },
    [],
  );

  // `workspace:open-more-menu` — fired by per-kind toolbars (sticky /
  // shape / connector) when their "..." button is pressed. We build
  // the menu items here from the LIVE selection so the menu always
  // matches the current state, regardless of what was selected when
  // the toolbar was painted.
  useEffect(() => {
    const onOpen = (e: Event) => {
      const ev = e as CustomEvent<{ x?: number; y?: number }>;
      const ctx = buildCtx();
      const items = buildMenuItemsFromCommands(commands, ctx, 'selection');
      const x = typeof ev.detail?.x === 'number' ? ev.detail.x : 0;
      const y = typeof ev.detail?.y === 'number' ? ev.detail.y : 0;
      openMoreMenu({ x, y }, items);
    };
    window.addEventListener('workspace:open-more-menu', onOpen);
    return () => window.removeEventListener('workspace:open-more-menu', onOpen);
  }, [buildCtx, commands, openMoreMenu]);

  // `workspace:duplicate-selection` — fired by per-kind toolbars'
  // Duplicate button so the toolbar doesn't have to thread the
  // registry's `duplicate` runner through props. The command lives
  // in `lib/workspace/commands.ts` and runs against the same context
  // the keyboard shortcut + menu use.
  useEffect(() => {
    const onDup = () => {
      const ctx = buildCtx();
      const cmd = commands.find((c) => c.id === 'duplicate');
      if (cmd && cmd.predicate(ctx)) void cmd.run(ctx);
    };
    window.addEventListener('workspace:duplicate-selection', onDup);
    return () =>
      window.removeEventListener('workspace:duplicate-selection', onDup);
  }, [buildCtx, commands]);

  // ---- map store items → React Flow nodes (skip docked) ----
  const fullscreenItemId = useFullscreenItemId();
  const pendingAnnotation = usePendingAnnotation();

  // Force-mount React Flow's `NodesSelection` group-drag overlay
  // whenever the user has >1 nodes selected — regardless of HOW they
  // got selected (marquee, Shift-click, Cmd-click, programmatic).
  //
  // RF only flips its internal `nodesSelectionActive` flag to true in
  // ONE place: the Pane's marquee `onPointerUp` (xyflow source ~1554).
  // Every other selection path — Shift-click, Cmd-click, addSelectedNodes
  // from a button, OUR controlled `selected: true` updates — leaves the
  // flag at false and the overlay never renders. That meant Shift-click
  // multi-select had no group-drag affordance: the user could see two
  // selection rings but had no way to grab them together.
  //
  // Worse, RF actively flips the flag BACK to false on essentially
  // every interaction afterwards: `handleNodeClick` unconditionally
  // sets `nodesSelectionActive: false` (xyflow ~1575) on every node
  // mousedown that goes through `selectNodesOnDrag`, the Pane's
  // `onClick` clears it (~1431), and the global delete handler too
  // (~1234). Without an active subscription we'd flip it true on
  // marquee end, then RF would immediately turn it off again the
  // moment the user clicked the bbox to start a group drag — the
  // overlay would unmount, the click would fall through to a node
  // (or the pane), and the gesture turned into a pan with the
  // selection visibly dropping.
  //
  // The fix is to OWN this flag from userland: subscribe to its
  // live store value so the effect re-runs whenever RF flips it,
  // and force it back to `true` whenever `selectedNodeIds.size > 1`.
  // Paired with the `z-index: 1001` CSS lift on
  // `.react-flow__nodesselection`, the overlay always sits above
  // selected widgets and intercepts the drag — every selected node
  // moves together no matter where inside the bbox the user clicks.
  const rfStore = useStoreApi();
  const rfNodesSelectionActive = useRfStore(
    (s: { nodesSelectionActive: boolean }) => s.nodesSelectionActive,
  );
  useEffect(() => {
    const shouldBeActive = selectedNodeIds.size > 1;
    if (rfNodesSelectionActive !== shouldBeActive) {
      rfStore.setState({ nodesSelectionActive: shouldBeActive });
    }
  }, [selectedNodeIds, rfNodesSelectionActive, rfStore]);

  // Stable-identity caches. Every `patchItemState` rebuilds `items`,
  // which means the nodes / edges memos below run on every frame of a
  // drag or resize gesture. Without caching we'd hand React Flow a
  // fresh array of fresh node objects each time — RF's internal diff
  // still runs but every child component sees new props (data, style)
  // and falls out of React.memo, which is the root cause of the
  // resize jitter + edge flicker the user sees. The refs keep the
  // SAME node object for items whose state-relevant fields didn't
  // change, so memoized renderers can short-circuit.
  const nodeCacheRef = useRef<Map<string, Node>>(new Map());
  const dataCacheRef = useRef<Map<string, { itemId: string }>>(new Map());
  const styleCacheRef = useRef<
    Map<
      string,
      {
        width: number;
        height: number;
        zIndex: number;
        pointerEvents?: 'none';
        visibility?: 'hidden';
      }
    >
  >(new Map());
  const positionCacheRef = useRef<Map<string, { x: number; y: number }>>(
    new Map(),
  );

  const nodes = useMemo<Node[]>(() => {
    const nextNodeCache = new Map<string, Node>();
    const nextDataCache = new Map<string, { itemId: string }>();
    const nextStyleCache = new Map<
      string,
      {
        width: number;
        height: number;
        zIndex: number;
        pointerEvents?: 'none';
        visibility?: 'hidden';
      }
    >();
    const nextPositionCache = new Map<string, { x: number; y: number }>();
    const nextClamped = new Set<string>();
    const arr: Node[] = [];
    // The phone app view: the presented window (when the usable box is
    // measured) is drawn at the usable size and every other node is hidden
    // with `visibility: hidden` and `pointer-events: none`, NEVER React
    // Flow's `hidden`, which unmounts the node and reloads its iframe (the
    // same reason `onlyRenderVisibleElements` stays off below). Null on a
    // desktop, so the loop below is unchanged there.
    const presented = presentedId && usableBoxState ? presentedId : null;
    // DESKTOP FULL SCREEN (lane D; the Stage algorithm, rules 12 to 14): a
    // window this device shows full screen is drawn at the CANVAS BOX from
    // its stored top-left, which `fullscreenCameraFor` puts on the canvas's
    // top-left, so it fills the canvas edge to edge with lane B's app bar
    // across its top and the app under it. Display only, like the phone's
    // presented window: the store keeps its size, the echo skips it, nothing
    // here writes. While its temporary screen is on view every other node is
    // hidden (`visibility`, never React Flow's `hidden`, which would unmount
    // an iframe); while it is not, the window itself is hidden, because it
    // lives on its own screen now, and it keeps the full-screen size so going
    // back does not reflow the app. In Stage it is drawn as it is stored, in
    // its temporary screen's card.
    const fullscreenBox = !fitFraming && !overviewMode && canvasBox ? canvasBox : null;
    const fullscreenIds = new Set(fitFraming ? [] : desktopFullscreenIds);
    const fullscreenShown =
      fullscreenBox && desktopFullscreenShown && fullscreenIds.has(desktopFullscreenShown)
        ? desktopFullscreenShown
        : null;
    for (const it of items) {
      // `docked` items live in the dock tray; `filed` items live only
      // in the Files widget / MY LIBRARY tree. Neither renders as a
      // free canvas tile. `getPlacement` honours legacy `docked` /
      // `keptInDock` rows.
      const placement = getPlacement(it.state);
      if (placement === 'docked' || placement === 'filed') continue;
      if (
        it.type !== 'agent' &&
        it.type !== 'content' &&
        it.type !== 'post' &&
        it.type !== 'terminal' &&
        it.type !== 'daemon' &&
        it.type !== 'ssh' &&
        it.type !== 'python' &&
        it.type !== 'files' &&
        it.type !== 'annotation' &&
        it.type !== 'board'
      ) {
        continue;
      }
      // Reuse the data / style / position sub-objects when the
      // underlying values match — keeps shallow-compare children
      // (e.g. memoized custom node renderers) from re-rendering.
      const prevData = dataCacheRef.current.get(it.id);
      const data =
        prevData && prevData.itemId === it.id ? prevData : { itemId: it.id };
      nextDataCache.set(it.id, data);

      const z = it.state.zIndex ?? 0;
      // Desktop full screen (see `fullscreenBox`): drawn at the canvas box.
      const fullscreenDrawn =
        fullscreenBox && it.type !== 'board' && fullscreenIds.has(it.id) ? fullscreenBox : null;
      // Handset clamp (display only): a window wider or taller than the
      // phone's visible canvas is DRAWN to fit, the store keeps its size.
      // Boards are the screen sheets and annotations are small; both keep
      // their size. The echo below skips clamped ids, and so the full-screen
      // size (lane D) rides the same record, `clamped` and all.
      const clamp = fullscreenDrawn
        ? { width: fullscreenDrawn.width, height: fullscreenDrawn.height, clamped: true }
        : clampNodeSizeForHandset({
            width: it.state.width,
            height: it.state.height,
            viewport: canvasBox,
            fit: fitFraming && it.type !== 'board' && it.type !== 'annotation',
          });
      // The presented window is drawn at the usable box, edge to edge (its
      // stored size is the laptop's and stays so); it joins the echo skip
      // set with the clamped ids. Everything else while presenting is
      // hidden behind it, whatever its z-order.
      const isPresented = it.id === presented;
      const hiddenBehind = !!presented && !isPresented;
      // Desktop full screen: everything behind the one on view, and a
      // full-screen window whose screen is not on view (it lives there).
      const fullscreenHidden =
        !overviewMode &&
        ((!!fullscreenShown && it.id !== fullscreenShown) ||
          (fullscreenIds.has(it.id) && it.id !== fullscreenShown));
      const hidden = hiddenBehind || fullscreenHidden;
      const nodeW = isPresented && usableBoxState ? usableBoxState.width : clamp.width;
      const nodeH = isPresented && usableBoxState ? usableBoxState.height : clamp.height;
      if (clamp.clamped || isPresented) nextClamped.add(it.id);
      const prevStyle = styleCacheRef.current.get(it.id);
      const style =
        prevStyle &&
        prevStyle.width === nodeW &&
        prevStyle.height === nodeH &&
        prevStyle.zIndex === z &&
        (prevStyle.visibility === 'hidden') === hidden
          ? prevStyle
          : hidden
            ? {
                width: nodeW,
                height: nodeH,
                zIndex: z,
                pointerEvents: 'none' as const,
                visibility: 'hidden' as const,
              }
          : it.type === 'board'
            ? // Boards are pointer-transparent sheets: the wrapper must
              // not be a pointer target so pane pan / marquee / clicks
              // inside the sheet behave exactly like open canvas. Only
              // the corner whisper re-enables pointer events locally.
              // (`.ws-navigate` on touch relies on the same trick.)
              {
                width: nodeW,
                height: nodeH,
                zIndex: z,
                pointerEvents: 'none' as const,
              }
            : { width: nodeW, height: nodeH, zIndex: z };
      nextStyleCache.set(it.id, style);

      // Overview spread (F6): while the overview is open, windows render
      // at their display-only spread positions; the store keeps the truth.
      // Overview lattice (Part VI): items render at their grid-cell
      // display positions. Items IN the current drag are exempt — RF
      // owns their live position and the drop remap converts back to
      // true coords (see onNodeDragStop).
      const beingDragged =
        itemDragActive && !!dragStartPosRef.current?.has(it.id);
      const spreadPos = beingDragged
        ? undefined
        : effectiveGrid?.display.get(it.id);
      const posX = spreadPos ? spreadPos.x : it.state.x;
      const posY = spreadPos ? spreadPos.y : it.state.y;
      const prevPos = positionCacheRef.current.get(it.id);
      const position =
        prevPos && prevPos.x === posX && prevPos.y === posY
          ? prevPos
          : { x: posX, y: posY };
      nextPositionCache.set(it.id, position);

      const isSelected = selectedNodeIds.has(it.id);
      // MODELESS touch model: the HEADER always drags (dragHandle scopes
      // RF's drag to `.ws-node-drag-handle`), the body always belongs to
      // the app, two-finger gestures navigate. The old pan-first gate
      // (draggable only while selected) predates default two-finger nav --
      // one-finger drags no longer need to fall through to pane panning.
      // Boards are PINNED (Part IV): a blank screen's backing sheet is
      // part of the lattice, never a movable object.
      // A presented handset window never drags (its bar is not a drag
      // handle, handoff section 4.2), and nothing hidden behind it does.
      const draggable: boolean | undefined =
        it.type === 'board' || presented ? false : isCoarse ? true : undefined;
      // Nothing drags while a full-screen screen is on view, and a
      // full-screen window never does (lane D: it is not a window there, its
      // app bar is not a handle).
      const nodeDraggable = fullscreenShown || fullscreenDrawn ? false : draggable;
      // Delegation pucks get a className that drives the smooth
      // spawn / exit animations defined in globals.css. We toggle
      // `ws-delegation-exit` via a transient `cache.delegationExiting`
      // flag — the hook patches it shortly before calling
      // `removeItem`, giving the CSS keyframe room to play.
      const itCache = (it.state.cache ?? {}) as Record<string, unknown>;
      let nodeClassName: string | undefined;
      if (itCache.relation === 'delegation') {
        nodeClassName = itCache.delegationExiting
          ? 'ws-delegation-puck ws-delegation-exit'
          : 'ws-delegation-puck';
      }
      // Drive the brief post-spawn pulse: any item whose cache carries
      // a fresh `justSpawnedAt` (set by `addItem` and auto-cleared 2s
      // later) gets `ws-just-spawned`. Stale timestamps are ignored so
      // a re-render after the marker was supposed to clear can't
      // resurrect the animation.
      //
      // The age is bounded on BOTH sides. The marker is stamped with the
      // WRITER's clock, so a peer whose clock runs behind computes a
      // negative age, which a bare `< 2200` reads as "fresh" — every item
      // on the canvas would pulse, forever. Screen sheets never pulse at
      // all: a sheet is scenery for the window that minted it.
      const spawnedAt = itCache.justSpawnedAt;
      const spawnAge =
        typeof spawnedAt === 'number' ? Date.now() - spawnedAt : Number.NaN;
      if (it.type !== 'board' && spawnAge >= 0 && spawnAge < 2200) {
        nodeClassName = nodeClassName
          ? `${nodeClassName} ws-just-spawned`
          : 'ws-just-spawned';
      }
      // The phone app view's hooks for CSS and tests: the one presented
      // window, and the nodes hidden behind it.
      if (isPresented) {
        nodeClassName = nodeClassName ? `${nodeClassName} ws-handset-app` : 'ws-handset-app';
      } else if (hiddenBehind) {
        nodeClassName = nodeClassName ? `${nodeClassName} ws-handset-hidden` : 'ws-handset-hidden';
      }
      // Desktop full screen's hooks for CSS and tests: the full-screen window
      // (no resize edges, globals.css) and the nodes hidden while one is shown.
      if (fullscreenDrawn) {
        nodeClassName = nodeClassName ? `${nodeClassName} ws-desktop-fs-app` : 'ws-desktop-fs-app';
      } else if (fullscreenHidden) {
        nodeClassName = nodeClassName ? `${nodeClassName} ws-desktop-fs-hidden` : 'ws-desktop-fs-hidden';
      }
      const prevNode = nodeCacheRef.current.get(it.id);
      const sameRefs =
        prevNode &&
        prevNode.type === it.type &&
        prevNode.data === data &&
        prevNode.style === style &&
        prevNode.position === position &&
        prevNode.width === nodeW &&
        prevNode.height === nodeH &&
        prevNode.className === nodeClassName &&
        // Same reasoning as `selected` below: `draggable` must be in the
        // cache key so flipping it (on selection change, or when the
        // pointer type switches coarse↔fine) rebuilds the Node object
        // instead of handing RF a stale `draggable` flag.
        prevNode.draggable === nodeDraggable &&
        // `selected` MUST be in the cache key — otherwise toggling a
        // node's selection produces the same memoized Node object,
        // RF's reconciler sees `userNode === internalNode.userNode`
        // and reuses the stale internalNode (with the old `selected`
        // flag), and the selection ring / toolbar never appears.
        !!prevNode.selected === isSelected;
      const node: Node = sameRefs
        ? (prevNode as Node)
        : {
            id: it.id,
            type: it.type,
            position,
            data,
            width: nodeW,
            height: nodeH,
            // Seed RF's internals directly: `adoptUserNodes` fills
            // `measured` ONLY from this field (never from width/height),
            // and otherwise waits for its ResizeObserver. Safari delivers
            // that measurement late enough that every drag frame hit the
            // "node not initialized" guard (error 015) and re-measure
            // churn — the red console on window drags in Safari. Our
            // store IS the source of truth for dimensions, so hand them
            // over; this is the same contract RF documents for SSR.
            measured: { width: nodeW, height: nodeH },
            style,
            selected: isSelected,
            draggable: nodeDraggable,
            className: nodeClassName,
            // Annotations are their own drag surface; widgets grab via
            // the header strip — both share `.ws-node-drag-handle`.
            // Group drag for marquee / Shift-click multi-selections is
            // owned by React Flow's `NodesSelection` overlay (CSS
            // `z-index: 1001` lifts it above selected nodes; see the
            // `nodesSelectionActive` useEffect above for why we keep
            // it active for any >1 selection regardless of origin).
            dragHandle: '.ws-node-drag-handle',
          };
      nextNodeCache.set(it.id, node);
      arr.push(node);
    }
    nodeCacheRef.current = nextNodeCache;
    dataCacheRef.current = nextDataCache;
    styleCacheRef.current = nextStyleCache;
    positionCacheRef.current = nextPositionCache;
    handsetClampedRef.current = nextClamped;
    return arr;
  }, [items, selectedNodeIds, isCoarse, effectiveGrid, itemDragActive, fitFraming, canvasBox, presentedId, usableBoxState, overviewMode, desktopFullscreenIds, desktopFullscreenShown]);

  // ---- derive edges from store ----
  // Two flavors:
  //   1. Provenance edges from `state.parentItemId` (quiet line, no head)
  //   2. Connector items (`type === 'connector'`) projected via
  //      `state.cache.from` / `.to` — these are the user-drawn arrows
  //      and re-use a single custom edge component.
  // Same identity-cache trick as nodes — see comment above. Connector
  // and provenance edges only change when their endpoint ids, handle
  // routing, or cached label/arrow/stroke change; the per-frame mid-
  // resize re-render now reuses every unchanged edge object so React
  // Flow's edge memo can short-circuit.
  const edgeCacheRef = useRef<Map<string, Edge>>(new Map());
  const edgeDataCacheRef = useRef<Map<string, { itemId: string }>>(new Map());

  const edges = useMemo<Edge[]>(() => {
    const visibleIds = new Set(
      items.filter((it) => !it.state.docked).map((it) => it.id),
    );
    const nextEdgeCache = new Map<string, Edge>();
    const nextEdgeDataCache = new Map<string, { itemId: string }>();
    const arr: Edge[] = [];
    // Older connectors persisted handle ids in the long-form
    // `<cardinal>-source` / `<cardinal>-target` shape that the early
    // NodeFrame draft emitted. The handles now use single-letter ids
    // (`t` / `r` / `b` / `l`) — see `node-frame.tsx` + `annotation-node.tsx`.
    // Strip the legacy suffix here so existing edges keep snapping to
    // their original cardinal slot instead of failing to bind and
    // emitting React Flow's "Couldn't create edge for source handle"
    // warning in the console.
    const normalizeHandleId = (id: string | null): string | null => {
      if (!id) return null;
      const m = /^([tblr])-(source|target)$/i.exec(id);
      return m ? m[1].toLowerCase() : id;
    };
    for (const it of items) {
      if (it.type === 'connector') {
        const from = (it.state.cache?.from as string | undefined) ?? null;
        const to = (it.state.cache?.to as string | undefined) ?? null;
        const fromHandle = normalizeHandleId(
          (it.state.cache?.fromHandle as string | undefined) ?? null,
        );
        const toHandle = normalizeHandleId(
          (it.state.cache?.toHandle as string | undefined) ?? null,
        );
        if (!from || !to) continue;
        if (!visibleIds.has(from) || !visibleIds.has(to)) continue;
        const prevData = edgeDataCacheRef.current.get(it.id);
        const data =
          prevData && prevData.itemId === it.id ? prevData : { itemId: it.id };
        nextEdgeDataCache.set(it.id, data);
        const isSelected = selectedEdgeIds.has(it.id);
        // Apply the persisted `zIndex` to the edge so connector
        // layering follows the same ordering commands (Bring to
        // front, Send to back, …) the node-class items use. RF
        // honours `Edge.zIndex` since v12.4 — the value is fed into
        // the per-edge `<g>` order. Connectors without an explicit
        // value default to 0, which is below any non-docked node
        // that has spawned (those land at `maxZ + 1`) — matches
        // the legacy implicit ordering.
        const z = it.state.zIndex ?? 0;
        const prevEdge = edgeCacheRef.current.get(it.id);
        const sameRefs =
          prevEdge &&
          prevEdge.source === from &&
          prevEdge.target === to &&
          prevEdge.sourceHandle === fromHandle &&
          prevEdge.targetHandle === toHandle &&
          prevEdge.data === data &&
          prevEdge.zIndex === z &&
          // Selection MUST be in the cache key. RF v12 does NOT
          // mutate `internalEdge.selected` on click (look at
          // `addSelectedEdges` — only `triggerEdgeChanges` is
          // called, no `mutateItem: true`). So the only path that
          // makes an edge visually selected is the `selected`
          // field on the user-supplied Edge propagating through.
          // Skipping this check leaves edge selection invisible
          // forever.
          !!prevEdge.selected === isSelected;
        const edge: Edge = sameRefs
          ? (prevEdge as Edge)
          : {
              id: it.id,
              source: from,
              target: to,
              sourceHandle: fromHandle,
              targetHandle: toHandle,
              type: 'connector',
              data,
              selected: isSelected,
              zIndex: z,
              // Selectability lets right-click on the edge surface up
              // through `onEdgeContextMenu` so the per-connector menu
              // (Phase 4) can fire. Visual chrome is owned by the custom
              // edge component, not React Flow's defaults.
              selectable: true,
              // Allow re-anchoring either endpoint by dragging it to
              // a different node handle. RF mounts an invisible
              // `reconnectRadius`-sized hot zone at each end while
              // selected; the `onReconnect` handler below catches
              // the resulting Connection and patches the connector
              // item's `cache.from / to / fromHandle / toHandle`.
              reconnectable: true,
            };
        nextEdgeCache.set(it.id, edge);
        arr.push(edge);
        continue;
      }
      const parentId = it.state.parentItemId;
      if (!parentId) continue;
      if (!visibleIds.has(it.id) || !visibleIds.has(parentId)) continue;
      const parent = items.find((p) => p.id === parentId);
      if (!parent) continue;
      // Pick the cardinal handle pair that points along the dominant
      // axis between the two widgets. Without this React Flow defaults
      // to `bottom→top` even when the child is plainly off to the
      // right of the parent — producing the "U-shape" provenance edge
      // people kept complaining about for `plt.show()` figures
      // spawned via `spawnChild({ placement: 'right' })`.
      const pCx = parent.state.x + parent.state.width / 2;
      const pCy = parent.state.y + parent.state.height / 2;
      const cCx = it.state.x + it.state.width / 2;
      const cCy = it.state.y + it.state.height / 2;
      const { sourceHandle, targetHandle } = pickProvenanceHandles(
        cCx - pCx,
        cCy - pCy,
      );
      const provId = `${parentId}->${it.id}`;
      const prevProv = edgeCacheRef.current.get(provId);
      // Delegation children carry `cache.relation === 'delegation'`
      // (set by `useDelegationVisualization` on spawn). Their edge gets
      // the animated DelegationEdge type and a status field that drives
      // the in-progress / complete / error visuals. When the status
      // flips on the child (via `patchItemState`), the cached edge
      // object would still hold the prior status — so we re-build any
      // delegation edge whose cached `data.status` no longer matches
      // the live cache value.
      const childCache = (it.state.cache ?? {}) as Record<string, unknown>;
      const isDelegation = childCache.relation === 'delegation';
      const delegationStatus =
        (childCache.delegationStatus as
          | 'in_progress'
          | 'complete'
          | 'error'
          | undefined) ?? 'in_progress';
      const cachedStatusMatches =
        !isDelegation ||
        ((prevProv?.data as { status?: string } | undefined)?.status ===
          delegationStatus);
      const sameHandles =
        prevProv &&
        prevProv.sourceHandle === sourceHandle &&
        prevProv.targetHandle === targetHandle &&
        cachedStatusMatches;
      const edge: Edge = sameHandles
        ? (prevProv as Edge)
        : isDelegation
          ? {
              id: provId,
              source: parentId,
              target: it.id,
              sourceHandle,
              targetHandle,
              type: 'delegation',
              animated: false,
              data: { status: delegationStatus },
              // Stroke width + colour are owned by DelegationEdge so
              // status changes don't require a parent-side re-style.
            }
          : {
              id: provId,
              source: parentId,
              target: it.id,
              sourceHandle,
              targetHandle,
              type: 'default',
              animated: false,
              style: { strokeWidth: 1.25, opacity: 0.55 },
              // No markerEnd; the connection should feel like a quiet provenance line.
            };
      nextEdgeCache.set(provId, edge);
      arr.push(edge);
    }
    edgeCacheRef.current = nextEdgeCache;
    edgeDataCacheRef.current = nextEdgeDataCache;
    return arr;
  }, [items, selectedEdgeIds]);

  // ---- viewport persistence ----
  useEffect(() => {
    // Screens v2: land where THIS user last was — their saved screen,
    // framed — rather than wherever the shared camera row happens to
    // point (collaborators used to inherit each other's last position).
    // One deferred tick so React Flow has registered the nodes first.
    // rAF is PAUSED in hidden tabs (background load, occluded window),
    // so fall back to a macrotask there — same reason animateViewport
    // snaps instead of gliding while hidden.
    const defer = (fn: () => void): (() => void) => {
      if (typeof document !== 'undefined' && document.hidden) {
        const t = setTimeout(fn, 16);
        return () => clearTimeout(t);
      }
      const raf = requestAnimationFrame(fn);
      return () => cancelAnimationFrame(raf);
    };
    // The arrival camera YIELDS to a window that was asked for in the
    // meantime (`focusAskedRef`, above the `ws:focus-item` listener): a link
    // that raises an app does so before this deferred frame runs, and "where
    // you last were" must not take the view back from it. The asked window
    // is framed here instead, with no glide, like every arrival. `?item=`
    // below is itself such a request and keeps the plain `defer`.
    const deferArrival = (fn: () => void): (() => void) =>
      defer(() => {
        const askedId = focusAskedRef.current;
        if (askedId) frameScreen(askedId, { duration: 0 });
        else fn();
      });
    // "Add to workspace" sends `?item=<id>`: FRAME what it just added, ahead
    // of everything below. Without this the arrival camera answers a
    // different question — your last saved screen, or for a workspace never
    // framed before (every share-link recipient who just signed up) a
    // `fitView` over EVERY item. Measured 2026-09-21: the first add landed at
    // zoom 0.81 with the new window sitting in empty canvas, and the second
    // landed on the PREVIOUS app with the thing just added off-screen at
    // x 4966. Read from `location` rather than `useSearchParams` so the
    // canvas does not drag a Suspense boundary in for one query string.
    const askedId =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('item')
        : null;
    const asked = askedId
      ? items.find(
          (it) =>
            it.id === askedId && isScreenType(it.type) && isOnCanvas(it.state),
        )
      : undefined;
    if (asked) {
      return defer(() => frameScreen(asked.id, { duration: 0 }));
    }
    const savedId = loadCurrentScreen(workspace.id);
    const saved = savedId
      ? items.find(
          (it) =>
            it.id === savedId && isScreenType(it.type) && isOnCanvas(it.state),
        )
      : undefined;
    if (saved) {
      return deferArrival(() => frameScreen(saved.id, { duration: 0 }));
    }
    // A workspace that has never been framed (cameraX/Y = 0, zoom = 1) — e.g. a
    // freshly cloned app/guest workspace — would otherwise load at the canvas
    // ORIGIN, leaving its content off-screen to the side. Center on the content
    // instead.
    const isDefaultCamera =
      workspace.cameraX === 0 && workspace.cameraY === 0 && workspace.cameraZoom === 1;
    if (isDefaultCamera && items.length > 0) {
      // Frame the first screen at 100%, like every arrival above. This was a
      // `fitView` over EVERY item, which lands at whatever zoom fits them all
      // (0.81 measured on 2026-09-21; up to the 3x ceiling for one small item)
      // although workspace zoom is disabled. A workspace with no screens (notes
      // and shapes only) is centred on its content, still at 100%.
      const first = orderScreenEntries(items).find((e) => {
        const it = items.find((x) => x.id === e.id);
        return !!it && isOnCanvas(it.state);
      });
      if (first) return deferArrival(() => frameScreen(first.id, { duration: 0 }));
      return deferArrival(() =>
        flow.fitView({ padding: 0.2, duration: 0, minZoom: 1, maxZoom: 1 }),
      );
    }
    if (workspace.cameraZoom > 0) {
      // Restore where the camera was, never its zoom. The camera row is
      // writable through `PATCH /api/workspaces/<id>` (`cameraZoom` 0.05 to 8)
      // and also records wherever the Stage view or a touch pinch last left
      // it, so restoring it verbatim made the API a zoom control and could
      // load a device into a Stage-scale camera outside the Stage. Keep the
      // same centre point and land at 100% (identical when it was already 1).
      const z = workspace.cameraZoom;
      const box = wrapperRef.current?.getBoundingClientRect();
      let x = workspace.cameraX;
      let y = workspace.cameraY;
      if (z !== 1 && box && box.width > 0 && box.height > 0) {
        x = box.width / 2 - (box.width / 2 - x) / z;
        y = box.height / 2 - (box.height / 2 - y) / z;
      }
      flow.setViewport({ x, y, zoom: 1 }, { duration: 0 });
      // One-shot rescue: a fallback camera restored mid-window (the old
      // trap state — app body filling the view, header off-screen) glides
      // onto that screen so no one ever loads INTO the trap.
      return deferArrival(() => maybeSettle(220));
    }
    // Only run once on mount; thereafter store-driven motion is initiated
    // by the user, not reflected back from the workspace prop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncViewportToStore = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const tl = flow.screenToFlowPosition({ x: rect.left, y: rect.top });
    const br = flow.screenToFlowPosition({ x: rect.right, y: rect.bottom });
    setViewportAnchor({
      x: tl.x + (br.x - tl.x) / 2,
      y: tl.y + (br.y - tl.y) / 2,
    });
    setViewportRect({
      x: tl.x,
      y: tl.y,
      width: br.x - tl.x,
      height: br.y - tl.y,
    });
    // Your view of the screen you are on, which the next window opens
    // against (the Stage algorithm, rules 2 and 3; the store's `screenView`):
    // the camera's world rect, published only while it shows a normal screen
    // on a desktop. Stage and a full-screen screen leave the last one in
    // place, so an open from there lands on the screen you were on, or on the
    // one the full-screen app came from. A handset publishes none: a phone
    // places for the 1440 x 900 reference screen (rule 18).
    if (fitFramingRef.current) {
      setScreenView(null);
    } else if (!overviewRef.current && !desktopFullscreenShownRef.current) {
      setScreenView({ x: tl.x, y: tl.y, width: br.x - tl.x, height: br.y - tl.y });
    }
    // …and the same box in SCREEN pixels, which is what spawn sizing uses:
    // it must not change when the user zooms.
    // Subtract the mobile bottom bar: it OVERLAYS the canvas (z-96), so the
    // wrapper's rect includes pixels the user can't see apps in — a
    // "full-height" phone open otherwise extends underneath the bar.
    const barPx = mobileBarPx();
    // The store's SIZING viewport. A window opened on a handset is sized for
    // a reference laptop screenful, not for the phone's box (decision 3 of
    // the handoff, 2026-09-29): the size is shared, so the phone's 432x819
    // would otherwise become the laptop's size too, and the phone presents
    // the window full-tab whatever its stored size. A desktop keeps its own
    // measured box, as before.
    setViewportBox(
      fitFramingRef.current
        ? { width: REFERENCE_SIZING_BOX.width, height: REFERENCE_SIZING_BOX.height }
        : {
            width: rect.width,
            height: Math.max(120, rect.height - barPx),
          },
    );
    const boxW = rect.width;
    const boxH = Math.max(120, rect.height - barPx);
    setCanvasBox((prev) =>
      prev && prev.width === boxW && prev.height === boxH ? prev : { width: boxW, height: boxH },
    );
    // The usable box for the phone app view: the wrapper minus the MEASURED
    // navigation and the safe-area insets, both read, never assumed (handoff
    // section 4.7). Null on a desktop. The ref serves the camera callbacks
    // synchronously; the state re-runs the node builder.
    const usable = fitFramingRef.current
      ? usableBox({
          wrapper: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
          page: { width: window.innerWidth, height: window.innerHeight },
          barPx,
          insets: readSafeAreaInsets(),
        })
      : null;
    usableBoxRef.current = usable;
    setUsableBoxState((prev) =>
      (prev === null && usable === null) ||
      (prev && usable && prev.x === usable.x && prev.y === usable.y && prev.width === usable.width && prev.height === usable.height)
        ? prev
        : usable,
    );
    const mz = Math.max(0.08, Math.min(1, (rect.width - 32) / SCREEN_CELL.width));
    dynMinZoomRef.current = mz;
    setDynMinZoom(mz);
  }, [flow, setViewportAnchor, setViewportRect, setViewportBox, setScreenView]);

  // The usable box depends on two things a wrapper resize does not report:
  // the handset test flipping (crossing md, a rotation) and the navigation's
  // measured height changing without a resize (in-tab full screen hides it;
  // the inset arrives after the first paint). Re-measure on both.
  useEffect(() => {
    syncViewportToStore();
  }, [fitFraming, syncViewportToStore]);
  useEffect(() => {
    const onBar = () => syncViewportToStore();
    window.addEventListener(BAR_MEASURED_EVENT, onBar);
    return () => window.removeEventListener(BAR_MEASURED_EVENT, onBar);
  }, [syncViewportToStore]);

  // Initialise the viewport anchor + rect on mount so the very first
  // spawn (e.g. an agent welcome-prompt sent before the user has even
  // panned) has a valid placement target. Without this, the first
  // `addItem` call falls through to the canvas origin because
  // `viewportAnchor` is still `null`. Re-runs whenever the wrapper
  // resizes (window resize, panel open/close) so `viewportRect`
  // tracks the actual visible area.
  useEffect(() => {
    syncViewportToStore();
    const el = wrapperRef.current;
    if (!el) return;
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => syncViewportToStore());
    ro.observe(el);
    return () => ro.disconnect();
  }, [syncViewportToStore]);

  // ---- screens: framing, settle, overview (window-management v2) --------
  //
  // The camera is screen-centric now: it rests framed on one app window
  // ("screen"), a released pan settles onto the nearest screen, and the
  // overview pulls the camera back to see every screen at once. Screens
  // are DERIVED from the items (no schema change) — see
  // docs/internal/architecture/workspace-screens.md and
  // lib/workspaces/screens.ts for the pure math.
  const currentScreenId = useCurrentScreenId();
  const prevScreenId = usePrevScreenId();
  // Refs so the stable listeners/RF callbacks read live values without
  // re-binding on every store tick (same pattern as the app switcher).
  const screensItemsRef = useRef(items);
  screensItemsRef.current = items;
  // Same for the live names the edge-swipe hint reads when a touch arms it.
  const liveContentMetaRef = useRef(liveContentMeta);
  liveContentMetaRef.current = liveContentMeta;

  const overviewRef = useRef(overviewMode);
  overviewRef.current = overviewMode;
  const currentScreenRef = useRef(currentScreenId);
  currentScreenRef.current = currentScreenId;
  const prevScreenRef = useRef(prevScreenId);
  prevScreenRef.current = prevScreenId;
  const fsRef = useRef(fullscreenItemId);
  fsRef.current = fullscreenItemId;
  // Guard: >0 while a programmatic camera animation is in flight, so the
  // onMoveEnd it fires never triggers a second settle (no oscillation).
  const screenGuardRef = useRef(0);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Draw-tool mirror for the pager (the pane pointer gesture must yield
  // to an armed annotation/board tool).
  const pendingToolRef = useRef(pendingAnnotation);
  pendingToolRef.current = pendingAnnotation;

  // ---- Navigation lock: TOTAL (Part X) ---------------------------------
  //
  // The desktop state never drifts, full stop: pane wheels and pane
  // drags move nothing at all (the rubber-band paging gestures are
  // retired — with several apps on one screen the exposed pane made
  // ordinary scrolls wobble the camera or flip screens). The camera
  // only ever moves programmatically: chords, dots, edge gestures, the
  // menu, and the stage view.

  // Edge push (Part IV S3): while a WINDOW drag dwells at a side edge,
  // a timer pages the camera to the neighbor screen underneath the
  // drag (the macOS Spaces move); the top edge opens the overview.
  const edgeDwellRef = useRef<{
    zone: 'left' | 'right' | 'top';
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);
  const [edgeDwell, setEdgeDwell] = useState<{
    zone: 'left' | 'right' | 'top';
    y: number;
  } | null>(null);
  // OVERDRAG progress (0..1) toward the stage view — see the gesture in
  // `onNodeDrag`. Ref is drag-time truth; state drives the hint.
  const overdragRef = useRef(0);
  const [overdrag, setOverdrag] = useState(0);
  const publishOverdrag = useCallback((raw: number) => {
    const next = Math.max(0, Math.min(1, raw));
    // Quantize the middle of the range: the hint is a 3px line, and a
    // setState per pointer pixel is pure churn mid-drag. The endpoints
    // always publish so the hint appears and completes exactly.
    if (
      next !== 0 &&
      next !== 1 &&
      Math.abs(overdragRef.current - next) < 0.02
    ) {
      return;
    }
    if (overdragRef.current === next) return;
    overdragRef.current = next;
    setOverdrag(next);
  }, []);
  const clearDwellTimer = useCallback(() => {
    if (edgeDwellRef.current) {
      clearTimeout(edgeDwellRef.current.timer);
      edgeDwellRef.current = null;
    }
  }, []);
  const clearEdgeDwell = useCallback(() => {
    clearDwellTimer();
    setEdgeDwell(null);
    publishOverdrag(0);
  }, [clearDwellTimer, publishOverdrag]);

  // Edge tiling (Part V): entering a side zone mid-drag immediately
  // previews the half-screen the window would tile into — drop BEFORE
  // the dwell fires and it snaps there (Windows-style); keep holding
  // and the dwell pages to the neighbor instead (the carry). Ref +
  // state pair: the ref is what onNodeDragStop trusts at event time.
  const snapPreviewRef = useRef<'left' | 'right' | null>(null);
  const [snapPreview, setSnapPreview] = useState<'left' | 'right' | null>(null);
  const publishSnapPreview = useCallback((side: 'left' | 'right' | null) => {
    if (snapPreviewRef.current === side) return;
    snapPreviewRef.current = side;
    setSnapPreview(side);
  }, []);

  // Overview "New screen" tile as a drop target (Part V): true while a
  // window drag hovers the tile slot.
  const tileDropRef = useRef(false);
  const [tileDropActive, setTileDropActive] = useState(false);
  const publishTileDrop = useCallback((active: boolean) => {
    if (tileDropRef.current === active) return;
    tileDropRef.current = active;
    setTileDropActive(active);
  }, []);

  // Edge gestures while dragging a window (Part VII): the SIDE bands
  // mean exactly one thing — tile into that half (preview on entry,
  // snap on drop; they never switch screens).
  //
  // The TOP is an OVERDRAG (user call): parking a window at the top of
  // the screen is an ordinary move, and a top BAND — on any dwell, however
  // short — stole it, opening the stage when all you wanted was the top
  // slot. So the stage now costs a push PAST the canvas: only once the
  // pointer leaves the top edge does a hairline appear and fill, and only
  // crossing OVERDRAG_COMMIT_PX opens the stage. Everything short of that
  // leaves the drag untouched, so the top position is free again.
  // Thresholds + the pure decision live in `lib/workspaces/overdrag.ts`
  // (testable without a live canvas — React Flow's drag needs real pointer
  // capture, so the gesture itself can only be exercised by hand).
  const EDGE_PUSH_PX = 28;

  /**
   * Remember the camera as the parked screen's own (fix round 2; see
   * `cameraMemoryRef`), when it rests there and still shows it: never mid-
   * glide, never on a handset or a full-screen screen, and never a camera
   * that does not show the screen (Stage's zoom, a window dragged out of
   * view), which `rememberScreenCamera` refuses on its own. Called as a move
   * starts (the camera the person rested on, before it leaves) and once the
   * move is over (the camera they arrived at).
   */
  const rememberCameraNow = useCallback(() => {
    if (fitFramingRef.current || desktopFullscreenShownRef.current) return;
    if (screenGuardRef.current > 0) return;
    const memory = cameraMemoryRef.current;
    const parked = currentScreenRef.current;
    const el = wrapperRef.current;
    if (!memory || !parked || !el) return;
    const entry = entryOfItem(orderScreenEntries(screensItemsRef.current), parked);
    if (!entry) return;
    const b = el.getBoundingClientRect();
    if (b.width <= 0 || b.height <= 0) return;
    const camera = flow.getViewport();
    const was = memory.get(entry.id);
    if (!rememberScreenCamera(memory, entry, screensItemsRef.current, camera, { width: b.width, height: b.height })) return;
    if (!was || !viewportsClose(was, camera)) saveScreenCameras(workspace.id, memory);
  }, [flow, workspace.id]);

  const animateViewport = useCallback(
    (vp: ViewportTransform, duration: number) => {
      // Hidden tab: rAF is paused, so a d3 glide would FREEZE mid-
      // flight and strand the camera between screens (broken zoom)
      // when the user returns. Nobody sees an animation in a hidden
      // tab — snap instantly instead.
      const d =
        typeof document !== 'undefined' && document.hidden ? 0 : duration;
      // The camera the person rested on belongs to the screen being left.
      rememberCameraNow();
      screenGuardRef.current += 1;
      void flow.setViewport(vp, { duration: d });
      window.setTimeout(() => {
        screenGuardRef.current = Math.max(0, screenGuardRef.current - 1);
        // Arrived, with nothing else moving: the screen now parked keeps it.
        rememberCameraNow();
      }, d + 120);
    },
    [flow, rememberCameraNow],
  );

  // EDGE PEEK-SWIPE = SCREEN SWITCHING on touch. The gesture ARMS in a
  // narrow strip inside either canvas edge; while it is live the camera
  // LERPS ALONG THE REAL PATH to the neighbor's frame - screens are
  // ordered x->y but placed freely in 2D, so a plain horizontal translate
  // revealed VOID whenever the neighbor sat above or below (the
  // "left-to-right feels buggy" report). A subtle zoom dip (parabolic,
  // peaks mid-gesture) makes the grab legible from the first pixels, and
  // the neighbor's name chip rides the pulled edge (peekHint). Release
  // past 35% of the path (or a flick) commits via ws:screen; anything
  // else springs back and settles.
  const EDGE_ZONE = 28;
  const swipeRef = useRef<{
    x: number;
    y: number;
    t: number;
    lx: number;
    ly: number;
    vp: ViewportTransform;
    nVp: ViewportTransform | null;
    dir: -1 | 1;
    active: boolean;
  } | null>(null);
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || !isCoarse || !railV2) return;
    const progressOf = (dx: number, s: NonNullable<typeof swipeRef.current>, w: number) => {
      const towardNeighbor = s.dir === -1 ? dx < 0 : dx > 0;
      if (!s.nVp || !towardNeighbor) return 0;
      return Math.max(0, Math.min(1, Math.abs(dx) / (w * 0.55)));
    };
    const apply = (dx: number, s: NonNullable<typeof swipeRef.current>, w: number, h: number) => {
      const t = progressOf(dx, s, w);
      if (s.nVp && t > 0) {
        // Straight-line camera slide toward the neighbor's frame, with a
        // parabolic zoom dip (max ~8% at mid-path) zoomed about the
        // viewport center so the dip reads as "holding the pager".
        const bx = s.vp.x + (s.nVp.x - s.vp.x) * t;
        const by = s.vp.y + (s.nVp.y - s.vp.y) * t;
        const bz = s.vp.zoom + (s.nVp.zoom - s.vp.zoom) * t;
        const dip = 1 - 0.08 * (4 * t * (1 - t));
        const cx = w / 2;
        const cy = h / 2;
        flow.setViewport({
          x: cx + (bx - cx) * dip,
          y: cy + (by - cy) * dip,
          zoom: bz * dip,
        });
      } else {
        // No neighbor that way (or pulling away from it): rubber-band.
        flow.setViewport({ x: s.vp.x + dx * 0.3, y: s.vp.y, zoom: s.vp.zoom });
      }
    };
    const commitOrSpring = (endX: number) => {
      const s = swipeRef.current;
      swipeRef.current = null;
      setPeekHint(null);
      if (!s || !s.active) return;
      const dx = endX - s.x;
      const dt = performance.now() - s.t;
      const w = el.getBoundingClientRect().width || 1;
      const t = progressOf(dx, s, w);
      const flick = t > 0.08 && Math.abs(dx) >= 60 && dt <= 400;
      if (s.nVp && (t >= 0.35 || flick)) {
        window.dispatchEvent(
          new CustomEvent('ws:screen', {
            detail: { kind: s.dir === -1 ? 'next' : 'prev' },
          }),
        );
      } else {
        animateViewport(s.vp, 220);
        window.setTimeout(() => maybeSettleRef.current(240), 260);
      }
    };
    // Shared driver: real pointer events AND forwarded in-app edge swipes
    // (see `robutler-host-edge` below) feed the SAME state machine, so the
    // preview, the change-your-mind spring-back and the commit thresholds
    // are identical wherever the finger landed.
    const edgeStart = (clientX: number, clientY: number) => {
      swipeRef.current = null;
      if (navigateModeRef.current) return;
      if (overviewRef.current || fsRef.current) return;
      const box = el.getBoundingClientRect();
      const fromLeft = clientX - box.left;
      const fromRight = box.right - clientX;
      let dir: -1 | 1 | 0 = 0;
      if (fromLeft <= EDGE_ZONE) dir = 1; // pulling the PREVIOUS screen in
      else if (fromRight <= EDGE_ZONE) dir = -1; // pulling the NEXT screen in
      if (!dir) return;
      startSwipe(clientX, clientY, dir, box);
    };
    const startSwipe = (
      clientX: number,
      clientY: number,
      dir: -1 | 1,
      box: DOMRect,
    ) => {
      const entries = screenEntriesNow();
      const cur = entryOfItem(entries, currentScreenRef.current);
      const idx = cur ? entries.indexOf(cur) : -1;
      const nIdx = idx < 0 ? -1 : idx + (dir === -1 ? 1 : -1);
      const neighbor =
        nIdx >= 0 && nIdx < entries.length ? entries[nIdx] : null;
      swipeRef.current = {
        x: clientX,
        y: clientY,
        t: performance.now(),
        lx: clientX,
        ly: clientY,
        vp: flow.getViewport(),
        nVp: neighbor ? frameEntryCamera(neighbor, box) : null,
        dir,
        active: false,
      };
      if (neighbor) {
        // The hint arms with the touch, before any movement - the
        // "beginning is noticeable" half of the fix.
        setPeekHint({
          label: resolveScreenEntryLabel(
            neighbor,
            screensItemsRef.current,
            liveContentMetaRef.current,
          ),
          dir,
        });
      }
    };
    const edgeMove = (clientX: number, clientY: number) => {
      const s = swipeRef.current;
      if (!s) return;
      s.lx = clientX;
      s.ly = clientY;
      const dx = clientX - s.x;
      const dy = clientY - s.y;
      if (!s.active) {
        if (Math.abs(dx) < 10) return; // slop
        if (Math.abs(dx) < Math.abs(dy) * 1.2) {
          // Vertical wins - this is a scroll, not a page.
          swipeRef.current = null;
          setPeekHint(null);
          return;
        }
        s.active = true;
      }
      const box = el.getBoundingClientRect();
      apply(dx, s, box.width, box.height);
    };
    const onDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      const target = e.target as HTMLElement | null;
      if (
        !target?.closest('[data-ws-edge]') &&
        !target?.closest('.react-flow__pane') &&
        !target?.closest('.react-flow__node')
      )
        return;
      edgeStart(e.clientX, e.clientY);
    };
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      edgeMove(e.clientX, e.clientY);
    };
    const onUp = (e: PointerEvent) => {
      if (e.pointerType === 'touch') commitOrSpring(e.clientX);
    };
    const onCancel = () => {
      const s = swipeRef.current;
      if (s) commitOrSpring(s.lx);
      else setPeekHint(null);
    };
    // Forwarded in-app edge swipes: the app measured the touch (screen
    // deltas, transform-free); the frame's edge must actually abut the
    // viewport edge, or a windowed app's inner edge would page screens.
    let fwd: { x0: number; y0: number } | null = null;
    const onEdgeMsg = (ev: MessageEvent) => {
      const d = ev.data as {
        source?: string;
        phase?: string;
        edge?: string;
        cx?: number;
        cy?: number;
        dx?: number;
        dy?: number;
      } | null;
      if (!d || d.source !== 'robutler-host-edge') return;
      if (d.phase === 'start') {
        fwd = null;
        const frame = [...el.querySelectorAll('iframe')].find(
          (f) => (f as HTMLIFrameElement).contentWindow === ev.source,
        ) as HTMLIFrameElement | undefined;
        if (!frame) return;
        const r = frame.getBoundingClientRect();
        const box = el.getBoundingClientRect();
        const sc = r.width / Math.max(1, frame.clientWidth || r.width);
        const clientX = r.left + (d.cx ?? 0) * sc;
        const clientY = r.top + (d.cy ?? 0) * sc;
        const nearViewportEdge =
          d.edge === 'left'
            ? clientX - box.left <= EDGE_ZONE + 6
            : box.right - clientX <= EDGE_ZONE + 6;
        if (!nearViewportEdge) return;
        fwd = { x0: clientX, y0: clientY };
        edgeStart(clientX, clientY);
        return;
      }
      if (!fwd) return;
      if (d.phase === 'move') {
        edgeMove(fwd.x0 + (d.dx ?? 0), fwd.y0 + (d.dy ?? 0));
        return;
      }
      if (d.phase === 'end') {
        commitOrSpring(fwd.x0 + (d.dx ?? 0));
        fwd = null;
        return;
      }
      if (d.phase === 'cancel') {
        const s = swipeRef.current;
        if (s) commitOrSpring(s.lx);
        else setPeekHint(null);
        fwd = null;
      }
    };
    el.addEventListener('pointerdown', onDown, { capture: true, passive: true });
    el.addEventListener('pointermove', onMove, { capture: true, passive: true });
    el.addEventListener('pointerup', onUp, { capture: true, passive: true });
    el.addEventListener('pointercancel', onCancel, { capture: true, passive: true });
    window.addEventListener('message', onEdgeMsg);
    return () => {
      el.removeEventListener('pointerdown', onDown, { capture: true });
      el.removeEventListener('pointermove', onMove, { capture: true });
      el.removeEventListener('pointerup', onUp, { capture: true });
      el.removeEventListener('pointercancel', onCancel, { capture: true });
      window.removeEventListener('message', onEdgeMsg);
    };
  }, [isCoarse, railV2, flow, animateViewport]);

  /**
   * The screen entries plus the index of the entry under the view
   * center (nearest by center when the camera sits between screens).
   * `excludeItemId` drops one window from the computation — the window
   * IN HAND during an edge-push drag is cargo, not a screen.
   */
  const entryAtViewCenter = useCallback((excludeItemId?: string): {
    entries: ScreenEntry[];
    idx: number;
    /** Whether the view center actually sits INSIDE entries[idx]. */
    inside: boolean;
    centerX: number;
  } => {
    const source = excludeItemId
      ? screensItemsRef.current.filter((it) => it.id !== excludeItemId)
      : screensItemsRef.current;
    const entries = screenEntriesNow(source);
    const el = wrapperRef.current;
    if (!el || entries.length === 0)
      return { entries, idx: -1, inside: false, centerX: 0 };
    const box = el.getBoundingClientRect();
    const c = flow.screenToFlowPosition({
      x: box.left + box.width / 2,
      y: box.top + box.height / 2,
    });
    // On a full-screen screen (lane D) the camera shows the window at the
    // canvas's size, not its stored rect, so the view centre can fall in the
    // screen it came from: the parked screen answers instead.
    const parkedFullscreen = entries.findIndex(
      (e) => e.fullscreen && e.id === currentScreenRef.current,
    );
    if (parkedFullscreen >= 0) {
      return { entries, idx: parkedFullscreen, inside: true, centerX: c.x };
    }
    let idx = entries.findIndex(
      (e) =>
        c.x >= e.rect.x &&
        c.x <= e.rect.x + e.rect.width &&
        c.y >= e.rect.y &&
        c.y <= e.rect.y + e.rect.height,
    );
    const inside = idx >= 0;
    if (idx < 0) {
      let best = Infinity;
      entries.forEach((e, i) => {
        const d = Math.abs(e.rect.x + e.rect.width / 2 - c.x);
        if (d < best) {
          best = d;
          idx = i;
        }
      });
    }
    return { entries, idx, inside, centerX: c.x };
  }, [flow, screenEntriesNow]);

  /** Camera-only page to the neighbor screen (no raise, no restore). */
  const pageToNeighbor = useCallback(
    (dir: 1 | -1, excludeItemId?: string): boolean => {
      const { entries, idx, inside, centerX } =
        entryAtViewCenter(excludeItemId);
      if (idx < 0) return false;
      // Parked on a screen: its ordered neighbor. Between screens (or
      // the camera drifted): the nearest entry IN the push direction —
      // the nearest-as-current fallback must never make the only
      // reachable screen read as "already here".
      const target = inside
        ? entries[idx + dir]
        : dir === 1
          ? entries.find((e) => e.rect.x + e.rect.width / 2 > centerX)
          : [...entries]
              .reverse()
              .find((e) => e.rect.x + e.rect.width / 2 < centerX);
      const el = wrapperRef.current;
      if (!target || !el) return false;
      const box = el.getBoundingClientRect();
      animateViewport(frameEntryCamera(target, box), 260);
      setCurrentScreen(target.id);
      return true;
    },
    [entryAtViewCenter, animateViewport, frameEntryCamera, setCurrentScreen],
  );

  /**
   * The rect framing a screen shows (Parts IX–X): ALWAYS exactly one
   * screenful. Boards frame as themselves (they ARE a screenful); a
   * window on a blank screen frames the sheet; a window on an app
   * screen frames a wrapper-sized cell centered on its entry union —
   * NEVER the raw union, which grows as windows stack up and made the
   * "screen" feel bigger and scrollable, and never the lone window,
   * whose framing shifted the camera whenever a sibling spawned.
   * Every window on a screen shares one camera.
   */
  const screenFrameRect = useCallback((id: string): ScreenRect | null => {
    const it = screensItemsRef.current.find((x) => x.id === id);
    if (!it) return null;
    if (it.type === 'board') return it.state;
    const entries = orderScreenEntries(screensItemsRef.current);
    const entry = entryOfItem(entries, it.id);
    if (entry && entry.kind === 'blank') return entry.rect;
    const box = wrapperRef.current?.getBoundingClientRect();
    if (entry && box && box.width > 40 && box.height > 40) {
      const cell = overviewEntryOutlineRect(
        entry,
        screensItemsRef.current,
        null,
        Math.round(box.width),
        Math.round(box.height),
      );
      if (cell) return cell;
    }
    // Fallback (unmeasured wrapper): the tiled union as before.
    const windowRects = orderScreens(screensItemsRef.current)
      .filter(
        (x) => x.type !== 'board' && isOnCanvas(x.state) && x.id !== it.id,
      )
      .map((x) => x.state as ScreenRect);
    return unionTiledRect(it.state, windowRects);
  }, []);

  /** Frame a screen (always exactly 100%) and park the camera on it. */
  const frameScreen = useCallback(
    (id: string, opts?: { duration?: number }) => {
      const el = wrapperRef.current;
      if (!el) return;
      const box = el.getBoundingClientRect();
      if (box.width === 0 || box.height === 0) return;
      // A window this device shows full screen (lane D): its temporary screen
      // APPEARS, like the phone's presented app, from its own camera. The
      // page's own view only: the browser is never asked for full screen
      // (the owner, 2026-09-30; see maximize-app-node.ts).
      const full = fullscreenCamera(id);
      if (full) {
        if (!viewportsClose(flow.getViewport(), full)) animateViewport(full, 0);
        setCurrentScreen(id);
        return;
      }
      // This device's screen list: framing a screen a full-screen window
      // came from frames what is still on it.
      const entry = entryOfItem(screenEntriesNow(), id);
      const fallbackRect = screenFrameRect(id);
      const target = entry
        ? frameTargetForEntry(entry)
        : fallbackRect
          ? { rect: fallbackRect, kind: 'app' as const }
          : null;
      // The handset presentation first (the id is what the camera parks on,
      // so it names the window presented); the desktop path otherwise: this
      // device's last camera for the screen while it still shows it (fix
      // round 2: a Stage cascade comes back exactly where it was built, so
      // the next open finds its spots), else the screen framed as before.
      const presented = handsetPresentationCamera(id);
      const recalled = !presented && entry ? recalledCamera(entry) : null;
      const vp =
        presented ?? recalled ?? (target ? frameVp(target.rect, box.width, box.height, target.kind) : null);
      if (!vp) return;
      // Already parked there (e.g. an app spawning onto the CURRENT
      // screen): no glide at all — the camera must not so much as
      // twitch under the user (Part X).
      if (viewportsClose(flow.getViewport(), vp)) {
        setCurrentScreen(id);
        return;
      }
      // A presented app appears; it does not glide in over empty canvas
      // (handoff section 4.3: "a tap presents that app; nothing else moves").
      animateViewport(vp, presented ? 0 : opts?.duration ?? 320);
      setCurrentScreen(id);
    },
    [animateViewport, flow, frameTargetForEntry, fullscreenCamera, handsetPresentationCamera, recalledCamera, screenEntriesNow, screenFrameRect, setCurrentScreen],
  );

  const enterOverview = useCallback(() => {
    // The camera fit happens in the entry effect below, AFTER the
    // spread positions exist, so the fit frames what actually renders.
    setOverviewMode(true);
  }, [setOverviewMode]);

  // Overview entry fit: frame the lattice in one glide. Runs on entry
  // (INCLUDING a drag-to-top entry — the stage must zoom out under the
  // in-flight drag, that is the whole gesture) AND when the screen
  // COUNT changes mid-overview (minting brings the new cell into view;
  // deferred until an in-flight drag ends so the camera never re-fits
  // under the user's hand) — but never on mere item edits.
  const overviewEnteredRef = useRef(false);
  const overviewCellCountRef = useRef(0);
  const overviewPendingFitRef = useRef(false);
  useEffect(() => {
    const was = overviewEnteredRef.current;
    overviewEnteredRef.current = overviewMode;
    const cellCount = overviewGrid?.cells.length ?? 0;
    const countChanged = cellCount !== overviewCellCountRef.current;
    overviewCellCountRef.current = overviewMode ? cellCount : 0;
    if (!overviewMode) {
      overviewPendingFitRef.current = false;
      return;
    }
    const entryFit = !was;
    const countFit = was && (countChanged || overviewPendingFitRef.current);
    if (!entryFit && !countFit) return;
    if (!entryFit && itemDragActive) {
      overviewPendingFitRef.current = true;
      return;
    }
    overviewPendingFitRef.current = false;
    const el = wrapperRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    if (box.width === 0 || box.height === 0) return;
    // Fit the overview LATTICE (Part VI): every grid cell plus the
    // "+ New screen" tile cell, with breathing room for the labels
    // under the bottom row.
    if (!overviewGrid) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const r of [...overviewGrid.cells.map((c) => c.cell), overviewGrid.tile]) {
      minX = Math.min(minX, r.x);
      minY = Math.min(minY, r.y);
      maxX = Math.max(maxX, r.x + r.width);
      maxY = Math.max(maxY, r.y + r.height);
    }
    if (minX === Infinity) return;
    // Label row under the bottom cells — keep it inside the fit.
    maxY += overviewGrid.rowGap / 2;
    const bw = Math.max(maxX - minX, 1);
    const bh = Math.max(maxY - minY, 1);
    if (box.width < 700) {
      // Narrow viewports WIDTH-fit and anchor at the top: the map scrolls
      // vertically (panOnDrag is enabled for overview there) instead of
      // shrinking every screen to an unreadable thumbnail to fit-all.
      const PADN = 16;
      const TOP = 64; // clear the switcher / Share chips
      const zoom = Math.max(0.05, Math.min((box.width - 2 * PADN) / bw, 1));
      animateViewport(
        {
          x: PADN + (box.width - 2 * PADN - bw * zoom) / 2 - minX * zoom,
          y: TOP - minY * zoom,
          zoom,
        },
        320,
      );
      return;
    }
    const PAD = 48;
    const zoom = Math.max(
      0.02,
      Math.min((box.width - 2 * PAD) / bw, (box.height - 2 * PAD) / bh, 1),
    );
    animateViewport(
      {
        x: box.width / 2 - (minX + bw / 2) * zoom,
        y: box.height / 2 - (minY + bh / 2) * zoom,
        zoom,
      },
      320,
    );
  }, [overviewMode, overviewGrid, animateViewport, itemDragActive]);

  const exitOverview = useCallback(
    (toId?: string) => {
      setOverviewMode(false);
      const target =
        toId ??
        currentScreenRef.current ??
        orderScreens(screensItemsRef.current).find((it) =>
          isOnCanvas(it.state),
        )?.id ??
        null;
      if (target) frameScreen(target);
    },
    [frameScreen, setOverviewMode],
  );

  /**
   * Settle a released pan onto the dominant screen. No-op while the
   * overview or a fullscreen takeover owns the surface, while a
   * programmatic animation is in flight, while the view sits inside a
   * board (bounded freeform keeps today's rules), or when no screen
   * covers enough of the viewport AND something is still visible
   * (browsing board content stays free). A release with NOTHING
   * visible glides back to the nearest screen — the soft version of
   * the old hard pan clamp.
   */
  // A window may NEVER be left with its header off the screen. Dragging
  // up to the top edge (and past it, for the stage overdrag) parks the
  // title bar above the viewport, where there is nothing left to grab —
  // the window is stranded and only a chord or the taskbar can recover it.
  // So after any freehand drop we pull it back into reach, gliding: the
  // top edge lands EXACTLY on the viewport's top (the position the user
  // was obviously aiming for), and a window dropped below the fold keeps
  // at least its header on screen.
  //
  // Vertical only. Screens sit side by side on the x axis, so clamping x
  // would fight the legitimate "drag it onto the neighbouring screen".
  const rescueOffscreenWindow = useCallback(
    (itemId: string) => {
      if (overviewRef.current || fsRef.current) return;
      // A handset never writes geometry (handoff section 4.8): the presented
      // window is pinned by the camera, and there is no freehand drop.
      if (fitFramingRef.current) return;
      const el = wrapperRef.current;
      if (!el) return;
      const box = el.getBoundingClientRect();
      if (box.width === 0 || box.height === 0) return;
      const it = screensItemsRef.current.find((x) => x.id === itemId);
      if (!it || it.type === 'board' || !isOnCanvas(it.state)) return;
      const tl = flow.screenToFlowPosition({ x: box.left, y: box.top });
      const br = flow.screenToFlowPosition({ x: box.right, y: box.bottom });
      const next = keepHeaderInView(it.state.y, tl.y, br.y);
      if (next === null) return;
      const rounded = Math.round(next);
      if (rounded === Math.round(it.state.y)) return;
      glideOnce();
      patchItemState(itemId, { y: rounded });
    },
    [flow, patchItemState, glideOnce],
  );

  const maybeSettle = useCallback(
    (duration = 260) => {
      if (overviewRef.current || fsRef.current) return;
      if (screenGuardRef.current > 0) return;
      const el = wrapperRef.current;
      if (!el) return;
      const box = el.getBoundingClientRect();
      if (box.width === 0 || box.height === 0) return;
      const tl = flow.screenToFlowPosition({ x: box.left, y: box.top });
      const br = flow.screenToFlowPosition({ x: box.right, y: box.bottom });
      const view = {
        x: tl.x,
        y: tl.y,
        width: br.x - tl.x,
        height: br.y - tl.y,
      };
      const screens = orderScreens(screensItemsRef.current).filter((it) =>
        isOnCanvas(it.state),
      );
      if (screens.length === 0) return;
      // A presented handset window is PINNED: whatever moved the camera (a
      // two-finger pan, a gesture the app let through), the settle puts the
      // stored top-left back on the usable box. Before the board check
      // below, because on a phone the presented window sits on its backing
      // sheet and "inside a board the camera is free" would leave it adrift.
      const pinned = handsetPresentationCamera(currentScreenRef.current);
      if (pinned) {
        if (!viewportsClose(flow.getViewport(), pinned)) animateViewport(pinned, duration);
        return;
      }
      // A desktop full-screen screen is pinned the same way (lane D).
      const pinnedFull = fullscreenCamera(currentScreenRef.current);
      if (pinnedFull) {
        if (!viewportsClose(flow.getViewport(), pinnedFull)) animateViewport(pinnedFull, 0);
        return;
      }
      // Inside a board the camera is free — that is what boards are for.
      if (
        screens.some(
          (b) => b.type === 'board' && rectContainsRect(b.state, view, 48),
        )
      ) {
        return;
      }
      // Candidate entries: windows collapse split-tiled runs into one
      // union screenful; boards stand as themselves.
      const windows = screens.filter((it) => it.type !== 'board');
      const windowRects = windows.map((it) => it.state as ScreenRect);
      const entries: Array<{ id: string; rect: ScreenRect }> = [];
      const seenEntry = new Set<string>();
      for (const it of windows) {
        const rect = unionTiledRect(it.state, windowRects);
        const key = `${Math.round(rect.x)}:${Math.round(rect.y)}:${Math.round(rect.width)}:${Math.round(rect.height)}`;
        if (seenEntry.has(key)) continue;
        seenEntry.add(key);
        entries.push({ id: it.id, rect });
      }
      for (const b of screens) {
        if (b.type === 'board') entries.push({ id: b.id, rect: b.state });
      }
      const pick = pickSettleScreen(entries, view);
      if (!pick) return;
      if (pick.coverage >= SETTLE_MIN_COVERAGE) {
        const entry = entries.find((e) => e.id === pick.id);
        if (!entry) return;
        // Park on the MEMBER window the user is closest to (arrows and
        // the same-digit back-hop anchor on items, not unions).
        let parkId = pick.id;
        let best = -1;
        for (const w of windows) {
          if (!rectContainsRect(entry.rect, w.state, 12)) continue;
          const ix = Math.max(
            0,
            Math.min(w.state.x + w.state.width, view.x + view.width) -
              Math.max(w.state.x, view.x),
          );
          const iy = Math.max(
            0,
            Math.min(w.state.y + w.state.height, view.y + view.height) -
              Math.max(w.state.y, view.y),
          );
          if (ix * iy > best) {
            best = ix * iy;
            parkId = w.id;
          }
        }
        const settleKind =
          screensItemsRef.current.find((x) => x.id === entry.id)?.type ===
          'board'
            ? ('blank' as const)
            : ('app' as const);
        const vp = frameVp(entry.rect, box.width, box.height, settleKind);
        if (viewportsClose(flow.getViewport(), vp)) {
          setCurrentScreen(parkId);
          return;
        }
        animateViewport(vp, duration);
        setCurrentScreen(parkId);
        return;
      }
      // Below the settle threshold: free browsing while anything is
      // visible; a release in the void glides back (soft clamp).
      const anyVisible = screensItemsRef.current.some(
        (it) =>
          it.type !== 'connector' &&
          isOnCanvas(it.state) &&
          rectsIntersect(it.state, view),
      );
      if (anyVisible) return;
      const entry = entries.find((e) => e.id === pick.id);
      if (!entry) return;
      // Same WHAT-to-frame brain as frameScreen: a settle onto a screen
      // whose cell overflows the box must land on the member windows, not
      // the cell corner (the spawn-clipping bug applied here on every
      // pan-release too).
      animateViewport(frameEntryCamera(entry, box, pick.id), 320);
      setCurrentScreen(pick.id);
    },
    [flow, animateViewport, frameEntryCamera, frameVp, fullscreenCamera, handsetPresentationCamera, setCurrentScreen],
  );
  maybeSettleRef.current = maybeSettle;

  // THE PHONE APP VIEW'S ONE INVARIANT, kept live: whenever the presented
  // window or the usable box changes (a rotation, the keyboard, the
  // navigation hiding for in-tab full screen, the insets arriving after the
  // first paint), the camera is re-aimed so the drawn window and the camera
  // agree again. Instant: the node is redrawn in the same frame.
  useEffect(() => {
    if (!presentedId || !usableBoxState) return;
    const vp = handsetPresentationCamera(currentScreenRef.current);
    if (!vp) return;
    if (viewportsClose(flow.getViewport(), vp)) return;
    animateViewport(vp, 0);
  }, [presentedId, usableBoxState, flow, animateViewport, handsetPresentationCamera]);

  // The in-tab full screen flag only means something while a window is
  // presented; when nothing is (a blank screen, the last app closed) the
  // navigation comes back by itself, so a phone can never be left without
  // its Discover, Stage and You. A desktop never sets the flag.
  const handsetFullscreen = useHandsetFullscreen();
  useEffect(() => {
    if (handsetFullscreen && !presentedId) setHandsetFullscreen(false);
  }, [handsetFullscreen, presentedId, setHandsetFullscreen]);

  // ---- desktop full screen (lane D; the Stage algorithm, rules 12 to 17) ----

  /**
   * Show `id` full screen on this device (rule 12): the store records it with
   * where to come back to (the parked screens and the camera as they are
   * now, rule 15), and the camera jumps to its temporary screen. The browser
   * was already asked for the page's full screen inside the click
   * (content-node's `maximizeApp`). Nothing is written.
   */
  const enterFullscreenWindow = useCallback(
    (id: string) => {
      if (fitFramingRef.current) return;
      const it = screensItemsRef.current.find((x) => x.id === id);
      if (!it || it.type === 'board' || !isScreenType(it.type) || !isOnCanvas(it.state)) return;
      if (overviewRef.current) setOverviewMode(false);
      const entered = enterDesktopFullscreen(id, {
        screenId: currentScreenRef.current,
        prevScreenId: prevScreenRef.current,
        viewport: flow.getViewport(),
      });
      if (!entered) return;
      // The refs the camera callbacks read follow the store NOW, not at the
      // next render.
      if (!desktopFullscreenIdsRef.current.includes(id)) {
        desktopFullscreenIdsRef.current = [...desktopFullscreenIdsRef.current, id];
      }
      animateViewport(fullscreenCameraFor(it.state), 0);
    },
    [animateViewport, enterDesktopFullscreen, flow, setOverviewMode],
  );

  /**
   * Leave desktop full screen for `id` (rule 15): its temporary screen goes,
   * and when the view was on it, the view returns to the screen it came
   * from, "framed as before": the saved camera and parked screens, when that
   * screen is still there and the saved view still shows the window's place
   * (a peer may have moved it meanwhile; then the view follows the window).
   */
  const exitFullscreenWindow = useCallback(
    (id: string) => {
      const wasShown = currentScreenRef.current === id;
      const record = exitDesktopFullscreen(id);
      if (!record) return;
      desktopFullscreenIdsRef.current = desktopFullscreenIdsRef.current.filter((x) => x !== id);
      if (!wasShown) return;
      const it = screensItemsRef.current.find((x) => x.id === id);
      const back = record.returnScreenId;
      const vp = record.returnViewport;
      const el = wrapperRef.current;
      const box = el?.getBoundingClientRect();
      const savedView =
        vp && box && box.width > 0 && vp.zoom > 0
          ? { x: -vp.x / vp.zoom, y: -vp.y / vp.zoom, width: box.width / vp.zoom, height: box.height / vp.zoom }
          : null;
      if (
        back &&
        vp &&
        it &&
        savedView &&
        entryOfItem(screenEntriesNow(), back) &&
        rectsIntersect(savedView, it.state)
      ) {
        if (!viewportsClose(flow.getViewport(), vp)) animateViewport(vp, 0);
        restoreScreens(back, record.returnPrevScreenId);
        return;
      }
      frameScreen(id);
    },
    [animateViewport, exitDesktopFullscreen, flow, frameScreen, restoreScreens, screenEntriesNow],
  );

  // The camera follows the full-screen window on view: when it changes, when
  // the canvas box changes (the rail steps aside, the browser's full screen
  // lands), and when a peer moves the window (its stored top-left is the
  // camera's anchor). Instant, like the phone's presentation.
  const fullscreenShownAnchor = useMemo(() => {
    if (!desktopFullscreenShown) return null;
    const it = items.find((x) => x.id === desktopFullscreenShown);
    return it ? `${it.state.x}:${it.state.y}` : null;
  }, [items, desktopFullscreenShown]);
  useEffect(() => {
    if (!desktopFullscreenShown) return;
    const vp = fullscreenCamera(desktopFullscreenShown);
    if (!vp || viewportsClose(flow.getViewport(), vp)) return;
    animateViewport(vp, 0);
  }, [desktopFullscreenShown, fullscreenShownAnchor, canvasBox, flow, animateViewport, fullscreenCamera]);

  // A handset shows one app at a time already (lane C): crossing into the
  // handset layout (a window narrowed under md, a rotation) ends desktop full
  // screen rather than leaving a window drawn at a desktop box on a phone.
  useEffect(() => {
    if (!fitFraming || desktopFullscreenRef.current.length === 0) return;
    for (const r of desktopFullscreenRef.current) exitDesktopFullscreen(r.id);
    desktopFullscreenIdsRef.current = [];
  }, [fitFraming, desktopFullscreen, exitDesktopFullscreen]);

  // FULL SCREEN ON OPEN (2026-10-03; the owner: "after signin via ?app=appname
  // the apps must open in full screen mode (no discover/stage/etc shown) by
  // default"). The link's spawn names the window in the store
  // (`pendingFullscreen`, lib/workspaces/url-actions.ts); this shows it full
  // screen the way THIS device does it, once the window is really here: a
  // desktop's temporary full-screen screen (the green dot's own path), or
  // the phone's in-tab flag, which only means something for the presented
  // window, so it waits for the phone to present it. A request whose window
  // never arrives is dropped after `PENDING_FULLSCREEN_MS`, so it cannot
  // fire minutes later under something else. This effect writes nothing.
  //
  // AND IT COMES BACK AFTER A RELOAD (the owner, the same day, about a
  // refresh returning to the ordinary view: "not on refresh, ... menu should
  // exit fullscreen"). The store keeps the full screen on view in this tab's
  // own memory and forgets it on every way out (`syncFullscreenMemory`); on
  // mount the canvas asks for it back through the very same request, so a
  // reload and an app link enter full screen by one path. The way out is the
  // person's: Exit full screen in the app's ⋯ menu (a phone's sheet and a
  // desktop's dropdown both carry it), the bar's exit, Esc on a desktop.
  useEffect(() => {
    recallFullscreenOnLoad();
  }, [recallFullscreenOnLoad, workspace.id]);
  const pendingFullscreen = usePendingFullscreen();
  useEffect(() => {
    if (!pendingFullscreen) return;
    const { id, at } = pendingFullscreen;
    if (Date.now() - at > PENDING_FULLSCREEN_MS) {
      clearPendingFullscreen();
      return;
    }
    const it = items.find((x) => x.id === id);
    if (!it || !isOnCanvas(it.state)) return;
    if (fitFraming) {
      if (presentedId !== id) return;
      setHandsetFullscreen(true);
    } else {
      enterFullscreenWindow(id);
    }
    clearPendingFullscreen();
  }, [
    pendingFullscreen,
    items,
    fitFraming,
    presentedId,
    enterFullscreenWindow,
    setHandsetFullscreen,
    clearPendingFullscreen,
  ]);

  // Esc (rule 14; the owner, 2026-09-30: full screen is the page's own view,
  // never the browser's). A press ON THE PAGE leaves the full screen on view.
  // A press inside an app never reaches this document (the frame has the
  // focus), so it stays the app's: apps use Esc themselves and the page
  // steals nothing. Whatever else owns Esc goes first: a drag cancel
  // (capture phase, preventDefault), the overview, an open menu or dialog,
  // a text field, an open panel. Bubble phase, like the overview's own Esc.
  //
  // AN OPEN PANEL OWNS ITS ESC (2026-10-03). The notifications card opens
  // over a full-screen app from the bar's ⋯, and a panel closes itself on
  // Esc (panel-shell.tsx) without claiming the key, so one press closed the
  // card AND left full screen. `watchPanelOwnsEsc` answers "was a panel
  // card open as this press began", which holds whichever of the two
  // handlers the browser runs first (fullscreen/panel-owns-esc.ts has the
  // why). The next press, with no card open, leaves full screen.
  useEffect(() => {
    if (!desktopFullscreenShown || overviewMode) return;
    const panelEsc = watchPanelOwnsEsc();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || e.repeat) return;
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest('input, textarea, select, [contenteditable="true"], [role="menu"], [role="dialog"]')) return;
      if (document.querySelector('[role="menu"], [role="dialog"][aria-modal="true"]')) return;
      if (panelEsc.owned()) return;
      const shown = desktopFullscreenShownRef.current;
      if (shown) exitFullscreenWindow(shown);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      panelEsc.stop();
    };
  }, [desktopFullscreenShown, overviewMode, exitFullscreenWindow]);

  // Leaving hand mode deliberately does NOT settle (user call): the mode is
  // how you park the camera somewhere exact, at an exact zoom, and any
  // snap-to-screen on exit threw that framing away. The nav lock resumes
  // from wherever the user left the camera; the Fit button (hand mode's one
  // zoom control) is the explicit way back to a framed screen.


  // `ws:screen` — the single chokepoint the rail taskbar, the ⌘⇧ chords,
  // and the overview covers use to drive screen navigation without a
  // flow ref. Kinds: `item` (go to a screen, restoring it from the dock
  // if minimized), `index` (1-based taskbar order; the digit of the
  // CURRENT screen hops back to the previous one), `overview-toggle`.
  // Lane D (2026-09-29) adds three: `fullscreen-enter` and
  // `fullscreen-exit` (the green dot, content-node's `maximizeApp`; rules 12
  // and 15), and `frame` (go to a screen WITHOUT raising or un-docking
  // anything: the store's return when the screen you were on is gone,
  // rule 9).
  useEffect(() => {
    const goToItem = (id: string) => {
      const it = screensItemsRef.current.find((x) => x.id === id);
      if (!it) return;
      if (getPlacement(it.state) === 'docked') {
        patchItemState(id, { placement: 'canvas' });
      }
      if (overviewRef.current) setOverviewMode(false);
      // Blank screens keep their sheet at the BACK of the stack —
      // raising one would cover the very items it hosts. A handset raises
      // nothing: z-order is shared, and the phone presents the parked
      // window directly (handoff section 4.1), so "Go to" writes only this
      // device's parked id. Nor does a window shown full screen on this
      // device (lane D): it has a screen of its own, and raising it would
      // write the shared z-order for a view no one else has.
      if (!desktopFullscreenIdsRef.current.includes(id)) {
        if (it.type !== 'board' && !fitFramingRef.current) bringToFront(id);
      }
      frameScreen(id);
    };
    const onScreen = (e: Event) => {
      const d = (
        e as CustomEvent<{
          kind?: string;
          index?: number;
          itemId?: string;
          layout?: 'left' | 'right' | 'full';
        }>
      ).detail;
      if (!d?.kind) return;
      // The takeover owns the surface — camera moves under it would be
      // invisible state churn (the rail mutes its chords for the same
      // reason; the ⌘⇧←/→ switcher handles fullscreen swaps itself).
      if (fsRef.current) return;
      if (d.kind === 'overview-toggle') {
        if (overviewRef.current) exitOverview();
        else enterOverview();
        return;
      }
      if (d.kind === 'item' && d.itemId) {
        goToItem(d.itemId);
        return;
      }
      if (d.kind === 'fullscreen-enter' && d.itemId) {
        enterFullscreenWindow(d.itemId);
        return;
      }
      if (d.kind === 'fullscreen-exit' && d.itemId) {
        exitFullscreenWindow(d.itemId);
        return;
      }
      if (d.kind === 'frame' && d.itemId) {
        // In Stage the camera belongs to the lattice: park only, and
        // leaving Stage frames the parked screen.
        if (overviewRef.current) {
          setCurrentScreen(d.itemId);
          return;
        }
        frameScreen(d.itemId);
        return;
      }
      if (d.kind === 'zoom-100') {
        // The mobile bar's zoom menu: straight back to 100%, centre
        // anchored (same helper as the dormant hand cluster).
        zoomTo100();
        return;
      }
      if (d.kind === 'zoom-fit-screen') {
        fitNearestScreen();
        return;
      }
      if (d.kind === 'zoom-fit-all') {
        fitAllScreens();
        return;
      }
      if (d.kind === 'new-screen') {
        // Mint a blank screen in the overview's next free slot — the same
        // path as the overview's dashed tile, exposed on the chokepoint so
        // the mobile Stage SHEET (S1) can create screens without owning
        // any of the canvas geometry.
        const s = overviewNewScreenSlot(screensItemsRef.current, null);
        createBoardAt(s ? { x: s.x, y: s.y } : undefined);
        return;
      }
      if (d.kind === 'next' || d.kind === 'prev') {
        // Camera-only paging between SCREEN ENTRIES, no wrap, no raise.
        pageToNeighbor(d.kind === 'next' ? 1 : -1);
        return;
      }
      if (d.kind === 'layout' && d.itemId && d.layout) {
        // Split layouts (Snap-Layouts style): place the window in the
        // left half / right half / whole of the 1:1 SCREENFUL centered
        // on what the user is looking at (the 100% law: regions are
        // viewport-pixel-sized in world units, so halves tile the exact
        // screenful at zoom 1). Grid-snapped, honoring declared min/max
        // sizes; afterwards the camera glides to frame that screenful
        // at 100%, and `unionTiledRect` makes complementary halves
        // settle/frame as one screenful from then on.
        const it = screensItemsRef.current.find((x) => x.id === d.itemId);
        const el = wrapperRef.current;
        if (!it || !el) return;
        const box = el.getBoundingClientRect();
        if (box.width === 0 || box.height === 0) return;
        // No layout writes on a handset (handoff section 4.8). The handset
        // "fill" this branch used to hold WROTE a phone-sized width and
        // height into the shared document on a header double-tap, so a
        // phone resized the laptop's window; the phone app view presents
        // the window full-tab without writing, and its bar has no
        // double-tap. The desktop tiles are the desktop's.
        if (fitFramingRef.current) return;
        // Nor while the window is full screen on this device (lane D): the
        // layouts write the window for everyone and are one Exit away.
        if (desktopFullscreenIdsRef.current.includes(d.itemId)) return;
        const c = flow.screenToFlowPosition({
          x: box.left + box.width / 2,
          y: box.top + box.height / 2,
        });
        // Uniform macOS-tile spacing (Part XII field pick): ONE small
        // margin everywhere — the outer edges and the seam between the
        // halves are the same 8px, so a tiled pair reads as one evenly
        // gridded surface.
        const m = 8;
        const gap = 8;
        const screenful: ScreenRect = {
          x: c.x - box.width / 2,
          y: c.y - box.height / 2,
          width: box.width,
          height: box.height,
        };
        const innerX = screenful.x + m;
        const innerY = screenful.y + m;
        const innerW = screenful.width - 2 * m;
        const innerH = screenful.height - 2 * m;
        if (innerW < 100 || innerH < 100) return;
        const region: ScreenRect =
          d.layout === 'left'
            ? { x: innerX, y: innerY, width: (innerW - gap) / 2, height: innerH }
            : d.layout === 'right'
              ? {
                  x: innerX + (innerW + gap) / 2,
                  y: innerY,
                  width: (innerW - gap) / 2,
                  height: innerH,
                }
              : { x: innerX, y: innerY, width: innerW, height: innerH };
        const cache = (it.state.cache ?? {}) as {
          maxSize?: { width?: number; height?: number };
          minSize?: { width?: number; height?: number };
        };
        let w = region.width;
        let h = region.height;
        if (typeof cache.maxSize?.width === 'number')
          w = Math.min(w, cache.maxSize.width);
        if (typeof cache.maxSize?.height === 'number')
          h = Math.min(h, cache.maxSize.height);
        if (typeof cache.minSize?.width === 'number')
          w = Math.max(w, cache.minSize.width);
        if (typeof cache.minSize?.height === 'number')
          h = Math.max(h, cache.minSize.height);
        // Exact placement — tiles are precise by definition; snapping
        // to the 24-grid fought the 16px margins and opened seams.
        patchItemState(d.itemId, {
          x: Math.round(region.x + (region.width - w) / 2),
          y: Math.round(region.y + (region.height - h) / 2),
          width: Math.round(w),
          height: Math.round(h),
        });
        bringToFront(d.itemId);
        setCurrentScreen(d.itemId);
        // Normalize the camera onto that screenful at 100% (the region
        // math above already framed it around the view center, so this
        // is a short glide; from a non-1 zoom it is the upgrade to 1:1).
        animateViewport(
          frameVp(screenful, box.width, box.height, 'app'),
          220,
        );
        return;
      }
      if (d.kind === 'index' && typeof d.index === 'number') {
        // Digits address SCREENS left to right (the macOS Spaces
        // convention, Part IV) — one list shared with arrows and dots,
        // this device's full-screen screens included (rule 17).
        const entries = screenEntriesNow();
        const target = entries[d.index - 1];
        if (!target) return;
        const cur = entryOfItem(entries, currentScreenRef.current);
        const prev = prevScreenRef.current;
        if (
          cur &&
          target.id === cur.id &&
          prev &&
          !cur.memberIds.includes(prev) &&
          screensItemsRef.current.some((x) => x.id === prev)
        ) {
          goToItem(prev);
          return;
        }
        goToItem(target.id);
      }
    };
    window.addEventListener('ws:screen', onScreen);
    return () => {
      window.removeEventListener('ws:screen', onScreen);
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
      }
    };
  }, [
    animateViewport,
    bringToFront,
    createBoardAt,
    enterFullscreenWindow,
    enterOverview,
    exitFullscreenWindow,
    exitOverview,
    flow,
    frameScreen,
    patchItemState,
    screenEntriesNow,
    setCurrentScreen,
    setOverviewMode,
  ]);

  // Esc leaves the overview (bubble phase, after the capture-phase
  // gesture-cancel handler — that one preventDefaults when a drag is in
  // flight, and we honor it).
  useEffect(() => {
    if (!overviewMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      exitOverview();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [overviewMode, exitOverview]);

  // Camera limits are SOFT now: instead of a hard d3 pan clamp
  // (translateExtent), a release with nothing visible glides back to
  // the nearest screen — see the rescue branch in `maybeSettle`. Free
  // overpan during the gesture, guaranteed recovery on release.

  const onMove = useCallback(
    (_e: unknown, vp: Viewport) => {
      // The shared camera row is not written while a handset presents a
      // window: that camera is the window's top-left on this phone, which a
      // laptop's first visit would otherwise restore (handoff section 4.8).
      // Nor while a desktop full-screen screen is on view (lane D), for the
      // same reason: that camera belongs to this device's full screen.
      if (!desktopFullscreenShownRef.current) {
        if (!(fitFramingRef.current && presentedRef.current)) setCamera(vp.x, vp.y, vp.zoom);
      }
      if (vp.zoom >= dynMinZoomRef.current) setZoomFloorRelaxed(false);
      // Keep the store's viewport anchor + rect in sync — `addItem`
      // uses these to drop new widgets in the user's current view
      // instead of the canvas origin. Cheap: a few screen→flow coord
      // conversions per pan/zoom step (already throttled by RF).
      syncViewportToStore();
    },
    [setCamera, syncViewportToStore],
  );

  // ---- Esc cancels an in-flight node drag / connection drag ------------
  //
  // React Flow has no native cancel gesture, and mid-drag positions are
  // already written into OUR store (`persist: false`, see onNodesChange)
  // so the node visually tracks the cursor. Cancelling therefore means:
  //   1. snapshot every dragged node's start position on drag-start;
  //   2. on Esc, arm `dragCancelRef` and synthetically end the gesture —
  //      d3-drag (node move) listens for `mouseup` on the window and
  //      XYHandle (connection drag) for `pointerup` on the document, so
  //      one pair of synthetic events dispatched at the document (whose
  //      propagation path includes the window) terminates either;
  //   3. while armed, onNodesChange substitutes the START coords for any
  //      position change RF emits (including the final dragging:false
  //      frame), and onNodeDragStop re-patches once more as a backstop.
  //
  // A connection drag has no store state to restore — ending it over
  // nothing simply never creates the edge. (`dragStartPosRef` /
  // `dragCancelRef` are declared up top, above the nodes memo, which
  // reads them during render for the overview drag exemption.)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const starts = dragStartPosRef.current;
      const connecting = !!wrapperRef.current?.querySelector(
        '.react-flow__connectionline, .react-flow__connection',
      );
      if ((!starts || starts.size === 0) && !connecting) return;
      // Capture phase + preventDefault so the cancel wins over every
      // other Esc consumer (panel close, annotation tool disarm, …).
      e.preventDefault();
      e.stopPropagation();
      if (starts && starts.size > 0) dragCancelRef.current = starts;
      document.dispatchEvent(
        new PointerEvent('pointerup', { bubbles: true, cancelable: true, view: window }),
      );
      document.dispatchEvent(
        new MouseEvent('mouseup', { bubbles: true, cancelable: true, view: window }),
      );
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);

  // ---- drag / resize → store ------------------------------------------
  // We let React Flow's internal store own the live position WHILE the
  // user is dragging — every position change with `dragging === true`
  // is intentionally a no-op as far as our store is concerned. That
  // eliminates the cascade of re-renders previously caused by re-emitting
  // store state at 60Hz, which manifested as cursor-vs-node jitter on
  // workspaces with more than a handful of widgets.
  //
  // On drag-end (`dragging === false`) we:
  //   1. Compute the alignment snap one final time so the resting
  //      position lands on the nearest guide / grid intersection.
  //   2. Patch the store (debounced) so the new position persists.
  //
  // During drag we still publish guide segments to the overlay so the
  // user gets the Figma-jam visual hint without paying for a store
  // round-trip.
  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      let pendingGuides: GuideLine[] = [];

      // Cached snap helpers, computed lazily — we only need them when
      // a position change actually arrives.
      const buildCandidates = (excludeId: string): Rect[] => {
        const arr: Rect[] = [];
        for (const it of items) {
          if (it.state.docked) continue;
          if (it.id === excludeId) continue;
          arr.push({
            id: it.id,
            x: it.state.x,
            y: it.state.y,
            width: it.state.width,
            height: it.state.height,
          });
        }
        return arr;
      };

      const viewportRect = () => {
        const el = wrapperRef.current;
        if (!el) return undefined;
        const rect = el.getBoundingClientRect();
        const tl = flow.screenToFlowPosition({ x: rect.left, y: rect.top });
        const br = flow.screenToFlowPosition({ x: rect.right, y: rect.bottom });
        return {
          x: tl.x,
          y: tl.y,
          width: br.x - tl.x,
          height: br.y - tl.y,
        };
      };

      // Collect `select` changes into a single state update so a
      // multi-change batch (e.g. "click node X" which emits a
      // deselect for previously-selected nodes AND a select for X)
      // resolves in one commit.
      //
      // Functional setState (no closure read of `selectedNodeIds`)
      // keeps this callback's dep array stable — otherwise
      // `onNodesChange` would change identity on every selection
      // toggle and RF would tear down/rebuild its drag listeners.
      const selectChanges = changes.filter((c) => c.type === 'select');
      if (selectChanges.length > 0) {
        setSelectedNodeIds((prev) => {
          let mutated = false;
          const next = new Set(prev);
          for (const c of selectChanges) {
            if (c.type !== 'select') continue;
            if (c.selected) {
              if (!next.has(c.id)) {
                next.add(c.id);
                mutated = true;
              }
            } else if (next.has(c.id)) {
              next.delete(c.id);
              mutated = true;
            }
          }
          return mutated ? next : prev;
        });
      }

      for (const change of changes) {
        if (change.type === 'position' && change.position) {
          // Esc-cancelled drag: ignore whatever position RF reports
          // (including the final dragging:false frame the synthetic
          // mouseup produces) and pin the node back to its drag-start
          // coordinates instead.
          const cancelledPos = dragCancelRef.current?.get(change.id);
          if (cancelledPos) {
            patchItemState(
              change.id,
              { x: cancelledPos.x, y: cancelledPos.y },
              change.dragging === true ? { persist: false } : { debounceMs: 200 },
            );
            pendingGuides = [];
            continue;
          }
          const moving = items.find((it) => it.id === change.id);
          if (!moving) continue;
          // Alignment-snap threshold. 12px is the largest value that
          // still feels "snappy" — any bigger and the moving item
          // yanks across the viewport on near misses.
          //
          // Why we own grid snap here instead of letting React Flow
          // do it via `snapToGrid`: when RF snaps positions to the
          // 24-px grid BEFORE handing them to this callback, the
          // moving rect is always on the lattice. Other on-grid
          // candidates produce alignment deltas that are exact
          // multiples of 24 — so the only delta inside our 12-px
          // threshold is 0 (literal edge-on-edge), which is exactly
          // when an alignment guide would be visually redundant.
          // Dragging through the in-between cells produced no
          // guides at all — the "rulers don't work reliably with
          // grid snap on" symptom the user flagged. Pulling snap
          // into userland lets us prefer alignment whenever it's
          // close enough, and only fall back to the grid when no
          // candidate is in range.
          const ALIGN_THRESHOLD = 12;
          const result = computeAlignmentSnap(
            {
              id: change.id,
              x: change.position.x,
              y: change.position.y,
              width: moving.state.width,
              height: moving.state.height,
            },
            buildCandidates(change.id),
            {
              threshold: ALIGN_THRESHOLD,
              cap: 100,
              viewport: viewportRect(),
            },
          );

          // For axes where alignment didn't fire, snap to the grid
          // ourselves so the moving item still rests on a tidy
          // lattice between alignment targets.
          if (snap) {
            const xSnapped = result.guides.some((g) => g.axis === 'x');
            const ySnapped = result.guides.some((g) => g.axis === 'y');
            if (!xSnapped) {
              result.x = Math.round(result.x / GRID_MINOR) * GRID_MINOR;
            }
            if (!ySnapped) {
              result.y = Math.round(result.y / GRID_MINOR) * GRID_MINOR;
            }
          }
          // Three cases for a position change:
          //   - `dragging === true`  → user-driven node drag, mid-flight.
          //     Update store with `persist: false` so the controlled
          //     `nodes` prop tracks the cursor without 60Hz API churn.
          //   - `dragging === false` → drag-end. Commit with debounce
          //     so the server stores the final snapped coords.
          //   - `dragging === undefined` → emitted by NodeResizer
          //     when the resize anchor is the top/left corner. Treat
          //     these like a final commit (debounced) — there's no
          //     explicit "end" frame, but the debounce coalesces a
          //     stream of changes into one PATCH 200ms after the
          //     resize gesture goes quiet.
          //
          // Why we can't skip the store write mid-drag: React Flow
          // nodes are controlled here (we pass `nodes` from store
          // state). RF does NOT keep an internal copy of positions
          // when nodes are controlled — it reads straight from the
          // prop on every frame. If we silently drop position changes
          // mid-drag the prop never moves and the node stays glued to
          // its start coordinate until drag-end, when we jump it to
          // the final spot. That's the "shapes are invisible during
          // drag, then teleport" bug.
          if (change.dragging === true) {
            patchItemState(
              change.id,
              { x: result.x, y: result.y },
              { persist: false },
            );
            pendingGuides = result.guides;
          } else {
            patchItemState(
              change.id,
              { x: result.x, y: result.y },
              { debounceMs: 200 },
            );
            pendingGuides = [];
          }
        }
        if (change.type === 'dimensions' && change.dimensions) {
          // Skip RF's ResizeObserver echo while our custom gesture
          // owns the size. RF measures the DOM and emits `dimensions`
          // changes during a resize, but `useNodeResize` already
          // committed the snapped `{x, y, width, height}` in the same
          // patch. Echoing RF's measured width back here overrides
          // ours while leaving `x` untouched — that mismatch shows up
          // as the OPPOSITE edge jittering by a pixel during LEFT/TOP
          // resizes (RIGHT/BOTTOM gestures don't shift x/y so the
          // conflict is invisible). The gesture's own `endResize`
          // commits the final size with `debounceMs: 200`, so the
          // server still picks up the post-gesture state.
          if (isResizingItem(change.id)) continue;
          // Same skip for the handset clamp: the DOM is deliberately drawn
          // smaller than the store says, and that must never be written back.
          if (handsetClampedRef.current.has(change.id)) continue;
          const isEnd = change.resizing === false;
          patchItemState(
            change.id,
            {
              width: change.dimensions.width,
              height: change.dimensions.height,
            },
            isEnd ? { debounceMs: 200 } : { persist: false },
          );
        }
      }

      // Single event per RF batch — the AlignmentGuides overlay
      // listens and paints the guides.
      window.dispatchEvent(
        new CustomEvent('workspace:alignment-guides', {
          detail: { guides: pendingGuides },
        }),
      );
    },
    [patchItemState, items, flow],
  );

  // RF v12 emits `select` changes through `onEdgesChange` whenever a
  // user clicks an edge (or whenever `resetSelectedElements` runs
  // after a pane click). Without a handler we lose those events
  // entirely — edge selection appears not to work at all.
  //
  // We don't process `remove` or other change kinds here: edge
  // deletion goes through `removeItem` in the workspace store, not
  // through RF's internal lifecycle. Anything that isn't a `select`
  // change is intentionally ignored.
  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    const selectChanges = changes.filter((c) => c.type === 'select');
    if (selectChanges.length === 0) return;
    setSelectedEdgeIds((prev) => {
      let mutated = false;
      const next = new Set(prev);
      for (const c of selectChanges) {
        if (c.type !== 'select') continue;
        if (c.selected) {
          if (!next.has(c.id)) {
            next.add(c.id);
            mutated = true;
          }
        } else if (next.has(c.id)) {
          next.delete(c.id);
          mutated = true;
        }
      }
      return mutated ? next : prev;
    });
  }, []);

  // ---- HTML5 drop from panels → addItem at drop position ----
  //
  // Two flavours of drop land here:
  //
  //   1. Internal panel drag (library / recent / chat / search) —
  //      payload travels on the `application/x-workspace-item`
  //      MIME, decodes to `DragPayload`, and we addItem with the
  //      referenced entity (agent / content / post).
  //
  //   2. OS file drop (Finder, Photos, browser tab, etc.) — the
  //      first image / video in `dataTransfer.files` is uploaded
  //      and dropped as a headerless content widget. This is the
  //      same pipeline `PasteHandler` uses for Cmd+V, so dragging
  //      a screenshot in feels identical to pasting it.
  // Shows the "Drop files to upload" overlay while an OS file drag hovers the canvas.
  const [fileDragOver, setFileDragOver] = useState(false);
  const onDragOver = useCallback((e: React.DragEvent) => {
    const types = e.dataTransfer.types;
    if (
      types.includes('application/x-workspace-item') ||
      types.includes('Files')
    ) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      if (types.includes('Files')) setFileDragOver(true);
    }
  }, []);
  // Hide the overlay only when the pointer truly leaves the canvas (dragleave also
  // fires when crossing INTO a child node — guard with a contains() check).
  const onDragLeave = useCallback((e: React.DragEvent) => {
    const rt = e.relatedTarget;
    if (!(rt instanceof HTMLElement) || !e.currentTarget.contains(rt)) setFileDragOver(false);
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      setFileDragOver(false);
      const point = flow.screenToFlowPosition({
        x: e.clientX,
        y: e.clientY,
      });

      // OS file drop path — upload ANY dropped file(s) (Finder, Photos, a
      // browser download, …) as content node(s): media goes headerless, other
      // files (PDF/code/zip/…) keep a titled node. Multi-file drops cascade so
      // they don't stack on one pixel. Runs before the internal-panel branch so
      // a drag that carries both still spawns the file node(s).
      const files = Array.from(e.dataTransfer.files ?? []);
      if (files.length) {
        e.preventDefault();
        files.forEach((f, i) => {
          void uploadFileAsContentItem(f, { x: point.x + i * 28, y: point.y + i * 28 }, addItem);
        });
        return;
      }

      e.preventDefault();
      const raw = e.dataTransfer.getData('application/x-workspace-item');
      if (!raw) return;
      let payload: DragPayload | null = null;
      try {
        payload = JSON.parse(raw) as DragPayload;
      } catch {
        return;
      }
      if (!payload?.type) return;

      addItem({
        type: payload.type,
        refId: payload.refId ?? null,
        // Cursor-anchored: the user dragged the row to a specific
        // point on the canvas. `placement: 'cursor'` honours that
        // pixel and only nudges off it if it would overlap.
        placement: 'cursor',
        state: {
          x: point.x,
          y: point.y,
          cache: payload.cache,
        },
      }).catch(() => {});
    },
    [flow, addItem],
  );

  // ---- Gesture lock ---------------------------------------------------
  //
  // While ANY canvas gesture is in flight — node drag, pane pan, pinch
  // zoom, custom modifier-wheel zoom — we toggle a `.ws-gesturing`
  // class on the wrapper. The matching CSS rule sets
  // `pointer-events: none` on every widget and its descendants so the
  // pointer (mouse or touch) cannot get "stolen" by an interactive
  // surface inside a widget mid-gesture.
  //
  // Why this is necessary: React Flow's d3-drag installs document-level
  // pointermove / pointerup listeners on gesture start, BUT widget
  // internals (CodeMirror selection, chat textarea drag, content
  // scrollers, iframe surfaces, …) frequently call `stopPropagation`
  // on pointermove / wheel events as soon as the pointer enters them.
  // On a fast drag where the pointer briefly slips off the header into
  // the body, RF stops receiving move events and the gesture drops —
  // the visible symptom is the user reported: "drag stops when mouse
  // leaves the header". Same pattern caused jittery pinch / two-finger
  // pan over widgets.
  //
  // Routing all pointer events to the canvas pane via
  // `pointer-events: none` on nodes is the standard fix. It is safe
  // because the gesture's original target keeps receiving events
  // through the captured pointer; the CSS only affects HIT-testing
  // for new pointer interactions, not in-flight captures.
  const setGesturing = useCallback((on: boolean) => {
    const el = wrapperRef.current;
    if (!el) return;
    el.classList.toggle('ws-gesturing', on);
  }, []);

  // Belt-and-braces release. A stuck `.ws-gesturing` class would
  // disable interaction across every widget on the canvas — a much
  // worse failure than a slightly-jittery gesture. We unconditionally
  // clear the flag on any of the events that signal "the gesture is
  // definitely over": a pointer release anywhere on the window,
  // a window blur (Cmd-Tab away mid-drag), or visibility change.
  // None of these fire during a healthy gesture, so the safety net
  // never interferes with normal operation.
  useEffect(() => {
    const clear = () => setGesturing(false);
    window.addEventListener('pointerup', clear);
    window.addEventListener('pointercancel', clear);
    window.addEventListener('blur', clear);
    document.addEventListener('visibilitychange', clear);
    return () => {
      window.removeEventListener('pointerup', clear);
      window.removeEventListener('pointercancel', clear);
      window.removeEventListener('blur', clear);
      document.removeEventListener('visibilitychange', clear);
    };
  }, [setGesturing]);

  // ---- Click inside a widget IFRAME = bring that app to front ---------
  //
  // NodeFrame's own `onPointerDown` raises the app when the click lands
  // on the header, the frame chrome, or same-document widget content.
  // But most apps render in a sandboxed cross-origin <iframe>, and the
  // parent document never receives pointer events that start inside one
  // — so "click the app body" silently failed to raise it. The one
  // signal the parent DOES get is focus: the browser fires a window
  // `blur` on our document the moment the iframe steals focus, with
  // `document.activeElement` already pointing at that iframe. Walk up
  // to the owning node frame (`data-item-id`, see node-frame.tsx) and
  // raise it. Re-clicking the already-focused iframe fires no blur, but
  // by then the app is already on top, so nothing is lost.
  useEffect(() => {
    const onWindowBlur = () => {
      const ae = document.activeElement;
      if (!(ae instanceof HTMLIFrameElement)) return;
      const host = ae.closest<HTMLElement>('[data-item-id]');
      const itemId = host?.dataset.itemId;
      if (itemId) bringToFront(itemId);
    };
    // Primary signal: the SDK's pointerdown ping relayed by WidgetBridge
    // (`robutler-widget-focus` → this CustomEvent). The blur fallback
    // above/below only fires when focus leaves THIS document — an
    // iframe→iframe click (the overlapping-windows case) never re-blurs
    // the parent, but the ping fires on every click inside any widget.
    const onWidgetFocus = (e: Event) => {
      const itemId = (e as CustomEvent<{ itemId?: string }>).detail?.itemId;
      if (itemId) bringToFront(itemId);
    };
    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('workspace:widget-focus', onWidgetFocus);
    return () => {
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('workspace:widget-focus', onWidgetFocus);
    };
  }, [bringToFront]);

  // Custom wheel-zoom + pinch-zoom session manager. Each modifier
  // wheel event extends a 200 ms idle window during which we keep
  // the gesture lock active. Without this the gesture lock would
  // flicker on/off between consecutive wheel ticks (the OS dispatches
  // ~60 events / second for a single pinch), producing the same
  // jitter we're trying to eliminate.
  const wheelGestureTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const beginWheelGesture = useCallback(() => {
    // Idempotent re-arm: every tick re-asserts `gesturing=true`. If
    // the safety-net cleanup (pointerup / blur / visibilitychange)
    // raced ahead and cleared the class mid-session, the next wheel
    // tick within 200 ms will re-set it. This keeps long pinch
    // sessions stable without growing more state.
    setGesturing(true);
    if (wheelGestureTimer.current) clearTimeout(wheelGestureTimer.current);
    wheelGestureTimer.current = setTimeout(() => {
      wheelGestureTimer.current = null;
      setGesturing(false);
    }, 200);
  }, [setGesturing]);

  // ---- Safari trackpad pinch (proprietary gesture events) -------------
  // Chrome delivers trackpad pinch as ctrl+wheel (handled below); Safari
  // delivers gesturestart/gesturechange with a cumulative `scale` and NO
  // wheel events, so without this hand-mode pinch was simply dead on
  // Safari (Responsive Design Mode included). preventDefault always — the
  // browser must never page-zoom over the canvas — and in hand mode the
  // scale drives the same anchored zoom as touch pinch.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    let startZoom = 1;
    let anchorWorld = { x: 0, y: 0 };
    type SafariGesture = Event & { scale?: number; clientX?: number; clientY?: number };
    const onStart = (ev: Event) => {
      const e = ev as SafariGesture;
      e.preventDefault();
      startZoom = flow.getZoom();
      anchorWorld = flow.screenToFlowPosition({
        x: e.clientX ?? 0,
        y: e.clientY ?? 0,
      });
    };
    const onChange = (ev: Event) => {
      const e = ev as SafariGesture;
      e.preventDefault();
      if (!WORKSPACE_ZOOM_ENABLED && !navigateModeRef.current) return;
      const scale = typeof e.scale === 'number' && e.scale > 0 ? e.scale : 1;
      const next = Math.min(3, Math.max(dynMinZoomRef.current, startZoom * scale));
      const rect = el.getBoundingClientRect();
      const cx = (e.clientX ?? 0) - rect.left;
      const cy = (e.clientY ?? 0) - rect.top;
      flow.setViewport(
        { x: cx - anchorWorld.x * next, y: cy - anchorWorld.y * next, zoom: next },
        { duration: 0 },
      );
    };
    const opts = { passive: false } as AddEventListenerOptions;
    el.addEventListener('gesturestart', onStart, opts);
    el.addEventListener('gesturechange', onChange, opts);
    return () => {
      el.removeEventListener('gesturestart', onStart, opts);
      el.removeEventListener('gesturechange', onChange, opts);
    };
  }, [flow]);

  // ---- In-app two-finger gestures, forwarded -------------------------
  // Touches that START inside a cross-origin app iframe never reach this
  // document, so the widget-doc containment script measures the pinch
  // inside the app and relays it (through the sandbox shell's relay).
  //
  // EVERY per-move value must be TRANSFORM-FREE, or the gesture feeds on
  // itself: content coordinates rescale under the zoom we apply, so both a
  // content-space midpoint AND a content-space distance ratio fed a
  // fraction of each applied zoom back into the next reading -- the
  // oscillation seen on real phones (canvas-started pinches were immune:
  // their fingers are measured in stable glass coordinates). The script
  // therefore reports SCREEN-coordinate scale and midpoint deltas, which
  // CSS transforms never touch. Content values arrive exactly once, at
  // start, for the world anchor (content px = world px + node origin, no
  // DOM measurement) and a screen/css calibration ratio. Moves are
  // rAF-coalesced.
  useEffect(() => {
    let session: {
      startZoom: number;
      anchorWorld: { x: number; y: number };
      glassStart: { x: number; y: number };
      /** Screen px per canvas css px, calibrated at start. */
      dprF: number;
    } | null = null;
    let pendingMove: { scale: number; dx: number; dy: number } | null = null;
    let raf = 0;
    const applyPending = () => {
      raf = 0;
      const m = pendingMove;
      pendingMove = null;
      if (!m || !session) return;
      const next = Math.min(
        3,
        Math.max(
          dynMinZoomRef.current,
          session.startZoom * (m.scale > 0 ? m.scale : 1),
        ),
      );
      const glassX = session.glassStart.x + m.dx / session.dprF;
      const glassY = session.glassStart.y + m.dy / session.dprF;
      flow.setViewport(
        {
          x: glassX - session.anchorWorld.x * next,
          y: glassY - session.anchorWorld.y * next,
          zoom: next,
        },
        { duration: 0 },
      );
    };
    const onMsg = (e: MessageEvent) => {
      const d = e.data as {
        source?: string;
        phase?: string;
        scale?: number;
        cx?: number;
        cy?: number;
        dc?: number;
        ds?: number;
        dx?: number;
        dy?: number;
      } | null;
      if (!d || d.source !== 'robutler-host-pinch') return;
      const el = wrapperRef.current;
      if (!el) return;
      if (d.phase === 'end') {
        if (session) {
          session = null;
          pendingMove = null;
          setGesturing(false);
        }
        return;
      }
      if (
        !WORKSPACE_ZOOM_ENABLED &&
        !navigateModeRef.current &&
        !isCoarseRef.current
      ) {
        return;
      }
      if (d.phase === 'start') {
        // Pinch-destination toggle (zoom menu): while on, an in-app pinch
        // belongs to the APP's own zoom — no session, the camera holds
        // still. The app receives the touches either way; this only stops
        // the workspace from acting on them too.
        if (isAppPinchOn()) return;
        const frame = [...el.querySelectorAll('iframe')].find(
          (f) => (f as HTMLIFrameElement).contentWindow === e.source,
        ) as HTMLIFrameElement | undefined;
        const nodeEl = frame?.closest('.react-flow__node');
        const nodeId = nodeEl?.getAttribute('data-id');
        const item = nodeId
          ? screensItemsRef.current.find((x) => x.id === nodeId)
          : null;
        if (!item) return;
        const vp = flow.getViewport();
        const anchorWorld = {
          x: item.state.x + (d.cx ?? 0),
          y: item.state.y + (d.cy ?? 0),
        };
        // Screen px per canvas css px: the app measured the same finger
        // spread in both spaces at start; content spread x zoom = glass
        // spread. Self-calibrating (covers DPR and any UA screen-unit
        // quirk); errors here are a constant factor on PAN SPEED only,
        // never a feedback term.
        const dc = d.dc && d.dc > 0 ? d.dc : 1;
        const ds = d.ds && d.ds > 0 ? d.ds : 1;
        const dprF = Math.max(0.05, ds / (dc * vp.zoom));
        session = {
          startZoom: vp.zoom,
          anchorWorld,
          glassStart: {
            x: anchorWorld.x * vp.zoom + vp.x,
            y: anchorWorld.y * vp.zoom + vp.y,
          },
          dprF,
        };
        setGesturing(true);
        return;
      }
      if (d.phase !== 'move' || !session) return;
      pendingMove = {
        scale: typeof d.scale === 'number' ? d.scale : 1,
        dx: d.dx ?? 0,
        dy: d.dy ?? 0,
      };
      if (!raf) raf = window.requestAnimationFrame(applyPending);
    };
    window.addEventListener('message', onMsg);
    return () => {
      window.removeEventListener('message', onMsg);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [flow, setGesturing]);

  // ---- Cmd / Ctrl + wheel = canvas zoom even over widgets ----
  // Capture-phase listener intercepts modifier-wheel events before any
  // inner `nowheel` element can swallow them. Cursor-anchored zoom uses
  // React Flow's own helpers so the focal point stays under the pointer.
  // This is also what makes trackpad pinch (which fires `wheel` with
  // `ctrlKey: true`) reach React Flow when the cursor is over a widget.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      // Scroll ownership (F2) + the TOTAL navigation lock (Part X):
      //   - wheel over ANY window belongs to the app, unconditionally;
      //   - wheel over the empty pane does NOTHING — the desktop never
      //     moves on scroll. (Wheel paging retired: with several apps
      //     on one screen the exposed pane made scrolls rubber-band the
      //     camera or flip screens mid-read. Screens change via the
      //     chords, dots, edge gestures, menu, and the stage view.)
      if (!(e.ctrlKey || e.metaKey)) {
        if (overviewRef.current) return; // overview: fixed map
        if (
          e.target instanceof Element &&
          e.target.closest('.react-flow__node')
        ) {
          e.stopPropagation();
          return;
        }
        if (!(e.target instanceof Element)) return;
        if (!e.target.closest('.react-flow__pane')) return;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      e.preventDefault();
      // Canvas zoom is disabled — still swallow the modifier-wheel so the
      // BROWSER page doesn't zoom, but don't move the viewport. (Over a
      // cross-origin widget the parent never sees this event, so the app's
      // own in-app zoom keeps working.) stopPropagation matters as much as
      // preventDefault here: React Flow's d3-zoom keeps its own wheel
      // listener alive for `panOnScroll`, and its default
      // `zoomActivationKeyCode` ('Meta') would happily turn Cmd+scroll
      // into a canvas zoom if the event reached the pane. We also pass
      // `zoomActivationKeyCode={null}` on the ReactFlow element below —
      // layered defense, since a descendant that re-dispatches wheel
      // events could bypass this capture listener.
      // Hand mode is the exception here exactly as for touch pinch: the
      // FAB now exists on narrow desktop windows too, and Chrome trackpad
      // pinch arrives as ctrl+wheel.
      if (!WORKSPACE_ZOOM_ENABLED && !navigateModeRef.current) {
        e.stopPropagation();
        return;
      }
      // Keep the gesture lock alive for the duration of the pinch /
      // wheel-zoom session so the pointer can't slip onto a widget
      // mid-gesture and lose the next tick.
      beginWheelGesture();
      // Softer zoom curve. The previous 0.006 coefficient made a
      // single trackpad pinch jump entire zoom steps, which made
      // pixel-precise framing of widgets feel jumpy. 0.003 keeps the
      // gesture responsive while halving the per-tick magnitude.
      const factor = Math.exp(-e.deltaY * 0.003);
      const current = flow.getZoom();
      const next = Math.min(3, Math.max(dynMinZoomRef.current, current * factor));
      const point = flow.screenToFlowPosition({ x: e.clientX, y: e.clientY });
      const rect = el.getBoundingClientRect();
      const cursorX = e.clientX - rect.left;
      const cursorY = e.clientY - rect.top;
      // Cursor-anchored zoom: the world point under the cursor stays
      // under the cursor as the zoom changes.
      let newX = cursorX - point.x * next;
      let newY = cursorY - point.y * next;
      // Pan-while-zoom: diagonal pinches on a macOS trackpad fire
      // `wheel` with `ctrlKey:true` AND a non-zero `deltaX` (and a
      // residual `deltaY` component that the OS attributes to gesture
      // translation rather than scale). Applying those deltas as
      // screen-space viewport offsets means the user can keep
      // pinching while moving the gesture's anchor point — the
      // viewport tracks the fingers instead of locking to the
      // pinch-start coordinate. We only consume `deltaX` because
      // `deltaY` is fully spent on the zoom factor; using it twice
      // would double-count the gesture and feel jittery. Inverted
      // sign matches RF's `panOnScroll` direction so two-finger
      // gestures behave the same whether or not Ctrl is held.
      if (e.deltaX !== 0) {
        newX -= e.deltaX;
      }
      flow.setViewport({ x: newX, y: newY, zoom: next }, { duration: 0 });
    };
    el.addEventListener('wheel', onWheel, { capture: true, passive: false });
    return () => el.removeEventListener('wheel', onWheel, { capture: true } as EventListenerOptions);
  }, [flow, beginWheelGesture]);

  // ---- Touchscreen 2-finger pinch + pan ----
  //
  // On macOS trackpad we're stuck with the OS-synthesised wheel stream
  // — pinch reports scale via `deltaY` but never reports the gesture's
  // translation. On real touchscreens (iPad Safari, Surface, Android
  // tablets) the browser gives us the actual finger positions through
  // `TouchEvent`, so we can compute BOTH scale AND translation in the
  // same frame and apply them to the viewport together. This is what
  // makes a 2-finger gesture feel "physical": pinch in, pinch out, and
  // slide-while-pinching all map to one continuous viewport update.
  //
  // This is the SOLE touch pinch+pan path on every device (RF's own
  // `zoomOnPinch` stays false). We deliberately do NOT use RF's native
  // pinch: d3-zoom listens on the pane, so any widget whose content
  // handles/stops the touch (chat lists, editors, scrollers, …) eats the
  // 2-finger gesture before it reaches the pane — "pinch works over the
  // empty canvas but some widgets consume it". This handler instead
  // listens on the WRAPPER in the CAPTURE phase, so it sees the 2-finger
  // gesture BEFORE any same-document widget descendant can, then
  // `stopPropagation`s so neither the widget NOR d3-zoom's single-finger
  // pan can hijack it. Result: 2-finger pinch+pan works over the pane and
  // over any (non-cross-origin-iframe) widget. Single-finger touches are
  // left to propagate so RF still owns one-finger pan.
  //
  // (Cross-origin widget iframes are the one exception — the browser
  // routes touches that start inside them to the iframe's own document,
  // so the parent never sees the gesture to intercept it. That's the
  // forthcoming per-widget "consume pinch" setting's territory.)
  //
  // Native `addEventListener` (instead of React's synthetic events) is
  // required because we MUST call `preventDefault` on touchmove to
  // suppress Safari's page-level pinch-zoom; React attaches passive
  // listeners for touchmove and `preventDefault` is a silent no-op
  // there.
  const touchGesture = useRef<{
    // World-space anchor: the world coordinate under the gesture's
    // initial midpoint. We keep this anchor stable under both scale
    // and translation by recomputing the viewport every move so that
    // the same world point lands at the CURRENT screen midpoint.
    anchorWorld: { x: number; y: number };
    // Initial finger distance — divides into the current distance to
    // give a frame-local scale ratio.
    startDist: number;
    // Initial zoom — the new zoom is `startZoom * (currDist / startDist)`,
    // clamped to React Flow's [0.1, 3] range.
    startZoom: number;
  } | null>(null);
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const midpoint = (a: Touch, b: Touch) => ({
      x: (a.clientX + b.clientX) / 2,
      y: (a.clientY + b.clientY) / 2,
    });
    const distance = (a: Touch, b: Touch) => {
      const dx = a.clientX - b.clientX;
      const dy = a.clientY - b.clientY;
      return Math.hypot(dx, dy);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 2) return;
      // 2nd finger landed mid-gesture: cancel any pending long-press
      // armed by the single-finger handler so we don't open a context
      // menu on top of the pinch.
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
      longPressStart.current = null;

      const [a, b] = [e.touches[0], e.touches[1]];
      const mid = midpoint(a, b);
      const rect = el.getBoundingClientRect();
      // Snapshot the world point under the gesture's centre. We hold
      // this constant across the gesture: every subsequent touchmove
      // pins this world point at the CURRENT screen midpoint, which
      // is what makes pan-while-pinching feel natural.
      const anchorWorld = flow.screenToFlowPosition({ x: mid.x, y: mid.y });
      void rect; // rect not needed beyond screenToFlowPosition
      touchGesture.current = {
        anchorWorld,
        startDist: distance(a, b),
        startZoom: flow.getZoom(),
      };
      setGesturing(true);
      // stopPropagation keeps d3-zoom (pane listener) from turning the
      // 2-finger touch into a single-finger pan; preventDefault suppresses
      // the browser's own page pinch-zoom. Both are why this listener runs
      // in the capture phase — see the block comment above.
      e.stopPropagation();
      e.preventDefault();
    };

    const onTouchMove = (e: TouchEvent) => {
      const g = touchGesture.current;
      if (!g) return;
      if (e.touches.length !== 2) return;
      // Same as touchstart: keep d3-zoom out of our 2-finger gesture.
      e.stopPropagation();
      e.preventDefault();

      const [a, b] = [e.touches[0], e.touches[1]];
      const mid = midpoint(a, b);
      const dist = distance(a, b);
      if (dist === 0 || g.startDist === 0) return;

      // TOTAL navigation lock (Part X): with canvas zoom disabled the
      // 2-finger gesture moves NOTHING — the old fallback turned it
      // into a free pan, which was exactly the "touch moves the
      // canvas" leak. The gesture is still consumed (preventDefault /
      // stopPropagation above) so the browser's page pinch-zoom and
      // d3's pan never see it.
      //
      // HAND MODE is the deliberate exception: it exists to look around,
      // and on a phone the desktop-sized windows land width-fit — far too
      // small to read — so pinch-to-zoom (with the combined pan below) is
      // the whole point of the mode. The lock stays total everywhere else.
      // Two-finger nav is MODELESS on touch (user call): a pinch can only
      // START outside the apps (touches inside a cross-origin iframe never
      // reach this document), so it is unambiguous -- one finger belongs to
      // the app under it, two fingers navigate. Hand mode (dormant, dev
      // hook only) and the future desktop zoom keep their gates.
      if (
        !WORKSPACE_ZOOM_ENABLED &&
        !navigateModeRef.current &&
        !isCoarseRef.current
      )
        return;
      // Combined zoom + pan: solve for a viewport where
      //   screenMidpoint = viewport.{x,y} + anchorWorld * zoom
      // i.e. the world anchor stays under the gesture's current
      // midpoint with the new scale. This single equation handles
      // pinch, pan, AND pinch-while-panning in one update.
      const nextZoom = Math.min(
        3,
        Math.max(dynMinZoomRef.current, g.startZoom * (dist / g.startDist)),
      );
      const rect = el.getBoundingClientRect();
      const cx = mid.x - rect.left;
      const cy = mid.y - rect.top;
      const newX = cx - g.anchorWorld.x * nextZoom;
      const newY = cy - g.anchorWorld.y * nextZoom;
      flow.setViewport({ x: newX, y: newY, zoom: nextZoom }, { duration: 0 });
    };

    const onTouchEnd = (e: TouchEvent) => {
      // End the gesture as soon as ONE finger leaves — promoting a
      // 1-finger continuation would silently flip the user into a
      // pan that they didn't intend. The single-finger long-press /
      // RF pan paths take over from here.
      if (touchGesture.current && e.touches.length < 2) {
        touchGesture.current = null;
        setGesturing(false);
        // NO settle on release (user call): the zoom-out LIMIT is the
        // dynamic minZoom; snapping the camera to a framed value after a
        // deliberate pinch read as the zoom "jumping". Where you release
        // is where you stay.
      }
    };

    // Capture phase so these run BEFORE d3-zoom's pane listeners.
    // touchend / touchcancel stay in the bubble phase (and never
    // stopPropagation) so d3-zoom still receives them and cleans up any
    // pan it began on the first finger before the second landed —
    // otherwise its internal gesture state would stick.
    el.addEventListener('touchstart', onTouchStart, { capture: true, passive: false });
    el.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    return () => {
      el.removeEventListener('touchstart', onTouchStart, { capture: true } as EventListenerOptions);
      el.removeEventListener('touchmove', onTouchMove, { capture: true } as EventListenerOptions);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [flow, setGesturing]);

  // ---- Esc cancels armed annotation tool ----
  // Single keypress dropdown — if the user picked a tool from the
  // toolbar and then changed their mind, Esc returns to the default
  // pointer mode. Connector mode included so a half-drawn connector
  // can be abandoned cleanly.
  useEffect(() => {
    if (!pendingAnnotation) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setPendingAnnotation(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pendingAnnotation, setPendingAnnotation]);

  // ---- crosshair cursor while a tool is armed ----
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    el.classList.toggle('ws-canvas-tool', !!pendingAnnotation);
  }, [pendingAnnotation]);

  // ---- click-drag-to-draw for annotation tools ----
  //
  // Figma-style draw gesture. While a non-connector tool is armed, the
  // user can:
  //   - Click (no drag)         → drop with default size at the click.
  //   - Click + drag rectangle  → drop sized to the dragged bbox.
  //
  // Default RF `panOnDrag` would steal pointer-down on the pane and
  // start a workspace pan, so we ALSO toggle `panOnDrag` off below
  // when a non-connector tool is armed (the connector tool keeps the
  // default behaviour because it relies on handle drags, not pane
  // drags). With panning suppressed, the pane is free for our own
  // pointer tracking. We attach listeners on the wrapper rather than
  // RF callbacks so we can update the preview overlay on every move
  // without churning through ReactFlow's own change pipeline.
  const [draftRect, setDraftRect] = useState<
    | { startX: number; startY: number; endX: number; endY: number }
    | null
  >(null);
  useEffect(() => {
    if (!pendingAnnotation) return;
    if (pendingAnnotation.kind === 'connector') return;
    const el = wrapperRef.current;
    if (!el) return;

    let start: { x: number; y: number; pointerId: number } | null = null;

    const isPaneTarget = (target: EventTarget | null) => {
      if (!(target instanceof Element)) return false;
      // Drawing should fire over the empty canvas only. Nodes, handles,
      // edges and any panel chrome must keep their own gestures.
      if (target.closest('.react-flow__node')) return false;
      if (target.closest('.react-flow__edge')) return false;
      if (target.closest('.react-flow__handle')) return false;
      if (target.closest('.react-flow__controls')) return false;
      // The actual pane surface either is or is inside `.react-flow__pane`.
      return !!target.closest('.react-flow__pane');
    };

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      if (!isPaneTarget(e.target)) return;
      // We want our gesture, not RF's pan. Even though `panOnDrag` is
      // off while a tool is armed we still preventDefault here so the
      // browser doesn't start a text-selection drag on the SVG layer.
      e.preventDefault();
      e.stopPropagation();
      start = { x: e.clientX, y: e.clientY, pointerId: e.pointerId };
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        // setPointerCapture can throw if the target was detached
        // between the event firing and the call. Fail-soft — the
        // window-level move listener still works without capture.
      }
      setDraftRect({
        startX: e.clientX,
        startY: e.clientY,
        endX: e.clientX,
        endY: e.clientY,
      });
    };

    const onMove = (e: PointerEvent) => {
      if (!start) return;
      setDraftRect({
        startX: start.x,
        startY: start.y,
        endX: e.clientX,
        endY: e.clientY,
      });
    };

    const onUp = (e: PointerEvent) => {
      if (!start) return;
      const startScreen = { x: start.x, y: start.y };
      const endScreen = { x: e.clientX, y: e.clientY };
      try {
        el.releasePointerCapture(start.pointerId);
      } catch {
        // ignore
      }
      start = null;
      setDraftRect(null);

      // Convert both screen corners to flow space — we can't just
      // translate width/height because zoom level matters.
      const a = flow.screenToFlowPosition({ x: startScreen.x, y: startScreen.y });
      const b = flow.screenToFlowPosition({ x: endScreen.x, y: endScreen.y });
      const x = Math.min(a.x, b.x);
      const y = Math.min(a.y, b.y);
      const drawnW = Math.abs(b.x - a.x);
      const drawnH = Math.abs(b.y - a.y);

      const { kind } = pendingAnnotation;
      const color = pendingAnnotation.color ?? 'yellow';
      const shape = pendingAnnotation.shape ?? 'rectangle';
      const baseSize =
        kind === 'sticky'
          ? { width: 192, height: 144 }
          : kind === 'text'
            ? { width: 192, height: 48 }
            : { width: 192, height: 144 };

      // Threshold below which we treat the gesture as a "click" — drop
      // at the click point with the default size. 8px in flow space
      // catches any accidental jitter while reading as a discrete tap.
      const CLICK_THRESHOLD = 8;
      let finalX: number;
      let finalY: number;
      let finalW: number;
      let finalH: number;
      if (drawnW < CLICK_THRESHOLD && drawnH < CLICK_THRESHOLD) {
        finalW = baseSize.width;
        finalH = baseSize.height;
        finalX = a.x - finalW / 2;
        finalY = a.y - finalH / 2;
      } else {
        // Floor at a usable minimum so micro-drags still get a readable
        // shape (rather than a 12×8px sliver).
        finalW = Math.max(drawnW, 48);
        finalH = Math.max(drawnH, 32);
        finalX = x;
        finalY = y;
      }

      void addItem({
        type: 'annotation',
        refId: null,
        state: {
          x: finalX,
          y: finalY,
          width: finalW,
          height: finalH,
          cache: {
            kind,
            text: '',
            color,
            ...(kind === 'shape'
              ? { shape, strokeStyle: 'solid' as const }
              : {}),
          },
        },
      });
      setPendingAnnotation(null);
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      setDraftRect(null);
    };
  }, [pendingAnnotation, flow, addItem, setPendingAnnotation]);

  // ---- Cmd (Mac) / Alt (Win+Linux) held = canvas pan surface ----
  //
  // By default React Flow puts `cursor: grab` on every draggable node
  // (we restrict drag to the header via `dragHandle`, but the cursor
  // hint stays). The body of an editor or chat widget is then visually
  // claimed by the canvas, even though clicking it interacts with the
  // widget. We override the cursor in CSS (`.react-flow__node.draggable
  // { cursor: default }`) so the widget's own cursor wins; but we ALSO
  // want the user to be able to pan/zoom the canvas with a modifier
  // held even while the pointer is over a widget.
  //
  // Modifier mapping:
  //   - Mac: Cmd (Meta). Option/Alt also works for parity with the
  //     Win/Linux key — Mac users who reach for the right-of-spacebar
  //     modifier still get the same gesture.
  //   - Windows/Linux: Alt. We deliberately do NOT bind Ctrl because
  //     Ctrl+click is already platform shorthand for context-menu /
  //     selection on those OSes and we'd hijack it.
  //
  // Strategy: toggle a `.ws-canvas-pan` class on the wrapper when the
  // modifier is held. The CSS rule then sets `pointer-events: none`
  // on every node so mousedown falls through to the pane behind, which
  // already has its own `cursor: grab` from React Flow's base.css. The
  // capture-phase wheel listener above keeps Cmd/Ctrl+wheel zoom
  // working independently of this flag (Ctrl is preserved there
  // because trackpad pinch zoom is delivered as `wheel { ctrlKey: true }`
  // by every browser on every OS).
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const setPan = (active: boolean) => {
      el.classList.toggle('ws-canvas-pan', active);
    };
    const isModKey = (e: KeyboardEvent) =>
      e.key === 'Meta' || e.key === 'Alt';
    const onKeyDown = (e: KeyboardEvent) => {
      if (isModKey(e)) setPan(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (isModKey(e)) setPan(false);
    };
    // CRITICAL: a missed `keyup` (Cmd+Tab, DevTools focus steal, system
    // dialog, etc.) used to leave `.ws-canvas-pan` stuck on, which sets
    // `pointer-events: none !important` on every node — that breaks
    // context menu, selection, double-click-to-edit, EVERYTHING that
    // needs a click on a widget. Sync the class with the actual
    // modifier state on every pointer event we receive, so the next
    // mousemove self-corrects no matter what the keyboard pipeline
    // dropped.
    const onPointerSync = (e: PointerEvent | MouseEvent) => {
      setPan(!!(e.metaKey || e.altKey));
    };
    const onBlur = () => setPan(false);
    const onVisibility = () => {
      if (document.hidden) setPan(false);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pointermove', onPointerSync);
    window.addEventListener('pointerdown', onPointerSync);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pointermove', onPointerSync);
      window.removeEventListener('pointerdown', onPointerSync);
    };
  }, []);

  // Native contextmenu listener — the last-resort fallback. React's
  // synthetic onContextMenu chain occasionally drops events when a
  // descendant calls `stopPropagation()` on the synthetic event (this
  // is how React Flow's `onNodeContextMenu` short-circuits the
  // wrapper handler above), and a few embedded surfaces (CodeMirror,
  // chat scrollers, contenteditable annotations) install their own
  // contextmenu handlers that swallow the synthetic too. Binding a
  // native listener on the wrapper in CAPTURE phase guarantees we
  // see every right-click before any descendant can intercept it.
  //
  // We still call `preventDefault` to suppress the browser's
  // built-in menu and then route to node / edge / pane the same way
  // the React onContextMenu fallback does. If RF already opened the
  // menu via its own callback, `openNodeMenu` is a no-op replace
  // because React.useState bails on identical updates.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const onCtx = (e: MouseEvent) => {
      e.preventDefault();
      if (pendingAnnotation) setPendingAnnotation(null);
      const target = e.target as Element | null;
      const nodeEl = target?.closest('.react-flow__node') as HTMLElement | null;
      if (nodeEl) {
        const nodeId = nodeEl.getAttribute('data-id');
        if (nodeId) {
          openNodeMenu(nodeId, e.clientX, e.clientY);
          return;
        }
      }
      const edgeEl = target?.closest('.react-flow__edge') as HTMLElement | null;
      if (edgeEl) {
        const edgeId =
          edgeEl.getAttribute('data-id') ?? edgeEl.id ?? null;
        if (edgeId) {
          openNodeMenu(edgeId, e.clientX, e.clientY);
          return;
        }
      }
      openPaneMenu({ clientX: e.clientX, clientY: e.clientY });
    };
    // Capture phase + bound on the wrapper element (not window) so it
    // only triggers for contextmenu events INSIDE the canvas. The
    // surrounding chrome (panels, header dropdowns, dialogs) keep
    // their own native menus.
    el.addEventListener('contextmenu', onCtx, true);
    return () => el.removeEventListener('contextmenu', onCtx, true);
  }, [openNodeMenu, openPaneMenu, pendingAnnotation, setPendingAnnotation]);

  // Cmd-click / Ctrl-click → canvas context menu, bypassing in-
  // widget click handlers. Mirrors the Cmd-wheel zoom pattern in
  // the same file (~line 1376) which bypasses iframe `nowheel`
  // via a capture-phase wheel listener: we sit one layer above
  // any handler a widget body might install and route the click
  // straight to `openNodeMenu` / `openPaneMenu` before the widget
  // sees the event.
  //
  // Why this is useful:
  //   - Widgets like Pyodide, calculators, snake / invaders, the
  //     Markdown editor, and chat composers eagerly consume left
  //     clicks for in-body interactions. There's no other way to
  //     pop the canvas context menu over them on a trackpad
  //     without two fingers (no right-click) or a long-press.
  //   - Cmd-click reads as the natural left-handed sibling of
  //     right-click (matching the macOS finder shortcut) and
  //     pairs nicely with the existing Cmd-wheel + Cmd-drag
  //     gestures: Cmd is the universal "override the widget,
  //     talk to the canvas" modifier.
  //
  // Platform notes:
  //   - Cmd-click on Mac, Ctrl-click on Win/Linux. The pan
  //     handler at workspace-canvas.tsx:~1748 deliberately binds
  //     Alt (not Ctrl) on Win/Linux to avoid clashing with the
  //     Ctrl+click context-menu shortcut, so we're consistent
  //     with that convention by treating Ctrl+click as a Mac
  //     Cmd+click equivalent on Win/Linux.
  //   - We only act on `button === 0` (primary). Middle-click and
  //     right-click already have their own paths and shouldn't be
  //     intercepted.
  //   - We do NOT call `e.preventDefault()` for left clicks on
  //     desktop: that would suppress the platform focus + drag
  //     start that React Flow / the resize edges still want. We
  //     only `stopPropagation()` + `stopImmediatePropagation()`
  //     so descendant click handlers don't fire alongside the
  //     menu — the browser still treats it as a regular click for
  //     focus purposes, which is what we want.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      // Don't hijack additive-selection Cmd-clicks. Multi-select
      // already uses Cmd/Ctrl on the React Flow pane / nodes, so
      // we treat Shift-held Cmd-click as "additive selection
      // intent" and leave the event alone for RF to handle.
      if (e.shiftKey) return;
      const target = e.target as Element | null;
      // Skip events that originated in canvas chrome (panels,
      // FAB, search popover, dock, dialogs). Those have their
      // own menus and don't sit on the canvas pane.
      if (target?.closest('[data-canvas-chrome]')) return;
      e.stopPropagation();
      e.stopImmediatePropagation();
      const nodeEl = target?.closest('.react-flow__node') as HTMLElement | null;
      if (nodeEl) {
        const nodeId = nodeEl.getAttribute('data-id');
        if (nodeId) {
          openNodeMenu(nodeId, e.clientX, e.clientY);
          return;
        }
      }
      const edgeEl = target?.closest('.react-flow__edge') as HTMLElement | null;
      if (edgeEl) {
        const edgeId =
          edgeEl.getAttribute('data-id') ?? edgeEl.id ?? null;
        if (edgeId) {
          openNodeMenu(edgeId, e.clientX, e.clientY);
          return;
        }
      }
      openPaneMenu({ clientX: e.clientX, clientY: e.clientY });
    };
    el.addEventListener('pointerdown', onPointerDown, true);
    return () =>
      el.removeEventListener('pointerdown', onPointerDown, true);
  }, [openNodeMenu, openPaneMenu]);

  // Belt-and-braces pane-click deselect. React Flow's `onPaneClick`
  // covers the common case, but it can be eaten by a half-armed
  // annotation tool, a synthetic event that `stopPropagation`-ed
  // mid-bubble, or a paneClickDistance check that interpreted
  // finger jitter as a drag.
  //
  // The native `pointerdown` listener below runs in the capture
  // phase, so it fires BEFORE any of those handlers can swallow
  // the event. When the target sits on the canvas background
  // (i.e. not inside a node, edge, toolbar, or popover) we drop
  // selection by clearing the controlled `selectedNodeIds` /
  // `selectedEdgeIds` sets — same path `onPaneClick` uses, so the
  // memos rebuild every node/edge with `selected: false` and the
  // floating toolbars unmount.
  //
  // The check is target-based, not coordinate-based, so a tap on
  // an annotation that happens to overlap a toolbar's empty
  // pass-through area still selects the annotation rather than
  // accidentally clearing it.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      // Alt-held clicks belong to pick-through (`use-pick-through.ts`)
      // OR to Alt-drag pan. Both intentionally happen on the pane
      // surface because the global CSS rule `.ws-canvas-pan
      // .react-flow__node` sets pointer-events: none while Alt is
      // held — that routes the pointer to the pane. Clearing the
      // selection here would race with pick-through (which selects
      // the next item under the cursor on pointerup) and visibly
      // drop the selection mid-cycle. Skip entirely while Alt is
      // down; the pick-through hook owns this gesture.
      if (e.altKey) return;
      // Cmd / Ctrl: belongs to the canvas Cmd-click context menu
      // override (see the `Cmd-click → context menu` handler above).
      // Clearing selection underneath the menu it's about to open
      // is just visual noise.
      if (e.metaKey || e.ctrlKey) return;
      const target = e.target as Element | null;
      if (!target) return;
      if (target.closest('.react-flow__node')) return;
      if (target.closest('.react-flow__edge')) return;
      if (target.closest('[role="menu"]')) return;
      if (target.closest('.react-flow__node-toolbar')) return;
      if (target.closest('.react-flow__edge-toolbar')) return;
      // EdgeLabelRenderer hosts the floating connector toolbar AND
      // the per-edge waypoint handles (filled endpoints, draggable
      // waypoints, hollow "add waypoint" midpoints). Treating it
      // as pane would clear the selection on capture-phase, unmount
      // the WaypointLayer, and silently swallow the user's left-
      // click on a handle BEFORE its `onPointerDown` could fire —
      // which is exactly the "waypoints aren't draggable" symptom
      // the user reported.
      if (target.closest('.react-flow__edgelabel-renderer')) return;
      // The unified selection floating toolbar isn't inside an
      // RF-managed node-toolbar / edge-toolbar wrapper — it lives
      // directly under the canvas wrapper. Without this guard a
      // click on the More / Align / Delete buttons would clear
      // the selection mid-action and the command would fire against
      // an empty selection.
      if (target.closest('[data-selection-toolbar]')) return;
      // React Flow's group-drag overlay (`.react-flow__nodesselection`)
      // sits inside the pane, so a `closest('.react-flow__pane')`
      // walk would otherwise treat it as a pane click and clear the
      // multi-selection in the capture phase — BEFORE the overlay's
      // own d3-drag can pick the gesture up. The visible symptom is
      // exactly what the user reported: marquee a group, mousedown
      // on the bbox to drag, selection vanishes and the canvas pans
      // instead of moving the widgets. Excluding the overlay (both
      // the outer container and the inner rect) lets the click reach
      // its drag handler, which then group-drags every selected node.
      if (target.closest('.react-flow__nodesselection')) return;
      const onPane =
        !!target.closest('.react-flow__pane') ||
        target === el ||
        target.classList.contains('workspace-canvas');
      if (!onPane) return;
      setSelectedNodeIds((prev) => (prev.size === 0 ? prev : new Set()));
      setSelectedEdgeIds((prev) => (prev.size === 0 ? prev : new Set()));
    };
    el.addEventListener('pointerdown', onDown, true);
    return () => el.removeEventListener('pointerdown', onDown, true);
  }, []);

  // Figma-style "click outside the group cancels the multi-selection".
  //
  // Once a multi-selection is active, ANY click that isn't a drag should
  // dismiss the group — including a click on the group's own bbox overlay
  // (the user's intent there is "I'm done with this selection", not
  // "drag this group 0px"). The only escape hatch is an additive modifier
  // (Shift / Meta / Ctrl), which preserves the standard add-to-selection
  // and toggle semantics that the rest of the canvas relies on.
  //
  // Why a paired down/up listener instead of `onClick`:
  //   - We have to distinguish a *click* from a *drag* by comparing the
  //     pointerdown→pointerup distance. RF's d3-drag overlay swallows
  //     React `onClick` on the bbox once a drag starts, so we'd never
  //     see the synthetic event for the cancel-on-click case anyway.
  //   - Native `pointerdown`/`pointerup` on the wrapper fires for every
  //     interaction inside the canvas, whether the target is the pane,
  //     a widget body, an annotation, or the `NodesSelection` rect.
  //
  // The effect is gated on `selectedNodeIds.size > 1` so single-selection
  // click semantics (focus a widget, open its toolbar, etc.) are
  // completely untouched. We also bail for UI chrome that owns its own
  // selection lifecycle (`role="toolbar"`, popovers, RF toolbars,
  // edgelabel renderer) so a click on, say, the `SelectionToolbar`'s
  // "Fit to selection" button doesn't immediately destroy the very
  // selection it's acting on.
  useEffect(() => {
    if (selectedNodeIds.size <= 1) return;
    const el = wrapperRef.current;
    if (!el) return;
    const CLICK_DISTANCE = 4; // matches RF's paneClickDistance
    let down: { x: number; y: number } | null = null;
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      down = { x: e.clientX, y: e.clientY };
    };
    const onUp = (e: PointerEvent) => {
      if (!down) return;
      const moved = Math.hypot(e.clientX - down.x, e.clientY - down.y);
      down = null;
      if (moved >= CLICK_DISTANCE) return; // user dragged, not clicked
      if (e.shiftKey || e.metaKey || e.ctrlKey) return; // additive intent
      const target = e.target as Element | null;
      if (!target) return;
      if (target.closest('[role="toolbar"]')) return;
      if (target.closest('[role="menu"]')) return;
      if (target.closest('[role="menuitem"]')) return;
      if (target.closest('[role="dialog"]')) return;
      if (target.closest('.react-flow__node-toolbar')) return;
      if (target.closest('.react-flow__edge-toolbar')) return;
      if (target.closest('.react-flow__edgelabel-renderer')) return;
      setSelectedNodeIds((prev) => (prev.size === 0 ? prev : new Set()));
      setSelectedEdgeIds((prev) => (prev.size === 0 ? prev : new Set()));
    };
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointerup', onUp);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointerup', onUp);
    };
  }, [selectedNodeIds.size]);


  // Touch long-press → open the same menu the desktop right-click
  // gesture opens. iOS/Android don't emit `contextmenu` on touch
  // long-press (or they emit it AFTER showing the native selection
  // menu, which is worse), so we instrument the wrapper with our own
  // 500ms timer. Routing mirrors the wrapper-level `onContextMenu`
  // fallback: if the touch landed on a node or edge we open that
  // entity's menu; otherwise we open the pane menu.
  //
  // Cancels on >16px move (so a pan or pinch never accidentally
  // summons the menu) and on touchend before 500ms.
  //
  // Note: NodeFrame ALSO has its own pointer-based long-press timer
  // that dispatches `workspace:node-context-menu`. Both fire on a
  // long-press over a widget — but they both end up calling
  // `openNodeMenu(itemId, ...)`, and React's `useState` setter is a
  // no-op when the next value is shallow-equal, so the second call
  // doesn't double-open. Annotations don't use NodeFrame, so the
  // wrapper-level routing here is the ONLY way long-press works for
  // sticky / shape / text nodes.
  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      longPressStart.current = { x: t.clientX, y: t.clientY };
      // Resolve the target up to a true Element. Touch targets are
      // occasionally Text nodes (when the user taps a text run inside
      // a sticky / chat / contenteditable) and `.closest()` isn't
      // defined on those, which silently routes everything to the
      // pane menu fallback below — causing the double-menu bug the
      // user reported.
      const raw = e.target as unknown as Node | null;
      const targetEl: Element | null =
        raw instanceof Element
          ? raw
          : raw && 'parentElement' in raw
            ? (raw as { parentElement: Element | null }).parentElement
            : null;
      // NodeFrame widgets install their OWN pointer-based long-press
      // timer (see `nodes/node-frame.tsx`). If we let the wrapper
      // also arm one, two menus race to open: NodeFrame's
      // (correctly anchored on the widget) and ours (sometimes
      // falling through to the pane menu when the closest lookup
      // misses for any reason). Bailing here lets NodeFrame own the
      // gesture cleanly. Annotations + sticky notes don't use
      // NodeFrame, so they still hit the wrapper logic below.
      if (targetEl?.closest('.ws-node-frame')) return;
      longPressTimer.current = setTimeout(() => {
        const nodeEl = targetEl?.closest('.react-flow__node') as HTMLElement | null;
        if (nodeEl) {
          const nodeId = nodeEl.getAttribute('data-id');
          if (nodeId) {
            openNodeMenu(nodeId, t.clientX, t.clientY);
            longPressTimer.current = null;
            return;
          }
        }
        const edgeEl = targetEl?.closest('.react-flow__edge') as HTMLElement | null;
        if (edgeEl) {
          const edgeId =
            edgeEl.getAttribute('data-id') ?? edgeEl.id ?? null;
          if (edgeId) {
            openNodeMenu(edgeId, t.clientX, t.clientY);
            longPressTimer.current = null;
            return;
          }
        }
        openPaneMenu(t);
        longPressTimer.current = null;
      }, 500);
    },
    [openPaneMenu, openNodeMenu],
  );
  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!longPressStart.current || !longPressTimer.current) return;
    const t = e.touches[0];
    const dx = t.clientX - longPressStart.current.x;
    const dy = t.clientY - longPressStart.current.y;
    // 12px tolerance (12² = 144). Generous enough to absorb finger
    // jitter during a held tap but tight enough that intentional pans
    // still cancel the long-press timer immediately.
    if (dx * dx + dy * dy > 144) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);
  const onTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    longPressStart.current = null;
  }, []);

  const selectionCtxValue = selectionSnapshot;

  // ---- Alt-click pick-through ------------------------------------------
  // Lets the user select items hidden under stacked widgets by
  // repeatedly Alt-clicking at the same spot — cycles through the
  // local z-stack from front to back. Conflict-resolved against the
  // existing Alt-drag "pan through widgets" gesture (this hook only
  // fires for short clicks, not drags). See `use-pick-through.ts`.
  usePickThrough({
    items,
    wrapper: wrapperRef,
    setSelectedNodeIds,
    setSelectedEdgeIds,
  });

  return (
    <WorkspaceSelectionProvider value={selectionCtxValue}>
    <div
      ref={wrapperRef}
      // `ws-coarse` switches on the touch-first CSS (touch-action so the
      // canvas owns pinch-zoom; resize handles only on selected widgets).
      // See the `touch-action` + `.ws-resize-edge` rules in globals.css.
      className={cn(
        'workspace-canvas absolute inset-0',
        isCoarse && 'ws-coarse',
        (isCoarse || !isMdViewport) && navigateMode && 'ws-navigate',
        // Overview morph (F6): nodes glide between true and spread
        // positions; kept through the exit grace so cards morph back.
        overviewAnim && 'ws-overview-anim',
        glideAnim && 'ws-node-glide',
      )}
      // Onboarding overlay anchor — `pinch to zoom · drag to pan`
      // annotation pins to the canvas centre. We attach the marker to
      // the React Flow wrapper rather than to a node so the anchor is
      // always present even when the canvas is empty.
      data-onboarding-anchor="canvas"
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragLeave={onDragLeave}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      // Belt-and-braces native contextmenu listener. RF's own
      // `onPaneContextMenu` / `onNodeContextMenu` should fire on
      // right-click, but in real life they're occasionally swallowed
      // by intervening listeners, a stuck `.ws-canvas-pan` class
      // (`pointer-events: none` on nodes), or a half-armed annotation
      // tool. This wrapper-level handler walks up from the actual
      // click target, figures out whether the user landed on a node /
      // edge / pane, and opens the correct menu unconditionally. If
      // RF's handler already fired the menu is already open and we
      // simply replace its position.
      onContextMenu={(e) => {
        e.preventDefault();
        // A right-click should never leave a tool armed — that traps
        // the user in crosshair mode with no obvious way out. Clear
        // proactively so the menu opens cleanly.
        if (pendingAnnotation) setPendingAnnotation(null);
        const target = e.target as Element | null;
        const nodeEl = target?.closest('.react-flow__node') as HTMLElement | null;
        if (nodeEl) {
          const nodeId = nodeEl.getAttribute('data-id');
          if (nodeId) {
            openNodeMenu(nodeId, e.clientX, e.clientY);
            return;
          }
        }
        const edgeEl = target?.closest('.react-flow__edge') as HTMLElement | null;
        if (edgeEl) {
          const edgeId =
            edgeEl.getAttribute('data-id') ?? edgeEl.id ?? null;
          if (edgeId) {
            openNodeMenu(edgeId, e.clientX, e.clientY);
            return;
          }
        }
        openPaneMenu({ clientX: e.clientX, clientY: e.clientY });
      }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onMove={onMove}
        // ---- Gesture lock lifecycle ----
        // See the `setGesturing` block above for the rationale. We
        // engage the lock on every gesture entry point RF exposes
        // (node drag, multi-selection drag, viewport pan/zoom) and
        // release it on the matching `*Stop` / `*End`. Belt-and-braces
        // also clears on pointer leave through the wrapper's
        // `onPointerLeave` (mounted at the wrapper div) so a dropped
        // gesture can't leave the canvas frozen with pointer-events
        // disabled.
        // Drag-start snapshots feed the Esc-cancel path (see the
        // `dragStartPosRef` effect above): `nodes` carries every node
        // participating in the gesture (1 for a plain drag, N for a
        // multi-select drag), positions still at their start coords.
        onNodeDragStart={(_e, _node, nodes) => {
          setGesturing(true);
          // Item drags mark the gesture window (`.ws-item-drag`) and
          // freeze the overview grid via `itemDragActive`.
          wrapperRef.current?.classList.add('ws-item-drag');
          setItemDragActive(true);
          // Snapshot TRUE (screen-space) coords for the Esc-cancel
          // restore — `n.position` is DISPLAY coords in the overview
          // lattice, and restoring those would teleport items to
          // wherever their grid cell happened to sit.
          dragStartPosRef.current = new Map(
            nodes.map((n) => {
              const it = screensItemsRef.current.find((x) => x.id === n.id);
              return [
                n.id,
                it
                  ? { x: it.state.x, y: it.state.y }
                  : { x: n.position.x, y: n.position.y },
              ];
            }),
          );
        }}
        onNodeDrag={(e, node) => {
          if (node.type === 'board') return;
          // Overview drags (Part VI): the map is a fixed picker. The
          // dragged card hunts DISPLAY cells: a blank screen's cell
          // glows to receive; the "+ New screen" tile cell lights up
          // under the POINTER and mints a screen on drop (see
          // onNodeDragStop). No edge zones, no screen chrome noise.
          if (overviewRef.current) {
            clearEdgeDwell();
            publishSnapPreview(null);
            const grid = effectiveGrid;
            const w = node.width ?? 0;
            const h = node.height ?? 0;
            const cx = node.position.x + w / 2;
            const cy = node.position.y + h / 2;
            const under = grid?.cells.find(
              (c) =>
                cx >= c.cell.x &&
                cx <= c.cell.x + c.cell.width &&
                cy >= c.cell.y &&
                cy <= c.cell.y + c.cell.height,
            );
            publishRegionDropTarget(
              under && under.kind === 'blank' ? under.id : null,
            );
            const px2 = (e as { clientX?: number }).clientX;
            const py2 = (e as { clientY?: number }).clientY;
            const pt =
              typeof px2 === 'number' && typeof py2 === 'number'
                ? flow.screenToFlowPosition({ x: px2, y: py2 })
                : null;
            const tile = grid?.tile;
            publishTileDrop(
              !!tile &&
                !!pt &&
                pt.x >= tile.x &&
                pt.x <= tile.x + tile.width &&
                pt.y >= tile.y &&
                pt.y <= tile.y + tile.height,
            );
            return;
          }
          // Desktop drags: NO screen chrome — the sheet never reveals
          // and nothing glows (Part VI). Folding onto a blank screen
          // still happens by geometry at drop.
          publishRegionDropTarget(null);
          publishTileDrop(false);
          // Edge gestures (Part VII): sides TILE (preview now, snap on
          // drop — never a screen switch); dragging to the TOP opens
          // the stage view after a short dwell so the drop can land on
          // any screen or the New-screen tile.
          const px = (e as { clientX?: number }).clientX;
          const py = (e as { clientY?: number }).clientY;
          const box = wrapperRef.current?.getBoundingClientRect();
          if (!box || typeof px !== 'number' || typeof py !== 'number') {
            clearEdgeDwell();
            publishSnapPreview(null);
            return;
          }
          const od = overdragFromPointer(py, box.top);
          // Overdrag outranks the side bands: a push through the top-left
          // corner means the stage, not a left tile.
          publishSnapPreview(
            od.kind !== 'idle'
              ? null
              : px >= box.right - EDGE_PUSH_PX
                ? 'right'
                : px <= box.left + EDGE_PUSH_PX
                  ? 'left'
                  : null,
          );
          if (od.kind === 'idle') {
            clearEdgeDwell();
            return;
          }
          if (od.kind === 'commit') {
            clearEdgeDwell();
            enterOverview();
            return;
          }
          publishOverdrag(od.progress);
          if (od.kind === 'holding') {
            // Arm ONCE, on the first frame that pins, and let it run while
            // the pointer stays there; moving off re-enters the distance
            // path above (which clears the timer via the branch below).
            if (edgeDwellRef.current?.zone !== 'top') {
              clearDwellTimer();
              const timer = setTimeout(() => {
                edgeDwellRef.current = null;
                setEdgeDwell(null);
                publishSnapPreview(null);
                publishOverdrag(0);
                enterOverview();
              }, OVERDRAG_CEILING_HOLD_MS);
              edgeDwellRef.current = { zone: 'top', timer };
              setEdgeDwell({ zone: 'top', y: py - box.top });
            }
          } else {
            clearDwellTimer();
            setEdgeDwell(null);
          }
        }}
        onNodeDragStop={(_e, node) => {
          setGesturing(false);
          wrapperRef.current?.classList.remove('ws-item-drag');
          setItemDragActive(false);
          clearEdgeDwell();
          const snapSide = snapPreviewRef.current;
          publishSnapPreview(null);
          const droppedOnTile = tileDropRef.current;
          publishTileDrop(false);
          const grid = frozenGridRef.current ?? effectiveGrid;
          const cancelled = dragCancelRef.current;
          if (cancelled) {
            // Backstop restore — onNodesChange already substituted start
            // coords for the frames RF emitted, but a gesture that ends
            // without a final position frame would otherwise keep the
            // last mid-drag coords. (Start coords are TRUE screen-space,
            // captured at drag start.)
            for (const [id, pos] of cancelled) {
              patchItemState(id, { x: pos.x, y: pos.y }, { debounceMs: 200 });
            }
          } else if (node && node.type !== 'board') {
            if (overviewRef.current && droppedOnTile) {
              // Dropped on the overview's "New screen" tile: mint a
              // blank screen (auto-named Screen N) at the TRUE-space
              // slot after the last screen, file the window onto it,
              // and land there.
              const box = wrapperRef.current?.getBoundingClientRect();
              // Slot from the OTHER screens' TRUE rects — the dragged
              // window's state holds display coords mid-drag. With no
              // other screen (single-window workspace), fall back to
              // one cell right of the window's TRUE start screenful.
              let slot = overviewNewScreenSlot(
                screensItemsRef.current.filter((x) => x.id !== node.id),
                null,
              );
              const start = dragStartPosRef.current?.get(node.id);
              if (!slot && start && box) {
                const scx = start.x + (node.width ?? 0) / 2;
                const scy = start.y + (node.height ?? 0) / 2;
                slot = {
                  x: scx + box.width / 2 + OVERVIEW_GRID_COL_GAP,
                  y: scy - box.height / 2,
                  width: box.width,
                  height: box.height,
                };
              }
              if (box && box.width > 40 && slot) {
                // Cell-sized, not viewport-sized: a screen minted on a phone
                // and one minted on a desktop have to be the same object.
                const rect: ScreenRect = {
                  x: Math.round(slot.x / GRID_MINOR) * GRID_MINOR,
                  y: Math.round(slot.y / GRID_MINOR) * GRID_MINOR,
                  width: slot.width,
                  height: slot.height,
                };
                const minZ = screensItemsRef.current.reduce(
                  (acc, it) => Math.min(acc, it.state.zIndex ?? 0),
                  0,
                );
                // "Screen N" for the number the new screen will have on
                // Stage (fix round 2), counted without the window being
                // filed onto it: it leaves its old place for this one.
                const name = newScreenName(
                  screensItemsRef.current.filter((x) => x.id !== node.id),
                  rect,
                  'new-screen',
                );
                void addItem({
                  type: 'board',
                  refId: null,
                  state: {
                    x: rect.x,
                    y: rect.y,
                    width: rect.width,
                    height: rect.height,
                    zIndex: minZ - 1,
                    cache: { title: name },
                  },
                }).then((b) => {
                  fileWindowIntoRect(node.id, rect);
                  setOverviewMode(false);
                  setCurrentScreen(b.id);
                  animateViewport(
                    frameVp(rect, box.width, box.height, 'blank'),
                    260,
                  );
                });
              }
            } else if (overviewRef.current && grid) {
              // Overview drop on the lattice (Part VI): the store holds
              // DISPLAY coords now (RF dragged what it rendered), so
              // invert the delta of the cell the window landed in —
              // "commit what you see" in TRUE screen space. No cell
              // under the center → nearest cell.
              const w = node.width ?? 0;
              const h = node.height ?? 0;
              const cx = node.position.x + w / 2;
              const cy = node.position.y + h / 2;
              let target = grid.cells.find(
                (c) =>
                  cx >= c.cell.x &&
                  cx <= c.cell.x + c.cell.width &&
                  cy >= c.cell.y &&
                  cy <= c.cell.y + c.cell.height,
              );
              if (!target) {
                let best = Infinity;
                for (const c of grid.cells) {
                  const d =
                    Math.abs(c.cell.x + c.cell.width / 2 - cx) +
                    Math.abs(c.cell.y + c.cell.height / 2 - cy);
                  if (d < best) {
                    best = d;
                    target = c;
                  }
                }
              }
              if (target) {
                // Inverse-map into the target screen's TRUE rect, then
                // clamp fully inside the screenful (a window must never
                // overhang its screen; too-big windows center).
                const trueX = target.cell.x - target.dx;
                const trueY = target.cell.y - target.dy;
                const minX2 = trueX + GRID_MINOR;
                const maxX2 = trueX + target.cell.width - w - GRID_MINOR;
                const minY2 = trueY + GRID_MINOR;
                const maxY2 = trueY + target.cell.height - h - GRID_MINOR;
                let nx = node.position.x - target.dx;
                let ny = node.position.y - target.dy;
                nx =
                  maxX2 >= minX2
                    ? Math.min(Math.max(nx, minX2), maxX2)
                    : trueX + (target.cell.width - w) / 2;
                ny =
                  maxY2 >= minY2
                    ? Math.min(Math.max(ny, minY2), maxY2)
                    : trueY + (target.cell.height - h) / 2;
                patchItemState(node.id, {
                  x: Math.round(nx),
                  y: Math.round(ny),
                });
              }
            } else if (!overviewRef.current && snapSide) {
              // Dropped inside a side zone before the dwell fired:
              // tile the window into the previewed half (same math as
              // the green-dot layout picker).
              window.dispatchEvent(
                new CustomEvent('ws:screen', {
                  detail: { kind: 'layout', itemId: node.id, layout: snapSide },
                }),
              );
            } else if (!overviewRef.current) {
              // P2 ("windows rest on slots"): a drop whose CENTER lands in
              // the void while its body overlaps a neighboring screenful is
              // the half-on-a-screen stray that made Stage unreadable —
              // push it out along the smallest clearance until it stands
              // free, grid-snapped. A clean void drop stays untouched:
              // dragging out IS how a window becomes its own screen.
              const it = screensItemsRef.current.find((x) => x.id === node.id);
              if (it && isOnCanvas(it.state)) {
                const r = {
                  x: it.state.x,
                  y: it.state.y,
                  width: it.state.width,
                  height: it.state.height,
                };
                const cx = r.x + r.width / 2;
                const cy = r.y + r.height / 2;
                const inBoard = screensItemsRef.current.some(
                  (b) =>
                    b.type === 'board' &&
                    isOnCanvas(b.state) &&
                    cx >= b.state.x &&
                    cx <= b.state.x + b.state.width &&
                    cy >= b.state.y &&
                    cy <= b.state.y + b.state.height,
                );
                if (!inBoard) {
                  const entries = orderScreenEntries(
                    screensItemsRef.current.filter((x) => x.id !== node.id),
                  );
                  const centerInEntry = entries.some(
                    (e2) =>
                      cx >= e2.rect.x &&
                      cx <= e2.rect.x + e2.rect.width &&
                      cy >= e2.rect.y &&
                      cy <= e2.rect.y + e2.rect.height,
                  );
                  if (!centerInEntry) {
                    const MARGIN = 24;
                    let px = r.x;
                    let py = r.y;
                    for (let pass = 0; pass < 3; pass++) {
                      const cur = { x: px, y: py, width: r.width, height: r.height };
                      const hit = entries.find((e2) => rectsIntersect(cur, e2.rect));
                      if (!hit) break;
                      const moves = [
                        { dx: hit.rect.x - (cur.x + cur.width) - MARGIN, dy: 0 },
                        { dx: hit.rect.x + hit.rect.width - cur.x + MARGIN, dy: 0 },
                        { dx: 0, dy: hit.rect.y - (cur.y + cur.height) - MARGIN },
                        { dx: 0, dy: hit.rect.y + hit.rect.height - cur.y + MARGIN },
                      ];
                      const best = moves.reduce((a, b) =>
                        Math.abs(a.dx + a.dy) <= Math.abs(b.dx + b.dy) ? a : b,
                      );
                      px += best.dx;
                      py += best.dy;
                    }
                    const snap = (v: number) =>
                      Math.round(v / GRID_MINOR) * GRID_MINOR;
                    if (px !== r.x || py !== r.y) {
                      patchItemState(node.id, { x: snap(px), y: snap(py) });
                    }
                  }
                }
              }
            }
          }
          // …and whatever the branches above decided, never leave the
          // window's header out of reach. Deferred one frame so this reads
          // the position they just committed (tile, push-out, plain drop).
          if (!cancelled && node && node.type !== 'board' && !overviewRef.current) {
            const rescueId = node.id;
            requestAnimationFrame(() => rescueOffscreenWindow(rescueId));
          }
          clearDropTargets();
          dragCancelRef.current = null;
          dragStartPosRef.current = null;
        }}
        onSelectionDragStart={(_e, nodes) => {
          setGesturing(true);
          wrapperRef.current?.classList.add('ws-item-drag');
          setItemDragActive(true);
          // TRUE coords for the cancel restore — see onNodeDragStart.
          dragStartPosRef.current = new Map(
            nodes.map((n) => {
              const it = screensItemsRef.current.find((x) => x.id === n.id);
              return [
                n.id,
                it
                  ? { x: it.state.x, y: it.state.y }
                  : { x: n.position.x, y: n.position.y },
              ];
            }),
          );
        }}
        onSelectionDragStop={() => {
          setGesturing(false);
          wrapperRef.current?.classList.remove('ws-item-drag');
          setItemDragActive(false);
          clearEdgeDwell();
          publishSnapPreview(null);
          publishTileDrop(false);
          clearDropTargets();
          const cancelled = dragCancelRef.current;
          if (cancelled) {
            for (const [id, pos] of cancelled) {
              patchItemState(id, { x: pos.x, y: pos.y }, { debounceMs: 200 });
            }
            dragCancelRef.current = null;
          }
          dragStartPosRef.current = null;
        }}
        onMoveStart={() => setGesturing(true)}
        // Navigation lock (P1): React Flow moves the camera only in the
        // OVERVIEW (freeform map pan) and for programmatic animations.
        // Desktop gestures live in the custom pager (paging + in-board
        // scrolling), so there is no settle-on-release anymore — the
        // camera is only ever AT a destination or on the way to one.
        // (`maybeSettle` survives solely as the mount-restore rescue.)
        // A move that ends outside `animateViewport` (a scroll inside a
        // sheet) still leaves the screen its last camera (fix round 2).
        onMoveEnd={() => {
          setGesturing(false);
          rememberCameraNow();
        }}
        // When the user shift-marquees, the focus stays on whatever
        // editable element they were previously typing in (chat input,
        // sticky contentEditable, code editor, etc.) — and our
        // `onBeforeDelete` veto then silently blocks the subsequent
        // Backspace/Delete because `document.activeElement` is still
        // editable. Blurring at marquee start lets Delete after a
        // multi-select remove the selected nodes instead of acting on
        // the now-irrelevant text field.
        onSelectionStart={() => {
          if (typeof document === 'undefined') return;
          const ae = document.activeElement as HTMLElement | null;
          const tag = ae?.tagName;
          if (
            ae?.isContentEditable ||
            tag === 'INPUT' ||
            tag === 'TEXTAREA' ||
            tag === 'SELECT'
          ) {
            ae?.blur();
          }
        }}
        // When a node is selected RF bumps its z-index (default
        // `elevateNodesOnSelect`) so it draws above its peers. Without
        // the matching `elevateEdgesOnSelect` the connectors and
        // provenance lines stay stuck in the base edge layer — they
        // visibly disappear UNDER the selected widget. Setting both
        // means a selected widget brings its connections up with it.
        elevateEdgesOnSelect
        elevateNodesOnSelect
        // NOTE: annotation drop is handled by the pointerdown/up
        // listeners installed by the click-drag-to-draw effect above,
        // not by `onPaneClick`. That gives us a single code path for
        // both "click → default size" and "drag → bbox-sized" gestures
        // and keeps RF's own click detection (which only fires after
        // pointerup with no drag) out of the equation.
        onPaneContextMenu={(e) => {
          e.preventDefault();
          openPaneMenu(e as unknown as MouseEvent);
        }}
        // Pane click → drop all selection. With the controlled
        // `selectedNodeIds` / `selectedEdgeIds` sets now driving
        // `node.selected` / `edge.selected` in the memos above,
        // clearing the sets is enough — RF's reconciler will mark
        // every previously-selected node/edge as unselected on the
        // next render and the floating toolbars unmount themselves.
        //
        // We don't reach into `nodeLookup` / `edgeLookup` any more
        // because RF v12 in controlled mode rebuilds the lookups
        // from our props on every render anyway.
        onPaneClick={() => {
          // In the overview, empty canvas = "take me back" (Mission
          // Control convention) — return to the current screen.
          if (overviewRef.current) {
            exitOverview();
            return;
          }
          setSelectedNodeIds((prev) => (prev.size === 0 ? prev : new Set()));
          setSelectedEdgeIds((prev) => (prev.size === 0 ? prev : new Set()));
        }}
        onEdgeContextMenu={(e, edge) => {
          // Edges representing connector items get the same per-node
          // menu the canvas already mounts. The menu builder branches
          // on the underlying item type so the connector strip (color,
          // arrow, stroke, label, delete) is wired in Phase 4.
          e.preventDefault();
          e.stopPropagation();
          const itemId =
            (edge.data as { itemId?: string } | undefined)?.itemId ?? edge.id;
          openNodeMenu(itemId, e.clientX, e.clientY);
        }}
        onConnect={(c) => {
          if (!c.source || !c.target) return;
          if (c.source === c.target) return;
          // Drop a `connector` workspace item; the edges memo will
          // pick it up on the next render. `width/height` are
          // schema-required but meaningless for edges, so we pass a
          // sentinel value within the (now-1) min.
          //
          // We also persist `fromHandle` / `toHandle` so the connector
          // remembers which side of each widget it anchored to. Without
          // them React Flow picks the first registered handle on each
          // node and the arrow tends to snap to the top of the source
          // widget regardless of where the user actually drew it.
          void addItem({
            type: 'connector',
            refId: null,
            state: {
              x: 0,
              y: 0,
              width: 1,
              height: 1,
              cache: {
                from: c.source,
                to: c.target,
                fromHandle: c.sourceHandle ?? undefined,
                toHandle: c.targetHandle ?? undefined,
                arrow: 'arrow' as const,
                strokeStyle: 'solid' as const,
              },
            },
          });
        }}
        // Endpoint reconnect: user drags either end of a selected
        // connector and drops it onto a different handle. RF gives
        // us the OLD edge and the NEW connection; we patch the
        // connector item's cache so the change persists exactly
        // like a fresh draw would.
        //
        // We don't add or delete edges here — there's still one
        // connector item before and after, just with different
        // anchors. The edges memo will recompute on the next render
        // and React Flow will rebind without any churn on the
        // visible path.
        onReconnect={(oldEdge, newConnection) => {
          if (!newConnection.source || !newConnection.target) return;
          const itemId =
            (oldEdge.data as { itemId?: string } | undefined)?.itemId ??
            oldEdge.id;
          patchItemState(itemId, {
            cache: {
              from: newConnection.source,
              to: newConnection.target,
              fromHandle: newConnection.sourceHandle ?? undefined,
              toHandle: newConnection.targetHandle ?? undefined,
            },
          });
        }}
        // Reconnect hot-zones default to 10px; bump to 14 so the
        // matching filled endpoint dots in the waypoint overlay
        // (12px visual) sit comfortably inside the grab radius.
        reconnectRadius={14}
        onNodeContextMenu={(e, node) => {
          e.preventDefault();
          e.stopPropagation();
          openNodeMenu(node.id, e.clientX, e.clientY);
        }}
        // Double-click on a widget body would previously zoom-to-fit
        // the whole node, which was surprising inside chat inputs and
        // text editors. The gesture now lives strictly on the widget
        // HEADER (via NodeFrame's `onFocus`), so the canvas no longer
        // installs a node-level handler. Pane double-clicks
        // (zoomOnDoubleClick) still zoom into the cursor.
        // Grid snap is OWNED BY OUR `onNodesChange` HANDLER, not by
        // React Flow's built-in `snapToGrid`. We have to: if RF
        // snaps before invoking the callback, every position lands
        // on the grid lattice — so alignment to other on-grid
        // candidates only ever produces deltas that are multiples
        // of the grid pitch, none of which fall inside our
        // alignment threshold except the literal zero-delta case.
        // That made dynamic alignment guides flicker on and off
        // depending on where the dragged item happened to come to
        // rest. By snapping in userland we can prefer alignment
        // whenever it's close enough, and only fall back to the
        // grid for axes where alignment didn't fire. See the
        // companion code in `onNodesChange`.
        snapToGrid={false}
        // Be more forgiving about what counts as a "pane click" vs
        // "pane drag". RF's default is 1 px — any micro-jitter on
        // mousedown/up (touch, slightly imprecise mice) reads as a
        // drag and RF skips its onPaneClick callback. The user's
        // "click outside doesn't deselect" symptom was usually
        // caused by an imperceptible 2-3 px wobble. 4 px is the
        // smallest threshold that catches realistic jitter without
        // making intentional canvas pans feel laggy.
        paneClickDistance={4}
        minZoom={overviewMode ? 0.02 : zoomFloorRelaxed ? 0.05 : dynMinZoom}
        maxZoom={3}
        proOptions={{ hideAttribution: true }}
        nodesDraggable
        // Connections are ALWAYS enabled — handle visibility is the
        // affordance, not the engine state. Connector dots are hidden
        // by default and surfaced when (a) the node is selected, (b)
        // a node is hovered, or (c) the connector tool is armed. Once
        // the user has a visible handle they can drag from it to draw
        // a connection without first arming any tool, which matches
        // the Figjam / Miro mental model — see the screenshot the
        // user attached: selecting a shape exposes four outer dots
        // and the line draws directly when dragged from one.
        //
        // Gating `nodesConnectable` on the tool meant the line never
        // appeared when the user dragged from a (visible) dot on a
        // selected shape, which read as "dragging a connector doesn't
        // render anything".
        nodesConnectable
        // Loose connection mode lets any handle (we mount source-type
        // only) accept incoming connections. Strict mode would require
        // a literal `target` handle, but we deliberately use one
        // bidirectional source handle per side (see node-frame.tsx) to
        // simplify the user's mental model: there's one dot, drag from
        // it, drop on any other dot.
        connectionMode={ConnectionMode.Loose}
        // Connection-line preview that follows the cursor while the
        // user drags from a handle. Default RF preview is a thin
        // black line which looks alien in the glass aesthetic.
        // CRITICAL: use a literal color (not `color-mix(... var(...))`)
        // because RF injects this style straight onto an SVG <path>
        // and SVG honours `stroke="currentColor"` but is finicky about
        // unresolved CSS variables in inline `style.stroke` — the
        // previous `color-mix(... var(--color-primary) ...)` value
        // silently fell back to `stroke="none"` in some Safari /
        // Firefox builds and the user saw "no line during drag".
        connectionLineStyle={{
          stroke: 'var(--color-foreground)',
          strokeWidth: 2,
          strokeDasharray: '6 4',
          opacity: 0.85,
        }}
        elementsSelectable
        // Delete / Backspace remove every selected node AND edge.
        // RF itself skips the key event when focus is on an editable
        // surface (input / textarea / contenteditable), so typing
        // inside a chat composer or sticky note never triggers a
        // delete. `onBeforeDelete` below adds a belt-and-braces
        // input-focus check that also covers programmatic deletes
        // (right-click "Remove from canvas", `SelectionToolbar`
        // delete button).
        deleteKeyCode={['Delete', 'Backspace']}
        onEdgesDelete={(deleted) => {
          for (const edge of deleted) {
            const itemId =
              (edge.data as { itemId?: string } | undefined)?.itemId ??
              edge.id;
            void removeItem(itemId);
          }
        }}
        onNodesDelete={(deleted) => {
          // Multi-select Delete: remove every selected widget from the
          // workspace. `onBeforeDelete` below already vetoes deletes
          // initiated while the user is typing in an input — so reaching
          // this callback means the gesture was an intentional Delete on
          // the canvas itself.
          for (const node of deleted) {
            void removeItem(node.id);
          }
        }}
        onBeforeDelete={async ({ edges, nodes }) => {
          // Pre-existing protection: typing Backspace inside an input /
          // textarea / contenteditable (the same predicates RF uses)
          // should never delete a workspace item. RF skips its own
          // key-event path for editable surfaces, but `onBeforeDelete`
          // also fires for programmatic deletes (e.g. our context-menu
          // "Remove" action) where the focus check isn't relevant.
          // The check below covers both — we drop ALL nodes when focus
          // is in an editable element, and let everything through
          // otherwise.
          if (typeof document !== 'undefined') {
            const ae = document.activeElement as HTMLElement | null;
            const tag = ae?.tagName;
            if (
              ae?.isContentEditable ||
              tag === 'INPUT' ||
              tag === 'TEXTAREA' ||
              tag === 'SELECT'
            ) {
              return { edges, nodes: nodes.filter(() => false) };
            }
          }
          return { edges, nodes };
        }}
        // IMPORTANT: do NOT enable `onlyRenderVisibleElements`.
        // React Flow's viewport-culling unmounts off-screen nodes, which
        // means scrolling past an agent / content / terminal widget tears
        // down its component state — chat history scroll position lost,
        // CodeMirror editor reset, HTML iframe reloaded — and revealing
        // it again pays a fresh mount cost. Memoization + the per-item
        // selector can't help once the component is unmounted. Keeping
        // every node mounted is the explicit performance trade-off for
        // workspace state preservation (Phase 8/9 design note).
        // Gesture model — RF's d3-zoom owns single-finger pan and
        // trackpad two-finger scroll pan. ALL zoom is ours: the custom
        // capture-phase `wheel` handler (Cmd/Ctrl + trackpad pinch) and
        // the custom capture-phase 2-finger `TouchEvent` handler (touch
        // pinch+pan, intercepted over the pane AND over widgets). Hence
        // `zoomOnScroll`/`zoomOnPinch` stay false — see the notes below.
        // While a widget is in fullscreen takeover we suppress every
        // camera gesture so an accidental wheel / trackpad scroll
        // doesn't drift the (now hidden) viewport behind the fullscreen
        // node and surprise the user on exit.
        // Pan vs marquee gesture model (RF v12):
        //   - Plain left-button drag on the pane = pan.
        //   - Shift + left-button drag = rectangle marquee selection
        //     (via `selectionKeyCode="Shift"`).
        //   - Middle/right-button drag = pan as well, for trackpad /
        //     Wacom users who can't easily hold Shift.
        //   - Shift-click / Cmd-click adds a single item to the
        //     selection.
        //
        // `selectionOnDrag` MUST stay false: when true, React Flow
        // Navigation lock (Part V), with two DELIBERATE mode exceptions:
        // React Flow's freeform pan stays OFF for normal desktop work (the
        // fixed screenful; the field review read free pan as "the whole
        // canvas is moving"), but (1) the NARROW overview pans — phones
        // width-fit the lattice and scroll it vertically instead of
        // shrinking nine screens into 375px — and (2) NAVIGATE MODE (the
        // Hand FAB, coarse-only) is an explicit "look around" state where
        // pan/pinch is the entire point. Marquee selection stays reachable
        // via Shift+drag because of `selectionKeyCode`.
        panOnDrag={(overviewMode && !isMdViewport) || navigateMode}
        selectionOnDrag={false}
        selectionKeyCode="Shift"
        selectionMode={SelectionMode.Partial}
        multiSelectionKeyCode={['Shift', 'Meta']}
        // Part IV: dragging a window against the canvas edge must NOT
        // creep the camera (RF's default auto-pan) — the desktop is a
        // fixed screenful. Cross-screen moves go through the edge-push
        // dwell (see onNodeDrag) or the overview.
        autoPanOnNodeDrag={false}
        // Overview: plain wheel / two-finger trackpad scroll pans the map —
        // the narrow overview is a vertical scroller, and without this the
        // "+ New screen" tile was unreachable by trackpad (drag-only).
        panOnScroll={overviewMode}
        // `zoomOnScroll` stays false on every device: the capture-phase
        // `wheel` listener above is the SOLE Cmd/Ctrl/trackpad-pinch zoom
        // path on desktop. Without it RF's own ctrl-wheel zoom (curve A)
        // fights ours (curve B) over empty canvas, and the user sees
        // different zoom speeds over a `.nowheel` widget (only ours) vs
        // empty space (both, doubling the factor).
        //
        // Both wheel-zoom paths stay false on EVERY device. Zoom is owned
        // by our two capture-phase handlers above: the `wheel` handler
        // (Cmd/Ctrl + trackpad pinch) and the 2-finger `TouchEvent`
        // handler. `zoomOnPinch` must stay false even on touch — RF's
        // native pinch lives on the pane and loses the gesture to any
        // widget whose content consumes the touch, whereas our wrapper
        // capture handler intercepts it over the pane AND over widgets.
        // See the long note on the touch handler effect above.
        zoomOnScroll={false}
        // Native pinch ONLY in navigate mode: there the nodes carry
        // pointer-events:none + touch-action:none (`.ws-navigate`), so no
        // widget can consume the gesture out from under RF — which is the
        // exact hazard that keeps this false everywhere else. Exiting the
        // mode re-frames the current screen at 100%, so the zoom never
        // leaks into normal work.
        zoomOnPinch={navigateMode}
        zoomOnDoubleClick={WORKSPACE_ZOOM_ENABLED && !fullscreenItemId}
        // RF's default zoom-activation key is Meta: with `panOnScroll`
        // enabled, holding Cmd converts the scroll into a d3 zoom even
        // when both `zoomOnScroll`/`zoomOnPinch` are false. Null it so
        // the wheel capture handler above stays the ONLY zoom authority
        // (which, with WORKSPACE_ZOOM_ENABLED=false, means no gesture
        // zoom at all).
        zoomActivationKeyCode={null}
      >
        {/* Dual-layer grid: minor dots + major crosshair, both
            anchored to the same world-coordinate lattice that passes
            through (0, 0). React Flow's default offset places dots
            and crosses on incompatible sublattices — the
            `offsetXY * z || 1 + size/2` quirk (see Background.mjs)
            means each variant gets its own center bias. We pass an
            explicit `offset` equal to `radius` for dots (0.5) and
            `size/2` for the cross (6) so the JS truthy-check bypasses
            the default, and both grids end up tiled from world (0, 0)
            outward. Result: every 5th dot is replaced by a "+" — they
            visually coincide. */}
        {/* Live multiplayer presence — remote cursors + viewports + a
            "who's in which app" focus ring. Renders + broadcasts nothing
            until a second peer joins the room. Focus = the maximized app,
            else the single selected node. */}
        <WorkspacePresence
          canvasRef={wrapperRef}
          focusedItemId={
            fullscreenItemId ??
            (selectedNodeIds.size === 1
              ? (selectedNodeIds.values().next().value ?? null)
              : null)
          }
          screenItemId={fullscreenItemId ?? currentScreenId}
        />
        {/* The dot/cross grid is DESKTOP chrome — the stage view is a
            clean picker with no guides (Part IX). */}
        {!overviewMode && (
          <Background
            id="grid-minor"
            variant={BackgroundVariant.Dots}
            gap={GRID_MINOR}
            size={1}
            offset={0.5}
            color="currentColor"
            className="text-foreground/12 dark:text-foreground/18"
          />
        )}
        {!overviewMode && (
          <Background
            id="grid-major"
            variant={BackgroundVariant.Cross}
            gap={GRID_MAJOR}
            size={8}
            // offset=size/2 makes the cross center sit at world (0, 0)
            // outward — the same lattice the dots above use — instead of
            // the off-by-half-gap default. Keep this in lockstep with
            // `size`; if size changes, change this too.
            offset={4}
            color="currentColor"
            className="text-foreground/20 dark:text-foreground/28"
            lineWidth={1}
          />
        )}
        {/* Canvas comments — threads + replies + reactions in the Yjs doc.
            Mounted LAST so the screen-anchored panel/composer paint above the
            grid Background (otherwise the dots bleed through → looks see-through).
            Pins still portal into the transformed viewport regardless of order. */}
        <CanvasComments />
        {/* Overview lattice chrome (Part VI): cell boundaries + the
            label row under each screen, then the "+ New screen" tile
            cell — click OR drop a window on it to mint a screen. */}
        {overviewMode && effectiveGrid && (
          <OverviewScreenOutlines
            grid={effectiveGrid}
            items={items}
            screenEntries={displayScreenEntries(items, desktopFullscreenIds)}
            onRenameScreen={(id, title, kind) => {
              if (kind === 'blank') {
                patchItemState(id, { cache: { title } });
                return;
              }
              // No sheet yet (an app screen, or one from before screens had
              // sheets). Create it at EXACTLY the outline the Stage is
              // drawing, so the windows already on it become its members and
              // nothing moves. The card is from this device's list; the
              // outline is the STORED screen's, so a window this device shows
              // full screen (still stored there) stays on it.
              const shown = displayScreenEntries(items, desktopFullscreenIds).find((e) => e.id === id);
              const entry = shown
                ? (entryOfItem(orderScreenEntries(items), shown.memberIds[0]) ?? shown)
                : undefined;
              const rect = entry
                ? overviewEntryOutlineRect(
                    entry,
                    items,
                    null,
                    SCREEN_CELL.width,
                    SCREEN_CELL.height,
                  )
                : null;
              if (!rect) return;
              const minZ = items.reduce(
                (acc, it) => Math.min(acc, it.state.zIndex ?? 0),
                0,
              );
              void addItem({
                type: 'board',
                refId: null,
                focus: false,
                state: {
                  x: Math.round(rect.x),
                  y: Math.round(rect.y),
                  width: rect.width,
                  height: rect.height,
                  zIndex: minZ - 1,
                  cache: { title },
                },
              });
            }}
            onDeleteScreen={(id, kind) => {
              // This device's list, without the temporary full-screen screens
              // (no place to move apps to, and never removed by this X).
              const entries = displayScreenEntries(items, desktopFullscreenIds).filter(
                (e) => !e.fullscreen,
              );
              // Never the last one — nowhere to move its apps to.
              if (entries.length <= 1) return;
              const idx = entries.findIndex((e) => e.id === id);
              const entry = entries[idx];
              if (!entry) return;
              // Apps are not deleted with their screen: they move to the
              // neighbour on the left, or to the right when this IS the
              // leftmost — the same adjacency the digits and arrows use.
              const target = entries[idx - 1] ?? entries[idx + 1];
              if (!target) return;
              const landed: ScreenRect[] = [];
              for (const memberId of entry.memberIds) {
                const m = items.find((x) => x.id === memberId);
                if (!m || m.type === 'board') continue;
                const at = fileWindowIntoRect(memberId, target.rect, landed);
                if (at) landed.push(at);
              }
              // Then the place itself, if it had a sheet.
              if (kind === 'blank') void removeItem(id);
            }}
          />
        )}
        {overviewMode && effectiveGrid && (
          <OverviewNewScreenTile
            slot={effectiveGrid.tile}
            dropActive={tileDropActive}
            onNewScreen={() => {
              const s = overviewNewScreenSlot(items, null);
              createBoardAt(s ? { x: s.x, y: s.y } : undefined);
            }}
          />
        )}
      </ReactFlow>

      {/* ⌘⇧←/→ app switcher — keyboard cycling between canvas apps
          (and between fullscreen apps in place), with a transient HUD. */}
      <AppSwitcher />

      {/* Folds a rename made in this tab (tile settings, the Content panel,
          an app's Manage drawer) into this store's live content cache, so
          every label that resolves through item-label.ts follows it without
          a reload. One per store; it never writes the shared document. */}
      <LiveContentRenameSync />

      {/* OS file-drag affordance — a full-canvas hint so drag-to-upload is
          discoverable. `pointer-events-none` so it never eats the drop. */}
      {fileDragOver && (
        <div className="pointer-events-none absolute inset-3 z-50 flex items-center justify-center rounded-2xl border-2 border-dashed border-primary/60 bg-primary/5 backdrop-blur-[1px]">
          <div className="flex items-center gap-2 rounded-full bg-background/90 px-4 py-2 text-sm font-medium shadow-lg ring-1 ring-border">
            <Upload className="h-4 w-4 text-primary" />
            {t('stage.dropFilesToUpload')}
          </div>
        </div>
      )}

      {/* Edge hot-strips for the peek-swipe: host-DOM slivers over both
          canvas edges so the gesture arms even where an iframe owns the
          pixels (touches on iframe content never reach this document).
          They consume ~20px of edge taps on touch devices - the standard
          price of edge gestures. */}
      {/* Off while a window is presented on a handset (decision 4 of the
          handoff): the strips stole taps from an app's own edge controls
          and competed with Android's Back gesture; the app bar's sideways
          swipe pages instead. Off over a desktop full-screen app too (lane
          D): nothing of the platform lies over it but its app bar. */}
      {isCoarse && railV2 && !desktopFullscreenShown && !overviewMode && !fullscreenItemId && !navigateMode && !presentedId && (
        <>
          <div
            aria-hidden
            data-ws-edge
            className="absolute bottom-0 left-0 top-0 z-20 w-7"
            style={{ touchAction: 'none' }}
          />
          <div
            aria-hidden
            data-ws-edge
            className="absolute bottom-0 right-0 top-0 z-20 w-7"
            style={{ touchAction: 'none' }}
          />
        </>
      )}

      {/* Edge-peek hint: the neighbor's name at the pulled edge while the
          gesture is live. */}
      {peekHint && (
        <div
          className={cn(
            'pointer-events-none absolute top-1/2 z-30 -translate-y-1/2 rounded-full border border-border/60 bg-background/95 px-3 py-1.5 text-xs font-medium text-foreground shadow-lg',
            peekHint.dir === 1 ? 'left-3' : 'right-3',
          )}
        >
          {peekHint.dir === 1 ? `‹ ${peekHint.label}` : `${peekHint.label} ›`}
        </div>
      )}

      {/* Touch zoom-lock FAB — on coarse pointers only. Tap to flip into
          "navigate" mode where pinch/pan drives the canvas over widgets
          too (widgets stop capturing touch). Bottom-right, clear of the
          bottom-centre zoom pill. */}
      {!overviewMode && navigateMode && (
        // Hand mode's second zoom control (user call): straight back to
        // 100% — the complement of Fit when a desktop-sized window needs
        // reading rather than surveying.
        <button
          type="button"
          onClick={zoomTo100}
          aria-label={t('stage.zoomTo100')}
          title="100%"
          style={{
            bottom:
              'calc(7.5rem + env(safe-area-inset-bottom) + var(--ws-bar-b, 0px))',
          }}
          className={cn(
            'liquid-glass pointer-events-auto absolute right-4 z-30 inline-flex h-11 w-11 items-center justify-center rounded-full text-[13px] font-semibold text-foreground/80 shadow-lg transition-colors',
            !isCoarse && 'hidden max-md:inline-flex',
          )}
        >
          1×
        </button>
      )}
      {!overviewMode && navigateMode && (
        // Hand mode's one zoom control: pinch/drag is the primary gesture;
        // this taps back to a readable screenful when the pinch overshot.
        // Stacked above the Hand FAB so thumb travel is minimal.
        <button
          type="button"
          onClick={fitNearestScreen}
          aria-label={t('stage.zoomFitScreen')}
          title={t('stage.fitScreen')}
          style={{
            bottom:
              'calc(4.25rem + env(safe-area-inset-bottom) + var(--ws-bar-b, 0px))',
          }}
          className={cn(
            'liquid-glass pointer-events-auto absolute right-4 z-30 inline-flex h-11 w-11 items-center justify-center rounded-full text-foreground/80 shadow-lg transition-colors',
            // Width fallback: withOUT a coarse pointer the FAB exists only
            // below md — the same width convention as the bottom bar.
            !isCoarse && 'hidden max-md:inline-flex',
          )}
        >
          <Maximize className="h-5 w-5" />
        </button>
      )}
      {/* The Hand FAB is retired (user call): touch is MODELESS now --
          two-finger gestures navigate by default, taps go straight into
          apps, headers drag, the corner grip resizes, and double-tap on
          a header fills the screen. `navigateMode` survives as dormant
          machinery behind the dev hook (__wsSetNavigateMode) for e2e --
          the 1x / Fit cluster above only renders while it is on. */}

      {/* Alignment guides overlay — paints the active snap lines while
       * dragging. Mounted INSIDE the canvas wrapper so it can pull the
       * live React Flow transform from the RF store. Sits below chrome
       * (`z-[5]`) so the dock/FABs/panels remain interactable. */}
      <AlignmentGuides />

      {/*
        Draft rectangle preview rendered while the user is click-dragging
        with an annotation tool armed. We paint in screen space directly
        (using the captured pointer coordinates) so we don't need to
        round-trip through the RF transform on every move — pure DOM
        translation stays smooth at 120Hz on a trackpad. The overlay is
        positioned absolutely against the wrapper rect so we subtract the
        wrapper's offset.
      */}
      {draftRect && wrapperRef.current
        ? (() => {
            const rect = wrapperRef.current.getBoundingClientRect();
            const left = Math.min(draftRect.startX, draftRect.endX) - rect.left;
            const top = Math.min(draftRect.startY, draftRect.endY) - rect.top;
            const width = Math.abs(draftRect.endX - draftRect.startX);
            const height = Math.abs(draftRect.endY - draftRect.startY);
            // Match the live annotation node radii:
            //   - sticky → square paper rectangle (rounded-none)
            //   - shape  → rectangle primitive with 6px radius
            //   - text   → small 4px to soften the dashed bbox
            const previewRadius =
              pendingAnnotation?.kind === 'sticky'
                ? 0
                : pendingAnnotation?.kind === 'shape'
                  ? 6
                  : 4;
            return (
              <div
                aria-hidden
                className="pointer-events-none absolute z-[6]"
                style={{
                  left,
                  top,
                  width,
                  height,
                  borderRadius: previewRadius,
                  border:
                    '1.5px dashed color-mix(in srgb, var(--color-primary) 70%, transparent)',
                  background:
                    'color-mix(in srgb, var(--color-primary) 8%, transparent)',
                  boxShadow:
                    '0 0 0 1px color-mix(in srgb, var(--color-primary) 15%, transparent)',
                }}
              />
            );
          })()
        : null}

      {/*
        Vertical zoom stack centred above the bottom-left + FAB. The
        column matches the FAB's `w-14`, so `left-3` on both auto-aligns
        their vertical axes. We park it at `bottom-24` so there's a
        clear visual gap between the secondary toolbar and the primary
        FAB sitting at `bottom-3` (h-14 = 56px + 12px gap ≈ 68px).
      */}
      {!railV2 && (
        <div
          className="ws-zoom-wrap absolute bottom-[calc(6rem_+_env(safe-area-inset-bottom))] left-3 z-30"
          onContextMenu={(e) => e.preventDefault()}
        >
          <ZoomControls snap={snap} onToggleSnap={() => setSnap((v) => !v)} />
        </div>
      )}
      {/* P6: "Back to content", shown only when no content is in view. Pans
          (a window lands at 100%); it never zooms. */}
      {railV2 && <BackToContentPill />}

      {/* Stage corners — screen-space chrome for the meta layer only:
       * workspace switcher (top left), share + presence (top right),
       * My files with the storage quota (bottom left), report a problem
       * (bottom right). */}
      {/* Desktop only: below md the Stage SHEET (S1) mounts the corners
          itself — the spatial lattice is unreadable at phone widths, so the
          sheet replaces it entirely there. */}
      {railV2 && overviewMode && isMdViewport && <StageCorners />}

      {/* The one-time "Remove N empty screens" offer (the Stage algorithm,
          rule 21), in Stage only, never acted on without a press, with Undo.
          Below md the Stage sheet mounts its own copy. */}
      {railV2 && overviewMode && isMdViewport && <StageTidyOffer />}

      {/* Screen position dots (touch): tap to jump, flick to page. Never
          drawn over a presented app (handoff section 4.6): nothing of the
          platform floats over the app at rest. Nor over a desktop
          full-screen app (lane D). */}
      {isCoarse && railV2 && !desktopFullscreenShown && !fullscreenItemId && !overviewMode && !presentedId && (
        <ScreenPageDots
          items={items}
          currentScreenId={currentScreenId}
          fullscreenIds={desktopFullscreenIds}
        />
      )}

      {/* Edge tiling preview (Part V): the half-screen the dragged
       * window would snap into if dropped now — appears the moment the
       * pointer enters a side zone, macOS-tile style. */}
      {snapPreview && !overviewMode && (
        <div aria-hidden className="pointer-events-none absolute inset-0 z-30">
          <div
            className={cn(
              'ws-snap-preview absolute inset-y-2 rounded-2xl border-2 border-primary/40 bg-primary/[0.07]',
              snapPreview === 'left'
                ? 'left-2 right-[calc(50%_+_4px)]'
                : 'left-[calc(50%_+_4px)] right-2',
            )}
          />
        </div>
      )}

      {/* Overdrag hint: one hairline at the top edge that grows from a
       * short nub to the full width as the drag pushes past the canvas —
       * the whole message being "keep going and something happens". It
       * exists ONLY while overdragging, so a window parked at the top
       * shows nothing at all. `ws-overdrag-charging` takes over the fill
       * for the pinned-ceiling fallback, where distance can't grow. */}
      {(overdrag > 0 || edgeDwell) && !overviewMode && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 z-40 flex justify-center"
        >
          <div
            className={cn('ws-overdrag-hint', edgeDwell && 'ws-overdrag-charging')}
            data-armed={overdrag >= 1 ? 'true' : undefined}
            style={{
              transform: `scaleX(${(0.22 + overdrag * 0.78).toFixed(3)})`,
              opacity: 0.35 + overdrag * 0.65,
            }}
          />
        </div>
      )}

      {/* Empty state: railV2 opens the Discover OVERLAY instead (the host
       * auto-opens it once for an empty workspace - the inline card is
       * retired); this legacy mount only serves the ?rail=0 escape hatch. */}
      {!railV2 &&
        workspaceIsEmpty(items) &&
        !overviewMode &&
        !fullscreenItemId && <CanvasEmptyState />}
      {/* railV2: once the one-shot Discover greeting is closed (or never
       * opened: a deep link, a workspace emptied later) the canvas was a
       * blank with no way in. A centred greeting with "Find an app" and
       * "Find an agent", both reopening Discover; it hides itself while
       * Discover is open. */}
      {railV2 &&
        workspaceIsEmpty(items) &&
        !overviewMode &&
        !fullscreenItemId && <EmptyWorkspaceHint />}
      {/* "Closed <name>" with Undo after a window's Close dot: the one
       * listener for `ws:window-closed`, mounted once, whatever the view. */}
      <ClosedWindowUndo />

      {/* Canvas right-click / long-press menu. Portaled to body so it
       * always sits above panels, FABs, and node widgets. */}
      <CanvasContextMenu
        anchor={paneMenu ? { x: paneMenu.x, y: paneMenu.y } : null}
        items={paneMenuItems}
        onClose={() => setPaneMenu(null)}
        ariaLabel="Workspace context menu"
      />

      {/* Per-widget right-click / long-press menu. Same portal style as
       * the pane menu; opened by ReactFlow's `onNodeContextMenu` for
       * mouse and a custom `workspace:node-context-menu` event for
       * touch (dispatched from NodeFrame). */}
      <CanvasContextMenu
        anchor={nodeMenu ? { x: nodeMenu.x, y: nodeMenu.y } : null}
        items={nodeMenuItems}
        onClose={() => setNodeMenu(null)}
        ariaLabel="Widget context menu"
      />

      {/* "More" menu opened by the floating selection toolbar's
       * overflow button. Mounted as a third menu instance because
       * its items + anchor are computed at the call-site (the
       * toolbar) rather than via right-click. */}
      <CanvasContextMenu
        anchor={moreMenu ? { x: moreMenu.x, y: moreMenu.y } : null}
        items={moreMenu?.items ?? []}
        onClose={() => setMoreMenu(null)}
        ariaLabel="Selection actions"
      />

      {/* Selection-bbox-anchored unified floating toolbar. Mounted
       * for any selection (>= 1 item) — single-item annotations /
       * connectors still get their type-specific value editors,
       * which sit on the opposite side of the bbox so the two
       * surfaces complement each other instead of stacking. */}
      {/* Hide the floating action bar while any context menu is open —
       *  the menu already exposes every action the bar would offer
       *  (it's the SAME registry), and showing both at once was
       *  flagged as visually noisy in field testing. The toolbar pops
       *  back in as soon as the menu dismisses. */}
      {!nodeMenu && !paneMenu && !moreMenu && (
        <SelectionToolbar
          clipboard={clipboard}
          onOpenMoreMenu={openMoreMenu}
        />
      )}
    </div>
    </WorkspaceSelectionProvider>
  );
}

// ---------------------------------------------------------------------------
// Annotation + connector right-click menus are now driven by the
// unified command registry — see `buildMenuItemsFromCommands` and
// `lib/workspace/commands.ts`. The legacy `buildAnnotationMenu` /
// `buildConnectorMenu` builders + their palette / picker helpers
// were removed; the type-specific value editors (color / stroke /
// arrowhead / kind conversion / etc.) remain on the floating
// `AnnotationToolbar` and `ConnectorEdgeToolbar` instead of being
// duplicated inside the context menu.
// ---------------------------------------------------------------------------

function CanvasEmptyState() {
  const items = useWorkspaceItems();
  const { t } = useTranslation();
  const tx = (key: string, fallback: string): string => {
    const v = t(key);
    return v && v !== key ? v : fallback;
  };

  const myRobutlerItem = items.find((it) => {
    const username = (it.state.cache as { username?: string } | undefined)
      ?.username;
    if (!username || !username.endsWith('.my-robutler')) return false;
    return it.state.docked === true || it.state.keptInDock === true;
  });
  const flow = useReactFlow();
  const railV2 = useRailV2();

  const openSearchAgents = () =>
    window.dispatchEvent(
      new CustomEvent('workspace:open-search', { detail: { type: 'agents' } }),
    );
  const openSearchWidgets = () =>
    window.dispatchEvent(
      new CustomEvent('workspace:open-search', { detail: { type: 'widgets' } }),
    );
  const askMyRobutler = () => {
    if (!myRobutlerItem) return;
    requestAnimationFrame(() => {
      flow.setCenter(
        myRobutlerItem.state.x + myRobutlerItem.state.width / 2,
        myRobutlerItem.state.y + myRobutlerItem.state.height / 2,
        { zoom: flow.getZoom(), duration: 320 },
      );
    });
  };

  // Rail V2: the empty screen IS Discover, inline. A centered screen-space
  // column (never world-space, never full bleed — you did not ask for it;
  // full width belongs to the opened mount), with its own scroll container
  // so nothing clips on short windows the way the old hero silently did.
  //
  // ANON SAFETY INVARIANT: this surface must never contain an
  // `openWorkspacePanel` call. The canvas mounts for a null user while the
  // panel host does not, so a panel event here is a dead button on every
  // public profile / post / content page. Spawns are safe — they mint a
  // guest through the store fetch wrapper.
  if (railV2) {
    return (
      <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center">
        {/* Phones have no 68px rail column reserving width, so the 96px side
            allowance would squeeze the column to ~280px on an SE — the
            allowance is for the DESKTOP rail + breathing room only. dvh, not
            vh: mobile browser chrome eats the difference and clipped the
            bottom band exactly the way the old hero did. */}
        {/* DEFINITE height, not max-height: the inner band area is a flex-1
            overflow-y-auto, and that only engages against a resolved height —
            with max-height alone the column silently overflowed past the fold
            and everything under the chips was unreachable (found live). The
            card chrome is deliberate too: a scrollable region floating bare
            on the dot grid read as broken, not minimal. */}
        <div
          className="pointer-events-auto flex w-[min(40rem,calc(100vw-16px))] flex-col overflow-hidden rounded-2xl border border-border/60 bg-background p-3 shadow-sm sm:w-[min(40rem,calc(100vw-96px))] sm:p-4 h-[min(46rem,calc(100dvh-152px))] md:h-[min(46rem,calc(100dvh-88px))]"
        >
          <DiscoverView variant="inline" />
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 z-[2] pointer-events-none flex items-center justify-center">
      <div className="flex flex-col items-center gap-6 select-none">
        {/* Single sketched illustration — a notebook-page corner with
            three scattered tile silhouettes. SVG kept tiny (≈90×60)
            so it reads as a margin doodle, not a hero illustration. */}
        <SketchPageDoodle />

        <div className="text-center text-foreground/55 dark:text-foreground/45 font-sketch text-[18px] tracking-wide">
          {tx('workspace.empty.lead', 'a blank page is just possibility')}
        </div>

        <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 max-w-md">
          <SketchStarter
            onClick={openSearchAgents}
            tilt={-3.5}
            label={tx('workspace.empty.pickAgent', 'pick an agent →')}
          />
          <SketchStarter
            onClick={openSearchWidgets}
            tilt={2.2}
            label={tx('workspace.empty.dropWidget', 'drop an app →')}
          />
          {myRobutlerItem ? (
            <SketchStarter
              onClick={askMyRobutler}
              tilt={-1.6}
              label={tx('workspace.empty.askRobutler', 'ask my-robutler →')}
            />
          ) : null}
        </div>

        <div className="text-xs text-muted-foreground/60 font-sketch text-[16px]">
          {tx('workspace.empty.shortcutHint', 'or just hit ⌘K')}
        </div>
      </div>
    </div>
  );
}

/** Hand-drawn notebook-page corner with three scattered puck
 *  silhouettes — agent (circle), widget (rounded square), content
 *  (folded page). Single-stroke SVG so it inherits the empty-state
 *  text color via `currentColor`. */
function SketchPageDoodle() {
  return (
    <svg
      viewBox="0 0 200 120"
      width={180}
      height={108}
      className="text-foreground/40 dark:text-foreground/35"
      aria-hidden
    >
      {/* Notebook page — slightly wavy single stroke so it reads as
          hand-drawn, not a strict rectangle. */}
      <path
        d="M 16 18 Q 60 14 110 17 Q 150 19 184 16 L 182 96 Q 140 102 100 99 Q 60 96 18 100 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinejoin="round"
      />
      {/* Punch holes — three small circles along the left edge. */}
      <circle cx="26" cy="36" r="1.8" fill="currentColor" opacity={0.55} />
      <circle cx="26" cy="58" r="1.8" fill="currentColor" opacity={0.55} />
      <circle cx="26" cy="80" r="1.8" fill="currentColor" opacity={0.55} />
      {/* Agent puck */}
      <circle
        cx="74"
        cy="56"
        r="11"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.4}
      />
      {/* Widget tile */}
      <rect
        x="106"
        y="44"
        width="26"
        height="22"
        rx="4"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.4}
      />
      {/* Content page */}
      <path
        d="M 146 50 L 162 50 L 168 56 L 168 74 L 146 74 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.4}
        strokeLinejoin="round"
      />
      <path d="M 162 50 L 162 56 L 168 56" fill="none" stroke="currentColor" strokeWidth={1.2} />
    </svg>
  );
}

function SketchStarter({
  onClick,
  label,
  tilt,
}: {
  onClick: () => void;
  label: string;
  tilt: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ transform: `rotate(${tilt}deg)` }}
      className={cn(
        'pointer-events-auto font-sketch text-[22px] leading-none',
        'text-foreground/80 hover:text-foreground transition-colors',
        'px-3 py-1.5 rounded-lg',
        'hover:bg-foreground/5 active:scale-[0.97] transition-transform',
        'underline decoration-foreground/30 decoration-1 underline-offset-[6px] hover:decoration-foreground/60',
      )}
    >
      {label}
    </button>
  );
}

// re-export so the shell doesn't need to know about WorkspaceItemDTO here
export type { WorkspaceItemDTO };
